import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';
import { BRAND } from '../../../config/brand';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import CallIcon from '@mui/icons-material/Call';
import SearchIcon from '@mui/icons-material/Search';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import FilterListIcon from '@mui/icons-material/FilterList';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CellTowerIcon from '@mui/icons-material/CellTower';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ComputerIcon from '@mui/icons-material/Computer';
import { useThemeContext } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { APP_VERSION } from '../../../version';
import salesService, { getTodayPackageOrders, getActivePackageOrders, PackageOrderDto, getPackageOrderById, updatePackageOrderStatus, getCustomerDisplaySettings, getPackageOrdersByDateRange, cancelPackageOrder } from '../../../services/salesService';
import { canCancelPhoneOrder, canEditPhoneOrderItems, REASON_I18N_KEY } from './utils/phoneOrderPermissions';
import type { PermissionResult } from './utils/phoneOrderPermissions';
import type { CancelPhoneOrderPayload } from './modals/CancelPhoneOrderModal';
import OrdersTable from './components/OrdersTable';
import LeftPanel from './components/LeftPanel';
import RightPanel from './components/RightPanel';
import { CashierInfo } from '../../salesSystem/quickSale/components/modals/CashierLoginModal';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { usePackageKitchenPrint, PackageKitchenReceiptData, PackageKitchenOrderItem, PackageKitchenOrderSubItem } from './components/PackageKitchenPrintService';
import { PackageReceiptData, printPackageReceipt } from './components/PackageReceiptPrintService';
import NotificationService from '../../../services/NotificationService';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { getPostLogoutRoute } from '../../../services/auth/terminalSession';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import { useLocalServiceUpdateCheck } from '../quickSale/hooks/useLocalServiceUpdateCheck';
import LocalServiceUpdateModal from '../quickSale/components/modals/LocalServiceUpdateModal';
import PavoP2PDeviceSelectDialog from '../quickSale/components/PavoP2PDeviceSelectDialog';

// ============================================================================
// TYPES (from ./types)
// ============================================================================
import {
  OrderSource,
  OrderStatus,
  PaymentType,
  SignalRConnectionStatus,
  PackageOrder,
  CustomerAddress,
  PackageCustomer,
  PhoneCall,
  MasaSiparisi,
  MasaSiparisDetay,
  TrendyolOrder,
  PlatformCategoryMapping,
  StoreToggleInfo,
  PlatformOpenState,
} from './types';

// ============================================================================
// HELPER FUNCTIONS (from ./utils/helpers)
// ============================================================================
import {
  getSourceIcon,
  getSourceColor,
  getStatusInfo,
  getPaymentInfo,
  getWaitingColor,
  formatWaitingTime,
  TODAY,
  getBusinessDayInfo,
  isBusinessDayDate,
  getOrderSourceFromMasaSiparisi,
  convertMasaSiparisiToPackageOrder,
  convertGetirOrderToPackageOrder,
  convertMigrosOrderToPackageOrder,
  convertYemeksepetiOrderToPackageOrder,
  convertTrendyolOrderToPackageOrder,
  ORDER_SOURCE_TO_PLATFORM,
} from './utils/helpers';

// ============================================================================
// STYLED COMPONENTS (from ./styles/StyledComponents)
// ============================================================================
import {
  ScreenContainer,
  HeaderBar,
  MainContent,
  LeftColumn,
  CenterColumn,
  RightColumn,
  FooterBar,
  LeftSection,
  SectionTitle,
  ActionButton,
  CallCard,
  CustomerCard,
  CompactCell,
} from './styles/StyledComponents';

// ============================================================================
// CUSTOM HOOKS
// ============================================================================
import { useFullscreen } from './hooks/useFullscreen';
import { useSignalR, GetirCancelInfo, TrendyolOrderUpdatedInfo, DEFAULT_STORE_KEY } from './hooks/useSignalR';
import type { GetirOrder, MigrosOrder, YemeksepetiOrder } from './hooks/useSignalR';
import type { PlatformIntegration } from '../../../types/platformIntegration';
import { resolveStoreLabel, isFoodPlatform, normalizePlatformKey, PLATFORM_LABELS } from '../../../types/platformIntegration';
import { useOrders, mapOrderStatus, mapPaymentType } from './hooks/useOrders';
import { useLocalSettings } from './hooks/useLocalSettings';
import { useRingSound } from './hooks/useRingSound';
import { useNewOrderSound } from './hooks/useNewOrderSound';
import { useAutoAcceptFailureSound } from './hooks/useAutoAcceptFailureSound';
import AutoAcceptFailureModal, { AutoAcceptFailure } from './modals/AutoAcceptFailureModal';
import { usePhoneCalls, QueuedCall } from './hooks/usePhoneCalls';
import { useNewOrderQueue, QueuedNewOrder } from './hooks/useNewOrderQueue';
import CallDecisionModal from './modals/CallDecisionModal';
import { useTrendyolYemek, isNewOrderNotifiableStatus } from './hooks/useTrendyolYemek';
import { extractAllPackageItemIds, getOrdersFromSqlByDateRange as getTrendyolOrdersByDateRange } from './services/TrendyolYemekService';
import {
  getGetirOrdersByDateRange,
  getMigrosOrdersByDateRange,
  getYemeksepetiOrdersByDateRange,
  getMasaSiparisleriByDateRange,
} from './services/orderHistoryService';
import { usePaymentHandlers } from './hooks/usePaymentHandlers';
import { getSellerByScreen, type SellerSnapshot } from '../../../services/sellerInfoService';
import PavoOrderTrackingModal from '../restaurant/components/PavoOrderTrackingModal';
import { pavoApiService, PavoPaymentRequest } from '../../../services/pavoApiService';
import { pavoSignalRService } from '../../../services/pavoSignalRService';
import { useKitchenPrintHandlers } from './hooks/useKitchenPrintHandlers';
import { useOrderStatusHandlers } from './hooks/useOrderStatusHandlers';
import { useCourierHandlers } from './hooks/useCourierHandlers';
import { usePhoneHandlers } from './hooks/usePhoneHandlers';
import { getStatusButtonInfo as computeStatusButtonInfo } from './utils/orderStatusButtons';
import { normalizeMigrosStatus } from './utils/migrosStatus';
import PackageOrderModals from './components/PackageOrderModals';
import PaymentVerificationModal from './modals/PaymentVerificationModal';
import AddressDetailModal from './modals/AddressDetailModal';
import PlatformCategoryMappingModal from './modals/PlatformCategoryMappingModal';
import CategoryMappingManagementModal from './modals/CategoryMappingManagementModal';
import { platformCategoryMappingService } from './services/platformCategoryMappingService';
import CategoryIcon from '@mui/icons-material/Category';
import { migrosApi, yemeksepetiApi, mainApi } from '../../../config/apiClient';
import CourierPanel, { PanelAssignItem } from '../../couriers/module/CourierPanel';
import { courierModuleService, toApiSource, CourierCollection } from '../../couriers/module/courierModuleService';

// ============================================================================
// COMPONENT
// ============================================================================

const PackageOrderScreen: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const { screenId } = useParams<{ screenId: string }>();
  const navigate = useNavigate();
  const { colors: themeColors } = useThemeContext();
  const { getBranchName, userDetails, logout, currentUser, warehouses } = useAuth();
  const companyId = userDetails?.companyId;
  
  // Admin kontrolü
  const isAdmin = userDetails?.role === 'admin';
  
  // Kullanıcı menüsü state
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  // Ekran bilgileri
  const [screenData, setScreenData] = useState<{
    id: number;
    name: string;
    branchId: string | null;
    packageScreenId: number | null; // ✅ Gerçek PackageScreenId (PhoneCalls için)
    // ✅ YENİ: Yazdırma ayarları
    localServiceUrl: string | null;
    printingSettings: {
      printingMode: 'single' | 'category';
      defaultReceiptPrinter: { id: string; name: string } | null;
      definedPrinters: Array<{
        id: string;
        name: string;
      }>;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
        standardFooterNote?: string;
        thankYouMessage?: string;
      };
      categoryPrinterAssignments?: { [categoryId: string]: string | null };
      adisyonPrinter?: { id: string; name: string } | null;
    } | null;
    // ✅ YENİ: Kasiyer sistemi
    useCashierSystem: boolean;
    // ✅ YENİ: Ödeme ayarları (Nakit/POS/Diğer)
    paymentSettings: {
      cashPaymentCashboxId: string | null;
      cashPaymentCashboxName: string | null;
      posOptions: Array<{
        id: string;
        label: string;
        bankAccountId: string;
        subAccountId: string | null;
        bankAccountName: string | null;
      }>;
      otherOptions: Array<{
        id: string;
        label: string;
        cashboxId: string;
        cashboxName: string | null;
      }>;
    } | null;
    // ✅ YENİ: Varsayılan depo ID (stok düşümü için)
    defaultWarehouseId: string | null;
    integrationSettings: {
      showPhoneOrders: boolean;
      showYemekSepetiOrders: boolean;
      showTrendyolOrders: boolean;
      showGetirOrders: boolean;
      showMigrosOrders: boolean;
      showKioskOrders: boolean;
      showQrMenuOrders: boolean;
      showEcommerceOrders: boolean;
    };
    autoAcceptEnabled: boolean;
    autoAcceptDelaySeconds: number;
    autoPrintKitchen: boolean;
    autoPrintReceiptOnAccept: boolean;
    // ✅ PlatformIntegrations - Yemek entegrasyonları (Getir, YemekSepeti, vs.)
    platformIntegrations: PlatformIntegration[];
    // ─── QuickSale ekranı bazlı yazdırma için ──────────────────────────────
    // `StockVisibilityJson`'dan türetilen 3 alan. Phone-order kategori-bazlı
    // mutfak yazdırması (useKitchenPrintHandlers) ve SettingsModal kategori
    // filtresi bunları kullanır. Detay için yükleme bloğundaki yoruma bakın.
    stockVisibility: any | null;
    screenCategoryIds: string[];
    productScreenCategoryMap: Record<string, string>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // ✅ Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // ✅ YENİ: Ödeme Al modalı state'leri
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // ✅ YENİ: Elle telefon girişi modalı (WhatsApp vs. siparişler için)
  const [isManualPhoneModalOpen, setIsManualPhoneModalOpen] = useState(false);
  const [manualPhoneNumber, setManualPhoneNumber] = useState('');
  const [isSearchingPhone, setIsSearchingPhone] = useState(false);
  
  // ✅ YENİ: Şirket adı cache (Fatura numarası için - QuickSaleScreen gibi)
  const [companyNameCache, setCompanyNameCache] = useState<string | null>(null);

  // ✅ PR2 (Invoice Seller Snapshot Refactor): Backend tarafından çözülen satıcı snapshot
  // Companies vs Branches.Firm* karar mantığı backend'de; UI/print için tutuluyor.
  const [seller, setSeller] = useState<SellerSnapshot | null>(null);

  // ============================================================================
  // DATA STATE - API'den gelecek veriler
  // ============================================================================
  // NOT: `orders` adi alt cumlede tarih filtresine gore live/historic arasinda
  // segecek bir "active selector" olarak yeniden tanimlanir. Burada arka plan
  // state ismi `liveOrders` olarak tutulur; setLiveOrders sadece loadPackageOrders
  // tarafindan canli telefon siparisleri ile doldurulur.
  const [liveOrders, setLiveOrders] = useState<PackageOrder[]>([]);
  const [pastOrders, setPastOrders] = useState<PackageOrder[]>([]);
  const [currentCustomer, setCurrentCustomer] = useState<PackageCustomer | null>(null);
  // ✅ incomingCall ve recentCalls artık usePhoneCalls hook'undan geliyor

  // Sipariş seçimi
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderSource, setSelectedOrderSource] = useState<string | null>(null);

  // Tab seçimi
  const [selectedTab, setSelectedTab] = useState<string>('all');

  // Menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOrderId, setMenuOrderId] = useState<number | null>(null);
  const [menuOrderSource, setMenuOrderSource] = useState<string | null>(null);

  // Detail Modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Kurye seçme modal state
  const [selectCourierModalOpen, setSelectCourierModalOpen] = useState(false);
  // Kurye paneli takip modunda ("Kuryeler" düğmesi / F8); atama modu selectCourierModalOpen ile açılır
  const [courierViewOpen, setCourierViewOpen] = useState(false);

  // Getir ileri tarihli sipariş uyarı modal state
  const [getirScheduledBlockOpen, setGetirScheduledBlockOpen] = useState(false);
  const [getirScheduledBlockDate, setGetirScheduledBlockDate] = useState<string | null>(null);
  const [getirScheduledBlockOrderId, setGetirScheduledBlockOrderId] = useState<number | null>(null);

  // ✅ YENİ: Kasiyer Sistemi State'leri
  const [isCashierLoginModalOpen, setIsCashierLoginModalOpen] = useState(false);
  const [currentCashier, setCurrentCashier] = useState<CashierInfo | null>(null);

  // Yeni müşteri modal state
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [newCustomerPhone, setNewCustomerPhone] = useState('');

  // Durum filtresi
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [statusFilterAnchor, setStatusFilterAnchor] = useState<null | HTMLElement>(null);

  // Mağaza/marka filtresi — yalnızca şubede birden fazla marka varsa görünür.
  const [storeFilter, setStoreFilter] = useState<string>('all');

  // Müşteri filtresi (Son Aramalar'dan seçim)
  const [customerPhoneFilter, setCustomerPhoneFilter] = useState<string | null>(null);
  const [customerNameFilter, setCustomerNameFilter] = useState<string | null>(null);

  // Seçili adres state
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  // Yeni adres ekleme modal state
  const [addAddressModalOpen, setAddAddressModalOpen] = useState(false);
  
  // Müşteri detay modal state
  const [customerDetailModalOpen, setCustomerDetailModalOpen] = useState(false);
  const [selectedCallForDetail, setSelectedCallForDetail] = useState<PhoneCall | null>(null);
  
  // ✅ YENİ: Tüm aramalar modal state
  const [allCallsModalOpen, setAllCallsModalOpen] = useState(false);
  
  // Adres detay modal state
  const [addressDetailModalOpen, setAddressDetailModalOpen] = useState(false);
  const [selectedAddressForDetail, setSelectedAddressForDetail] = useState<CustomerAddress | null>(null);

  // Ödeme Kontrol Modal State
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);

  // Platform Ürün-Kategori Eşleştirme
  const [platformCategoryMappings, setPlatformCategoryMappings] = useState<PlatformCategoryMapping[]>([]);
  const [categoryMappingModalOpen, setCategoryMappingModalOpen] = useState(false);
  const [categoryManagementModalOpen, setCategoryManagementModalOpen] = useState(false);
  const [unmappedProductsForModal, setUnmappedProductsForModal] = useState<Array<{ productName: string; productId?: string | null; platform: string }>>([]);

  // ✅ YENİ: Hızlı Satış Modal State (Paket Siparişi)
  const [quickSaleModalOpen, setQuickSaleModalOpen] = useState(false);
  const [quickSaleOpenSettings, setQuickSaleOpenSettings] = useState<(() => void) | null>(null); // Ayarlar fonksiyonu
  const [quickSaleOpenProductSort, setQuickSaleOpenProductSort] = useState<(() => void) | null>(null); // ✅ YENİ: Ürün Kartı Düzeni fonksiyonu
  const [addOnOrderId, setAddOnOrderId] = useState<number | null>(null); // ✅ YENİ: İlave sipariş modu için sipariş ID'si
  
  // ══════════════════════════════════════════════════════════════════════════
  // YAZDIRMA HATASI TOPLAYICI (SINK)
  // ══════════════════════════════════════════════════════════════════════════
  // Yazdırma katmanı hataları eskiden `alert()` + `return` ile yutuluyordu;
  // `performAcceptAndPrint`in try/catch'i hiç tetiklenmiyordu. Sonuç: oto kabul
  // geri sayımı bitiyor, modal kapanıyor, sipariş onaylanmamış/basılmamış oluyor
  // ve kimse fark etmiyordu.
  //
  // Çözüm: kabul+yazdırma akışı başlarken bir toplayıcı dizi kurulur. Yazdırma
  // katmanı hata bildirince (`capturePrintFailure`) hata bu diziye düşer ve
  // `alert()` GÖSTERİLMEZ — akış sonunda hepsi tek bir hata modalında sunulur.
  // Toplayıcı yoksa (ekrandaki manuel yazdır butonları vb.) eski `alert()`
  // davranışı korunur.
  const printFailureSinkRef = useRef<string[] | null>(null);
  const capturePrintFailure = useCallback((message: string) => {
    const sink = printFailureSinkRef.current;
    if (!sink) return false;
    sink.push(message);
    return true;
  }, []);

  // ✅ YENİ: Mutfak Yazdırma Servisi
  const kitchenPrintScreenSettings = useMemo(() => {
    if (!screenData?.printingSettings) return null;
    return {
      printingMode: screenData.printingSettings.printingMode,
      defaultReceiptPrinter: screenData.printingSettings.defaultReceiptPrinter,
      definedPrinters: screenData.printingSettings.definedPrinters,
      receiptDisplaySettings: screenData.printingSettings.receiptDisplaySettings,
      categoryPrinterAssignments: screenData.printingSettings.categoryPrinterAssignments,
      adisyonPrinter: screenData.printingSettings.adisyonPrinter || null,
    };
  }, [screenData?.printingSettings]);
  
  const { printPackageKitchenReceipt, printPackageKitchenReceiptByCategory } = usePackageKitchenPrint({
    companyId: companyId || null,
    screenId: screenId || null,
    screenSettings: kitchenPrintScreenSettings,
    localServiceUrl: screenData?.localServiceUrl || undefined,
    onPrintError: (error) => {
      console.error('🖨️ [PackageOrder] Mutfak fişi yazdırma hatası:', error);
      // Kabul+yazdırma akışı içindeyse hata modalına yönlendirilir; bu servis
      // hatayı kendi içinde yuttuğu (throw etmediği) için tek yakalama noktası
      // burasıdır.
      if (capturePrintFailure(`Mutfak fişi: ${error}`)) return;
      alert(`Yazdırma Hatası: ${error}`);
    },
    onPrintSuccess: (message) => {
      console.log('🖨️ [PackageOrder] Mutfak fişi yazdırıldı:', message);
    },
  });
  
  // Platform kategori eşleştirme lookup Map'i — O(1) arama
  const mappingLookup = useMemo(() => {
    const map = new Map<string, { categoryId: string; categoryName: string }>();
    platformCategoryMappings.forEach(m => {
      const key = `${m.platform}_${m.productName.toLowerCase().trim()}`;
      map.set(key, { categoryId: m.categoryId, categoryName: m.categoryName });
      if (m.productId) {
        map.set(`${m.platform}_pid_${m.productId}`, { categoryId: m.categoryId, categoryName: m.categoryName });
      }
    });
    return map;
  }, [platformCategoryMappings]);

  // Envanter kategorileri (API'den yüklenir — eşleştirme modal'larında kullanılır)
  const [availableCategories, setAvailableCategories] = useState<Array<{ id: string; name: string }>>([]);

  // availableCategories artık loadScreenData içinde StockVisibilityJson'dan yükleniyor

  // ✅ Tam ekran modu (Custom Hook)
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  
  // ============================================================================
  // SIGNALR - QR MENU / E-TİCARET / KIOSK SİPARİŞLERİ (Custom Hook)
  // ============================================================================
  
  // ============================================================================
  // YENİ SİPARİŞ BİLDİRİM KUYRUĞU (O1-O3) — useNewOrderQueue hook ile yönetilir.
  // Eski "tek slot" mimari (newOrderModalOpen + newOrderData + newTrendyolOrderData)
  // artık tamamen kaldırıldı. Yeni sipariş bildirimleri:
  //   - SignalR ve Trendyol polling handler'ları enqueueNewOrderRef üzerinden
  //     kuyruğa ekler (KATI FIFO, asla overwrite yok).
  //   - Reactive promotion (hook içinde): ekran müsait olduğu anda pending[0]
  //     active'e yükselir, NewOrderModal açılır, geri sayım başlar.
  //   - "Ekran müsait" = !anyOtherModalOpen && !incomingCall (yemek için tanım).
  //   - Hook çağrısı, anyOtherModalOpen useMemo'sundan SONRA yapılır.
  // Handler'lar kuyruğa "ref" üzerinden push eder çünkü useNewOrderQueue çağrısı
  // bu satırdan çok sonra (anyOtherModalOpen useMemo'sundan sonra) yapılır.
  // ============================================================================
  const enqueueNewOrderRef = useRef<((q: QueuedNewOrder) => boolean) | null>(null);
  const completeActiveNewOrderRef = useRef<(() => void) | null>(null);

  // ============================================================================
  // ✅ C1 fix: Pavo / REST-P2P cihaz seçim modal state'leri PackageOrderScreen'de
  // tutuluyor (önceden usePaymentHandlers içindeydi). Sebep: anyOtherModalOpen
  // useMemo'sundan görülebilsin → Pavo cihaz seçim ekranı ortasında yeni yemek
  // siparişi geldiğinde NewOrderModal arkadan açılmasın + ses çalmasın, ayrıca
  // yeni telefon araması gelirse onNewCall müşteri context wipe guard'ı geçerli olsun.
  // Setter'ları usePaymentHandlers'a parametre olarak geçiyoruz.
  // ============================================================================
  const [pavoP2pSelectOpen, setPavoP2pSelectOpen] = useState(false);
  const [restP2pReceiptSelectOpen, setRestP2pReceiptSelectOpen] = useState(false);

  // ✅ QR Menü Sipariş Detay Modalı
  const [qrOrderDetailModalOpen, setQrOrderDetailModalOpen] = useState(false);
  const [selectedQrOrder, setSelectedQrOrder] = useState<MasaSiparisi | null>(null);
  
  // ✅ Getir Sipariş Detay Modalı
  const [getirDetailModalOpen, setGetirDetailModalOpen] = useState(false);
  const [getirDetailOrderId, setGetirDetailOrderId] = useState<number | null>(null);

  // ✅ Getir Sipariş İptal Modalı
  const [getirCancelModalOpen, setGetirCancelModalOpen] = useState(false);
  const [cancelledOrderInfo, setCancelledOrderInfo] = useState<GetirCancelInfo | null>(null);

  // Önceki sipariş durumlarını takip et (1dk yenilemede iptal algılama için)
  const prevGetirStatusRef = useRef<Map<number, number>>(new Map());
  const prevMigrosStatusRef = useRef<Map<number, string>>(new Map());
  const prevYsStatusRef = useRef<Map<number, string>>(new Map());
  const isFirstLoadRef = useRef(true);
  
  // ✅ SignalR Hook - QR Menü, E-Ticaret, Kiosk, Getir, Migros, Yemeksepeti
  // Tüm platformlar `MasaSiparisi` formatında geliyor; siparisKaynagi'ndan
  // platform türünü derive edip kuyruğa ekliyoruz (KATI FIFO, asla overwrite yok).
  const handleSignalRNewOrder = useCallback((order: MasaSiparisi) => {
    const kaynak = (order.siparisKaynagi || '').toLowerCase();
    let platform: QueuedNewOrder['platform'];
    if (kaynak.includes('getir')) platform = 'getir';
    else if (kaynak.includes('migros')) platform = 'migros';
    else if (kaynak.includes('yemeksepeti')) platform = 'yemeksepeti';
    else if (kaynak.includes('ecommerce') || kaynak.includes('e-commerce')) platform = 'ecommerce';
    else if (kaynak.includes('kiosk')) platform = 'kiosk';
    else platform = 'qr';

    const key = order.notificationKey
      || (platform === 'migros' || platform === 'yemeksepeti'
        ? `${platform}|${order.siparisId || order.id}|new-order`
        : `${platform}_${order.siparisId || order.id}`);
    console.log('🔔 [SignalR] YENİ SİPARİŞ BİLDİRİMİ - Kuyruğa ekleniyor:', { key, platform });

    enqueueNewOrderRef.current?.({
      masaSiparisi: order,
      trendyolOrder: null,
      platform,
      enqueuedAt: Date.now(),
      key,
    });
  }, []);

  // ✅ Getir token yenileme fonksiyonu (useCallback)
  // Hem timer'dan hem de 401 token hatası alındığında çağrılır
  const refreshGetirToken = useCallback(async () => {
    return;
  }, []);

  const playCancelSound = useCallback(() => {
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.7;
      audio.play().catch(() => console.log('🔇 Ses çalınamadı (kullanıcı etkileşimi gerekli)'));
    } catch (e) {
      console.log('🔇 Ses çalma hatası:', e);
    }
  }, []);

  // ✅ Getir sipariş iptal edildiğinde çağrılır
  const handleGetirOrderCancelled = useCallback((cancelInfo: GetirCancelInfo) => {
    console.log('🚫 [GetirCancel] Sipariş iptal bildirim modalı açılıyor:', cancelInfo);
    setCancelledOrderInfo(cancelInfo);
    setGetirCancelModalOpen(true);
    playCancelSound();
  }, [playCancelSound]);

  // Server-side polling (local-service v1.4.3+) sırasında SignalR
  // "TrendyolOrderUpdated" event'ini useTrendyolYemek state'ine bridge etmek için
  // ref pattern. useTrendyolYemek hook'u aşağıda çağrılıyor; setTrendyolOrders'a
  // direkt erişemiyoruz. Aşağıdaki useEffect ref'i hook çağrısı sonrasında set eder.
  const trendyolPushBridgeRef = useRef<((info: TrendyolOrderUpdatedInfo) => void) | null>(null);

  const {
    status: signalRStatus,
    qrMenuOrders: liveQrMenuOrders,
    ecommerceOrders: liveEcommerceOrders,
    kioskOrders: liveKioskOrders,
    getirOrders: liveGetirOrders,
    setGetirOrders,
    allowedQrMenuIdsLoaded,
    isQrOrderForThisBranch,
    loadAllowedQrMenuIds,
    fetchMasaSiparisleri,
    fetchGetirOrders,
    updateMasaSiparisiStatus,
    updateGetirOrderStatus,
    syncGetirOrderStatuses,
    getirRestaurantOpen,
    getirRestaurantOpenMap,
    checkGetirRestaurantStatus,
    openGetirRestaurant,
    closeGetirRestaurant,
    migrosOrders: liveMigrosOrders,
    setMigrosOrders,
    fetchMigrosOrders,
    updateMigrosOrderStatus,
    migrosRestaurantOpen,
    migrosRestaurantOpenMap,
    checkMigrosRestaurantStatus,
    openMigrosRestaurant,
    closeMigrosRestaurant,
    yemeksepetiOrders: liveYemeksepetiOrders,
    setYemeksepetiOrders,
    fetchYemeksepetiOrders,
    updateYemeksepetiOrderStatus,
    yemeksepetiVendorOpen,
    yemeksepetiVendorOpenMap,
    checkYemeksepetiVendorStatus,
    openYemeksepetiVendor,
    closeYemeksepetiVendor,
  } = useSignalR({
    companyId: companyId || null,
    branchId: screenData?.branchId || null,
    onNewOrder: handleSignalRNewOrder,
    onGetirOrderCancelled: handleGetirOrderCancelled,
    onGetirTokenError: refreshGetirToken,
    onMigrosOrderCancelled: (cancelInfo) => {
      console.log('[Migros] Siparis iptal bildirimi:', cancelInfo);
      setMigrosCancelModalOpen(true);
      setMigrosCancelledOrderInfo(cancelInfo);
      playCancelSound();
    },
    onMigrosDeliveryStatusChanged: (statusInfo) => {
      console.log('[Migros] Teslimat durumu degisti:', statusInfo);
      fetchMigrosOrders();
    },
    onYemeksepetiOrderCancelled: (cancelInfo) => {
      console.log('[YS] Siparis iptal bildirimi:', cancelInfo);
      setYsCancelModalOpen(true);
      setYsCancelledOrderInfo(cancelInfo);
      playCancelSound();
    },
    onTrendyolOrderUpdated: (info) => {
      // useTrendyolYemek hook'u aşağıda çağrılıyor — gerçek mantık oraya bağlandığı
      // ref üzerinden çalışır. Eski mod'da push hiç gelmez (eski local-service v1.4.2
      // backend'i tetiklemez), yeni mod'da bu callback aktif olur.
      trendyolPushBridgeRef.current?.(info);
    },
  });

  // ✅ Trendyol Yemek Hook - Polling tabanlı sipariş takibi
  // Polling cycle'ında birden fazla yeni sipariş gelebilir (for loop). Her biri
  // ayrı ayrı kuyruğa eklenir, asla overwrite olmaz.
  const handleTrendyolNewOrder = useCallback((order: TrendyolOrder, notificationKey?: string) => {
    const key = notificationKey || `trendyol|${order.id || order.orderCode || order.orderNumber}|new-order`;
    console.log('🔔 [Trendyol] YENİ SİPARİŞ - Kuyruğa ekleniyor:', { key, orderCode: order.orderCode });

    enqueueNewOrderRef.current?.({
      masaSiparisi: null,
      trendyolOrder: order,
      platform: 'trendyol',
      enqueuedAt: Date.now(),
      key,
    });
  }, []);

  const handleTrendyolOrderCancelled = useCallback((order: TrendyolOrder) => {
    console.log('🚫 [Trendyol] Sipariş İPTAL:', order.orderCode, order.orderNumber, order.packageStatus);
    setTrendyolCancelledNotifOrder(order);
    setTrendyolCancelNotifModalOpen(true);
    playCancelSound();
  }, [playCancelSound]);

  const {
    trendyolOrders: liveTrendyolOrders,
    setTrendyolOrders,
    isPolling: trendyolPolling,
    vendorStatus: trendyolVendorStatus,
    vendorStatusMap: trendyolVendorStatusMap,
    error: trendyolError,
    updateOrderStatus: updateTrendyolOrderStatus,
    cancelTrendyolOrder,
    openVendor: openTrendyolVendor,
    closeVendor: closeTrendyolVendor,
    refreshOrders: refreshTrendyolOrders,
    localServiceConnected: trendyolLocalServiceConnected,
    serverPollingStale: trendyolServerPollingStale,
    multiStoreUnsupported: trendyolMultiStoreUnsupported,
    trendyolProductDescriptions,
  } = useTrendyolYemek({
    companyId: companyId || null,
    branchId: screenData?.branchId || null,
    enabled: !!(screenData?.integrationSettings?.showTrendyolOrders) && !!(screenData?.platformIntegrations?.some(p => p.platform === 'trendyol_yemek' && p.isEnabled)),
    platformIntegrations: screenData?.platformIntegrations || [],
    userEmail: currentUser?.email || '',
    localServiceUrl: screenData?.localServiceUrl || null,
    onNewOrder: handleTrendyolNewOrder,
    onOrderCancelled: handleTrendyolOrderCancelled,
  });

  // ============================================================================
  // TARIH FILTRESI (Son 7 Gun) - HISTORIC MODE
  // ============================================================================
  // Mevcut canli akis (SignalR, polling, fetch*Orders, useTrendyolYemek polling
  // vs.) bu blok tarafindan HIC dokunulmaz. Tarih filtresi aktif oldugunda
  // (selectedHistoricDate != null) yalnizca paralel "historic" state grubu
  // doldurulur ve render seviyesinde aktif state olarak segecilir. Carpiya
  // basildiginda selectedHistoricDate null'a doner ve UI ani olarak yine
  // canli state'leri gosterir (yeniden fetch gerekmez; live state arkada
  // SignalR/polling ile guncel kalmaya devam etmistir).
  const [selectedHistoricDate, setSelectedHistoricDate] = useState<Date | null>(null);
  // Tarih filtresi dropdown menu anchor'u (header'daki ikon butonu icin)
  const [dateMenuAnchor, setDateMenuAnchor] = useState<HTMLElement | null>(null);
  // Son 7 gun (bugun haric). Saatler 12:00 ayarli, gun karsilastirmasinda
  // saat dilimi kaymalarinin etkilenmemesi icin.
  // Tarih hesaplari icin timezone-bagimsiz yardimcilar.
  // `setHours` LOCAL timezone'a gore calistigi icin, sistem timezone'u yanlis
  // ayarli olan kasalarda off-by-one bug uretiyor olabilir. Bunun yerine Date
  // constructor'unu (yyyy, m, d, h) formatinda kullanmak gun bazinda kaymaz.
  const toLocalDateString = useCallback((d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // last7Days her render'da yeniden hesaplaniyor (deps `[]` degil), boylece
  // gece geçince stale kalmaz. Sayfa uzun sure acik tutulup ertesi gune
  // gectiginde options otomatik kayar.
  const last7Days = useMemo(() => {
    const days: Date[] = [];
    const now = new Date();
    // Bugunun local gun basi noon'una sabitlenmis Date - timezone-stable
    const todayY = now.getFullYear();
    const todayM = now.getMonth();
    const todayD = now.getDate();
    for (let i = 1; i <= 7; i++) {
      // Date(yyyy, m, d, 12) constructor'u local timezone'da gun ofsetini
      // dogru hesaplar (setDate'ten daha guvenli)
      const d = new Date(todayY, todayM, todayD - i, 12, 0, 0, 0);
      days.push(d);
    }
    return days;
    // Saniye-bazli stamp degil; render-bazli deps yok ki sik sik degismesin.
    // Tarih degisirse zaten dependent useMemo'lar tetiklenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const formatHistoricDateLabel = useCallback((d: Date) => {
    // "10.05.2026 - Pazar (10 Mayis 2026)" formati: dd.mm.yyyy ile haftalik
    // gun adi yan yana, kafa karisikligini engellemek icin numerik tarih ON PLANDA.
    const dateStr = toLocalDateString(d).split('-').reverse().join('.'); // 10.05.2026
    const weekday = d.toLocaleDateString('tr-TR', { weekday: 'long' });
    const weekdayTr = weekday.charAt(0).toLocaleUpperCase('tr-TR') + weekday.slice(1);
    const monthLong = d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' });
    return `${dateStr} - ${weekdayTr} (${monthLong})`;
  }, [toLocalDateString]);
  const [historicOrders, setHistoricOrders] = useState<PackageOrder[]>([]); // Telefon siparisleri (donusmus)
  const [historicTrendyolOrders, setHistoricTrendyolOrders] = useState<TrendyolOrder[]>([]);
  const [historicGetirOrders, setHistoricGetirOrders] = useState<GetirOrder[]>([]);
  const [historicMigrosOrders, setHistoricMigrosOrders] = useState<MigrosOrder[]>([]);
  const [historicYemeksepetiOrders, setHistoricYemeksepetiOrders] = useState<YemeksepetiOrder[]>([]);
  const [historicQrMenuOrders, setHistoricQrMenuOrders] = useState<MasaSiparisi[]>([]);
  const [historicEcommerceOrders, setHistoricEcommerceOrders] = useState<MasaSiparisi[]>([]);
  const [historicKioskOrders, setHistoricKioskOrders] = useState<MasaSiparisi[]>([]);
  const [isLoadingHistoric, setIsLoadingHistoric] = useState(false);

  // "Active" selector'lar: tarih filtresi varsa historic, yoksa canli state.
  // Bu noktadan sonra `trendyolOrders`, `getirOrders` vb. isimleri otomatik
  // olarak dogru veriye baglanir; alttaki useMemo/useEffect/JSX bloklarinin
  // hicbiri degistirilmek zorunda kalmaz.
  const orders = selectedHistoricDate ? historicOrders : liveOrders;
  const trendyolOrders = selectedHistoricDate ? historicTrendyolOrders : liveTrendyolOrders;
  const getirOrders = selectedHistoricDate ? historicGetirOrders : liveGetirOrders;
  const migrosOrders = selectedHistoricDate ? historicMigrosOrders : liveMigrosOrders;
  const yemeksepetiOrders = selectedHistoricDate ? historicYemeksepetiOrders : liveYemeksepetiOrders;
  const qrMenuOrders = selectedHistoricDate ? historicQrMenuOrders : liveQrMenuOrders;
  const ecommerceOrders = selectedHistoricDate ? historicEcommerceOrders : liveEcommerceOrders;
  const kioskOrders = selectedHistoricDate ? historicKioskOrders : liveKioskOrders;

  // Tarih-filtresi-aware "is in active day window" predicate.
  // - Canli mod: isBusinessDayDate(orderDate) (bugun + gece modunda dun)
  // - Historic mod: HER SIPARIS GECERLI (server zaten secilen gunun siparislerini
  //   dondurdu; UI seviyesinde "bugun mu?" filtresi uygulanmamali)
  // orderTabs, shouldShowOrder, footerStats vb. tum useMemo'lar bu predicate'a
  // baglanir; selectedHistoricDate degisince hepsi otomatik yeniden hesaplanir.
  const isInActiveDayWindow = useCallback((orderDate: string): boolean => {
    if (selectedHistoricDate) return true;
    return isBusinessDayDate(orderDate);
  }, [selectedHistoricDate]);

  // Tarih filtresi aktifken 6 kanali paralel olarak server-side cek.
  // Telefon (PackageOrder) icin `historicPackageOrders` doldurulur; bu state
  // asagidaki `convertPackageOrderDtoToOrder` benzeri donusum noktasinda
  // `orders` state'inin yerine kullanilir (asagiya bakin).
  const loadHistoricOrders = useCallback(async (date: Date) => {
    if (!companyId) return;
    const branchId = screenData?.branchId || null;

    // Secili gunun yerel 00:00:00 - 23:59:59.999 araligi.
    // setHours yerine Date constructor (yyyy, m, d, h, m, s, ms) kullaniyoruz:
    // setHours LOCAL timezone'a gore calistigi icin sistem TZ ayari yanlis
    // (ornegin Docker/UTC) makinelerde off-by-one uretebilir; Date constructor
    // gun bazinda ASLA kaymaz.
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    const startDate = new Date(y, m, d, 0, 0, 0, 0);
    const endDate = new Date(y, m, d, 23, 59, 59, 999);

    // Aciklayici log: kullanicinin secimi ile API'ye giden aralik net gozuksun
    const localDateStr = toLocalDateString(date);
    console.log('[HistoricFilter] Yukleme basliyor:', {
      seciliGun: localDateStr,
      startLocal: startDate.toString(),
      endLocal: endDate.toString(),
      startUTC: startDate.toISOString(),
      endUTC: endDate.toISOString(),
      branchId: branchId || 'YOK',
    });
    setIsLoadingHistoric(true);
    try {
      const [
        pkgRes,
        trendyolRes,
        getirRes,
        migrosRes,
        ysRes,
        masaRes,
      ] = await Promise.allSettled([
        getPackageOrdersByDateRange(startDate, endDate, branchId || undefined),
        getTrendyolOrdersByDateRange(startDate, endDate, branchId, 1, 1000, companyId),
        getGetirOrdersByDateRange(startDate, endDate, branchId),
        getMigrosOrdersByDateRange(startDate, endDate, branchId),
        getYemeksepetiOrdersByDateRange(startDate, endDate, branchId),
        // SUBE FILTRESI: canli akistaki (useSignalR.fetchMasaSiparisleri) ile
        // ayni isQrOrderForThisBranch predicate'i frontend savunma katmani
        // olarak geciliyor. Backend branchId yetersiz kalsa bile burada elenir.
        getMasaSiparisleriByDateRange(startDate, endDate, branchId, isQrOrderForThisBranch),
      ]);

      if (pkgRes.status === 'fulfilled' && pkgRes.value?.Data) {
        // DTO -> PackageOrder donusumu (loadPackageOrders icindeki ile aynidir).
        const mapped: PackageOrder[] = pkgRes.value.Data.map((order: PackageOrderDto) => {
          const status = mapOrderStatus(order.OrderStatus || 'PENDING');
          const createdAt = new Date(order.CreatedAt);
          let waitingMinutes = order.WaitingMinutes || 0;
          if (status === 'delivered' || status === 'cancelled' || status === 'undelivered') {
            if (order.CompletedAt) {
              const completedAt = new Date(order.CompletedAt);
              waitingMinutes = Math.floor((completedAt.getTime() - createdAt.getTime()) / 60000);
            }
          }
          return {
            id: order.Id,
            shortOrderNo: order.ShortCode || '',
            externalOrderNo: order.OrderNumber || undefined,
            source: 'phone' as OrderSource,
            customerName: order.AccountName || '',
            customerPhone: order.CustomerPhone || '',
            customerAddress: order.CustomerAddress || '',
            orderDate: createdAt.toISOString().split('T')[0],
            orderTime: createdAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
            totalAmount: order.TotalAmount || 0,
            status: status,
            itemCount: order.ItemCount || order.Items?.length || 0,
            waitingMinutes: waitingMinutes,
            paymentType: mapPaymentType(order.PaymentMethod),
            courierName: order.CourierName || undefined,
            courierStatus: order.CourierStatus || undefined,
            isPaid: order.PaymentStatus === 'PAID',
            totalPaid: order.TotalPaid || 0,
            paymentMethod: order.PaymentMethod || undefined,
            createdAt: order.CreatedAt,
            completedAt: order.CompletedAt || undefined,
            invoiceId: order.InvoiceId || null,
            invoiceNumber: order.InvoiceNumber || null,
            referenceNumber: order.ReferenceNumber || null,
          };
        });
        setHistoricOrders(mapped);
      } else {
        setHistoricOrders([]);
      }
      setHistoricTrendyolOrders(trendyolRes.status === 'fulfilled' ? trendyolRes.value : []);
      setHistoricGetirOrders(getirRes.status === 'fulfilled' ? getirRes.value : []);
      setHistoricMigrosOrders(migrosRes.status === 'fulfilled' ? migrosRes.value : []);
      setHistoricYemeksepetiOrders(ysRes.status === 'fulfilled' ? ysRes.value : []);
      if (masaRes.status === 'fulfilled') {
        setHistoricQrMenuOrders(masaRes.value.qrOrders);
        setHistoricEcommerceOrders(masaRes.value.ecommerceOrders);
        setHistoricKioskOrders(masaRes.value.kioskOrders);
      } else {
        setHistoricQrMenuOrders([]);
        setHistoricEcommerceOrders([]);
        setHistoricKioskOrders([]);
      }

      console.log('[HistoricFilter] Yukleme tamamlandi:', {
        package: pkgRes.status === 'fulfilled' ? pkgRes.value?.Data?.length ?? 0 : 'failed',
        trendyol: trendyolRes.status === 'fulfilled' ? trendyolRes.value.length : 'failed',
        getir: getirRes.status === 'fulfilled' ? getirRes.value.length : 'failed',
        migros: migrosRes.status === 'fulfilled' ? migrosRes.value.length : 'failed',
        ys: ysRes.status === 'fulfilled' ? ysRes.value.length : 'failed',
        masa: masaRes.status === 'fulfilled'
          ? { qr: masaRes.value.qrOrders.length, ecom: masaRes.value.ecommerceOrders.length, kiosk: masaRes.value.kioskOrders.length }
          : 'failed',
      });
    } catch (err) {
      console.error('[HistoricFilter] Yukleme hatasi:', err);
    } finally {
      setIsLoadingHistoric(false);
    }
  }, [companyId, screenData?.branchId, isQrOrderForThisBranch, toLocalDateString]);

  // Tarih veya sube degistiginde historic'i yeniden cek. Filtre null'a donerse
  // historic state'leri temizler. Filtre KAPATILDIGINDA canli state'leri
  // yeniden fetch edip diger sube sizmasi (SignalR push'lari arka planda
  // gelmis olabilir) varsa temizleriz — bu logic asagidaki ikinci useEffect'te
  // calisir (loadPackageOrders henuz tanimlanmadigi icin burada cagirilamaz).
  const justClearedHistoricRef = useRef(false);
  const prevHistoricDateRef = useRef<Date | null>(null);
  useEffect(() => {
    const wasHistoric = prevHistoricDateRef.current !== null;
    if (selectedHistoricDate) {
      loadHistoricOrders(selectedHistoricDate);
    } else {
      setHistoricOrders([]);
      setHistoricTrendyolOrders([]);
      setHistoricGetirOrders([]);
      setHistoricMigrosOrders([]);
      setHistoricYemeksepetiOrders([]);
      setHistoricQrMenuOrders([]);
      setHistoricEcommerceOrders([]);
      setHistoricKioskOrders([]);
      if (wasHistoric) {
        // Asagidaki ikinci useEffect kanli fetch'leri yapacak.
        justClearedHistoricRef.current = true;
      }
    }
    prevHistoricDateRef.current = selectedHistoricDate;
  }, [selectedHistoricDate, loadHistoricOrders]);

  // SignalR "TrendyolOrderUpdated" push'unu setTrendyolOrders'a bridge eder.
  // Bu hook çağrısı useSignalR'dan sonra olduğu için ref'i burada bağlıyoruz.
  // setTrendyolOrders, handleTrendyolNewOrder, handleTrendyolOrderCancelled değiştiğinde
  // bridge fonksiyonu yeniden oluşturulur.
  useEffect(() => {
    trendyolPushBridgeRef.current = (info: TrendyolOrderUpdatedInfo) => {
      setTrendyolOrders(prev => {
        const idx = prev.findIndex(o => o.id === info.order.id);
        if (idx === -1) {
          return [info.order, ...prev];
        }
        const updated = [...prev];
        // Mevcut optimistic gtsPaid bayrağını overwrite etmeyelim. invoice/paid
        // alanları TrendyolOrder DTO'sunda flat değil; gtsPaid yeterli sinyal.
        const prevOrder = prev[idx];
        updated[idx] = {
          ...info.order,
          gtsPaid: prevOrder.gtsPaid || info.order.gtsPaid,
        };
        return updated;
      });

      // Yeni sipariş ise tip ve cancel notifikasyonu tetikle (mevcut polling
      // davranışını taklit ederek).
      //
      // Durum kapısı 'Created' ile sınırlı DEĞİL: local-service 10 saniyede bir
      // yokluyor ve sipariş 'Created' durumunda bundan daha kısa kalabiliyor.
      // Sipariş ilk kez 'Picking' iken görüldüğünde isNew=true olur; eski kapı
      // bunu eleyip bildirimi kalıcı olarak düşürüyordu.
      if (info.isNew && !info.duplicateNewOrder && isNewOrderNotifiableStatus(info.order.packageStatus)) {
        handleTrendyolNewOrder(info.order, info.notificationKey);
      } else if (info.statusChanged) {
        const cancelledStatuses = new Set(['Cancelled', 'UnSupplied', 'Unsupplied', 'Returned', 'UnDelivered']);
        if (cancelledStatuses.has(info.order.packageStatus || '')) {
          handleTrendyolOrderCancelled(info.order);
        }
      }
    };
    return () => {
      trendyolPushBridgeRef.current = null;
    };
  }, [setTrendyolOrders, handleTrendyolNewOrder, handleTrendyolOrderCancelled]);

  // Trendyol ürün açıklamaları debug log
  useEffect(() => {
    if (trendyolProductDescriptions.size > 0) {
      console.log(`[PackageOrderScreen] Trendyol ürün açıklamaları yüklendi: ${trendyolProductDescriptions.size} ürün`, 
        Object.fromEntries(Array.from(trendyolProductDescriptions.entries()).slice(0, 10))
      );
    }
  }, [trendyolProductDescriptions]);

  // Platform siparisindan unmapped urunleri cikar (tum platformlarda ortak helper)
  const getUnmappedProductsForOrder = useCallback((order: PackageOrder): Array<{ productName: string; productId?: string | null; platform: string }> => {
    const platform = order.source;
    const products: Array<{ productName: string; productId?: string | null; platform: string }> = [];

    if (platform === 'trendyol') {
      const trdOrder = trendyolOrders.find(o => {
        const converted = convertTrendyolOrderToPackageOrder(o);
        return converted.id === order.id;
      });
      if (trdOrder?.lines) {
        for (const line of trdOrder.lines) {
          const found = mappingLookup.get(`trendyol_pid_${line.productId}`)
            || mappingLookup.get(`trendyol_${(line.name || '').toLowerCase().trim()}`);
          if (!found) {
            products.push({ productName: line.name || 'Ürün', productId: line.productId?.toString() || null, platform });
          }
        }
      }
    } else if (platform === 'getir') {
      const gOrder = getirOrders.find(o => {
        const converted = convertGetirOrderToPackageOrder(o);
        return converted.id === order.id;
      });
      if (gOrder?.items) {
        for (const item of gOrder.items) {
          const found = mappingLookup.get(`getir_pid_${item.id}`)
            || mappingLookup.get(`getir_${(item.name || '').toLowerCase().trim()}`);
          if (!found) {
            products.push({ productName: item.name || 'Ürün', productId: item.id?.toString() || null, platform });
          }
        }
      }
    } else if (platform === 'migros') {
      const mOrder = migrosOrders.find(o => {
        const converted = convertMigrosOrderToPackageOrder(o);
        return converted.id === order.id;
      });
      if (mOrder?.items) {
        for (const item of mOrder.items) {
          const found = mappingLookup.get(`migros_pid_${item.productId}`)
            || mappingLookup.get(`migros_${(item.name || '').toLowerCase().trim()}`);
          if (!found) {
            products.push({ productName: item.name || 'Ürün', productId: item.productId?.toString() || null, platform });
          }
        }
      }
    } else if (platform === 'yemeksepeti') {
      const ysOrder = yemeksepetiOrders.find(o => {
        const converted = convertYemeksepetiOrderToPackageOrder(o);
        return converted.id === order.id;
      });
      if (ysOrder?.items) {
        for (const item of ysOrder.items) {
          if (item.categoryName) continue;
          const found = mappingLookup.get(`yemeksepeti_pid_${item.productId}`)
            || mappingLookup.get(`yemeksepeti_${(item.name || '').toLowerCase().trim()}`);
          if (!found) {
            products.push({ productName: item.name || 'Ürün', productId: item.productId?.toString() || null, platform });
          }
        }
      }
    }

    return products;
  }, [trendyolOrders, getirOrders, migrosOrders, yemeksepetiOrders, mappingLookup]);

  // Platform siparislerinde eslesmemis urun sayisini hesapla
  const getUnmappedCount = useCallback((order: PackageOrder): number => {
    const platformSources = ['trendyol', 'getir', 'migros', 'yemeksepeti'];
    if (!platformSources.includes(order.source)) return 0;
    return getUnmappedProductsForOrder(order).length;
  }, [getUnmappedProductsForOrder]);

  // Badge tiklandiginda eslestirme modalini ac
  const handleUnmappedBadgeClick = useCallback((order: PackageOrder) => {
    const products = getUnmappedProductsForOrder(order);
    if (products.length > 0) {
      setUnmappedProductsForModal(products);
      setCategoryMappingModalOpen(true);
    }
  }, [getUnmappedProductsForOrder]);
  
  // Local service guncelleme kontrolu
  const {
    hasPendingUpdate: hasLocalServiceUpdate,
    version: localServiceUpdateVersion,
    dismissed: localServiceUpdateDismissed,
    applyUpdateAndRestart: applyLocalServiceUpdate,
    dismiss: dismissLocalServiceUpdate,
  } = useLocalServiceUpdateCheck(screenData?.localServiceUrl || null);

  const [showLocalServiceUpdateModal, setShowLocalServiceUpdateModal] = useState(false);

  useEffect(() => {
    if (hasLocalServiceUpdate && !localServiceUpdateDismissed) {
      setShowLocalServiceUpdateModal(true);
    }
  }, [hasLocalServiceUpdate, localServiceUpdateDismissed]);

  // Trendyol local-service bağlantı uyarı modal state
  const [showLocalServiceWarningModal, setShowLocalServiceWarningModal] = useState(false);
  const localServiceWarningDismissedAtRef = useRef<number>(0);
  const isTrendyolEnabled = !!(screenData?.integrationSettings?.showTrendyolOrders) && !!(screenData?.platformIntegrations?.some(p => p.platform === 'trendyol_yemek' && p.isEnabled));

  useEffect(() => {
    if (!isTrendyolEnabled) return;

    if (!trendyolLocalServiceConnected) {
      const timeSinceDismiss = Date.now() - localServiceWarningDismissedAtRef.current;
      if (timeSinceDismiss > 60000) {
        setShowLocalServiceWarningModal(true);
      }
    } else {
      setShowLocalServiceWarningModal(false);
    }
  }, [isTrendyolEnabled, trendyolLocalServiceConnected]);

  const handleDismissLocalServiceWarning = useCallback(() => {
    setShowLocalServiceWarningModal(false);
    localServiceWarningDismissedAtRef.current = Date.now();
  }, []);

  const handleRetryLocalServiceConnection = useCallback(async () => {
    setShowLocalServiceWarningModal(false);
    await refreshTrendyolOrders();
  }, [refreshTrendyolOrders]);

  // Trendyol polling durdu uyarısı: local-service AYAKTA ama sipariş sorgulama
  // tick'i dönmüyor (2026-08-03 vakası: 11 saat sessiz kesinti). Kasiyer bunu
  // görmeli; tek çözüm masaüstü yazılımını yeniden başlatmak. Kapatılırsa 10dk
  // sonra durum düzelmediyse tekrar gösterilir.
  const [showPollingStaleModal, setShowPollingStaleModal] = useState(false);
  const pollingStaleDismissedAtRef = useRef<number>(0);

  useEffect(() => {
    if (!isTrendyolEnabled || !trendyolServerPollingStale) {
      setShowPollingStaleModal(false);
      return;
    }
    const remainingDismissMs = 10 * 60 * 1000 - (Date.now() - pollingStaleDismissedAtRef.current);
    if (remainingDismissMs <= 0) {
      setShowPollingStaleModal(true);
      return;
    }
    // Kapatılmış: süre dolunca hâlâ stale ise tekrar göster
    const timerId = setTimeout(() => setShowPollingStaleModal(true), remainingDismissMs);
    return () => clearTimeout(timerId);
  }, [isTrendyolEnabled, trendyolServerPollingStale, showPollingStaleModal]);

  const handleDismissPollingStale = useCallback(() => {
    setShowPollingStaleModal(false);
    pollingStaleDismissedAtRef.current = Date.now();
  }, []);

  // Çoklu mağaza uyarısı: şubede birden fazla Trendyol mağazası tanımlı ama yerel
  // servis sürümü yalnızca birini yoklayabiliyor. Diğer mağazaların siparişleri
  // hiç düşmez; sessiz kalırsak sorun günler sonra "kayıp sipariş" olarak döner.
  const [showMultiStoreWarning, setShowMultiStoreWarning] = useState(false);
  const multiStoreDismissedAtRef = useRef<number>(0);

  useEffect(() => {
    if (!isTrendyolEnabled || !trendyolMultiStoreUnsupported) {
      setShowMultiStoreWarning(false);
      return;
    }
    const remainingDismissMs = 30 * 60 * 1000 - (Date.now() - multiStoreDismissedAtRef.current);
    if (remainingDismissMs <= 0) {
      setShowMultiStoreWarning(true);
      return;
    }
    const timerId = setTimeout(() => setShowMultiStoreWarning(true), remainingDismissMs);
    return () => clearTimeout(timerId);
  }, [isTrendyolEnabled, trendyolMultiStoreUnsupported]);

  const handleDismissMultiStoreWarning = useCallback(() => {
    setShowMultiStoreWarning(false);
    multiStoreDismissedAtRef.current = Date.now();
  }, []);

  // ✅ YENİ: Kullanıcı menüsü fonksiyonları
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      // Hedef rota çıkıştan ÖNCE hesaplanır: terminal oturumu tespiti oturumdaki
      // kimliğe bakıyor ve logout onu siliyor.
      const target = getPostLogoutRoute();
      await logout();
      handleCloseUserMenu();
      navigate(target);
    } catch (error) {
      console.error("Çıkış yapılırken hata oluştu:", error);
    }
  };
  
  // Kullanıcı adı
  const userDisplayName = useMemo(() => {
    if (userDetails?.firstName && userDetails?.lastName) {
      return `${userDetails.firstName} ${userDetails.lastName}`;
    }
    return currentUser?.email?.split('@')[0] || 'Kullanıcı';
  }, [userDetails, currentUser]);
  
  // ✅ YENİ: Ayarlar Modal State
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [screenDataRefreshKey, setScreenDataRefreshKey] = useState(0);
  
  // Getir restoran kapali modal state.
  // Yukleniyor bayraklari entegrasyon kimligiyle anahtarli: bir markanin
  // dugmesine basmak digerinin dugmesini kilitlemesin.
  const [getirRestaurantClosedModalOpen, setGetirRestaurantClosedModalOpen] = useState(false);
  const [getirRestaurantOpening, setGetirRestaurantOpening] = useState<Record<number, boolean>>({});
  
  // Migros cancel modal state
  const [migrosCancelModalOpen, setMigrosCancelModalOpen] = useState(false);
  const [migrosCancelledOrderInfo, setMigrosCancelledOrderInfo] = useState<any>(null);
  
  // Yemeksepeti cancel modal state
  const [ysCancelModalOpen, setYsCancelModalOpen] = useState(false);
  const [ysCancelledOrderInfo, setYsCancelledOrderInfo] = useState<any>(null);
  
  // Migros restoran kapali modal state
  const [migrosRestaurantClosedModalOpen, setMigrosRestaurantClosedModalOpen] = useState(false);
  const [migrosRestaurantOpening, setMigrosRestaurantOpening] = useState<Record<number, boolean>>({});
  
  // Yemeksepeti vendor kapali modal state
  const [ysVendorClosedModalOpen, setYsVendorClosedModalOpen] = useState(false);
  const [ysVendorOpening, setYsVendorOpening] = useState<Record<number, boolean>>({});
  
  // Trendyol Yemek state'leri
  const [trendyolDetailModalOpen, setTrendyolDetailModalOpen] = useState(false);
  const [trendyolDetailOrder, setTrendyolDetailOrder] = useState<TrendyolOrder | null>(null);
  const [trendyolCancelModalOpen, setTrendyolCancelModalOpen] = useState(false);
  const [trendyolCancelNotifModalOpen, setTrendyolCancelNotifModalOpen] = useState(false);
  const [trendyolCancelledNotifOrder, setTrendyolCancelledNotifOrder] = useState<TrendyolOrder | null>(null);
  const [trendyolCancelOrder, setTrendyolCancelOrder] = useState<TrendyolOrder | null>(null);
  const [trendyolVendorClosedModalOpen, setTrendyolVendorClosedModalOpen] = useState(false);
  const [trendyolVendorOpening, setTrendyolVendorOpening] = useState<Record<number, boolean>>({});
  
  // Restoran kapatma modal state
  const [closeRestaurantModalOpen, setCloseRestaurantModalOpen] = useState(false);
  const [closeRestaurantPlatform, setCloseRestaurantPlatform] = useState<string | null>(null);
  const [closingRestaurant, setClosingRestaurant] = useState(false);

  // Migros detail modal state
  const [migrosDetailModalOpen, setMigrosDetailModalOpen] = useState(false);
  const [migrosDetailOrderId, setMigrosDetailOrderId] = useState<number | null>(null);
  
  // Yemeksepeti detail modal state
  const [ysDetailModalOpen, setYsDetailModalOpen] = useState(false);
  const [ysDetailOrderId, setYsDetailOrderId] = useState<number | null>(null);
  
  // Migros reject modal state
  const [migrosRejectModalOpen, setMigrosRejectModalOpen] = useState(false);
  const [migrosRejectOrderId, setMigrosRejectOrderId] = useState<number | null>(null);
  const [migrosRejectOrderStatus, setMigrosRejectOrderStatus] = useState<string>('');
  const [migrosCancelReasons, setMigrosCancelReasons] = useState<any[]>([]);
  const [selectedMigrosCancelReasonId, setSelectedMigrosCancelReasonId] = useState<number | null>(null);
  
  // Yemeksepeti reject modal state
  const [ysRejectModalOpen, setYsRejectModalOpen] = useState(false);
  const [ysRejectOrderId, setYsRejectOrderId] = useState<number | null>(null);
  const [ysRejectReasons, setYsRejectReasons] = useState<any[]>([]);
  const [selectedYsRejectReason, setSelectedYsRejectReason] = useState<string | null>(null);

  // Migros reject modali acildiginda iptal sebeplerini yukle
  useEffect(() => {
    if (migrosRejectModalOpen && migrosCancelReasons.length === 0) {
      const loadReasons = async () => {
        if (!companyId) return;
        try {
          const resp = await migrosApi.get(`/api/migros-orders/cancel-reasons`);
          const result = resp.data;
          if (result.success && result.data) {
            setMigrosCancelReasons(result.data);
          }
        } catch (err) {
          console.error('[Migros] Iptal sebepleri yuklenemedi:', err);
        }
      };
      loadReasons();
    }
  }, [migrosRejectModalOpen, migrosCancelReasons.length, companyId]);

  // Yemeksepeti reject modali acildiginda iptal sebeplerini yukle
  useEffect(() => {
    if (ysRejectModalOpen && ysRejectReasons.length === 0) {
      const loadReasons = async () => {
        if (!companyId) return;
        try {
          const params = new URLSearchParams();
          if (screenData?.branchId) params.append('branchId', screenData.branchId);
          const queryStr = params.toString();
          const resp = await yemeksepetiApi.get(`/api/ys-orders/reject-reasons${queryStr ? '?' + queryStr : ''}`);
          const result = resp.data;
          const success = result.Success ?? result.success;
          const data = result.Data ?? result.data;
          if (success && data) {
            setYsRejectReasons(data);
          }
        } catch (err) {
          console.error('[YS] Red sebepleri yuklenemedi:', err);
        }
      };
      loadReasons();
    }
  }, [ysRejectModalOpen, ysRejectReasons.length, companyId, screenData?.branchId]);

  // Getir siparis durum guncelleme sonrasi 60 sn geri sayim (SİPARİŞ BAZLI)
  // Getir API kuralı: verify→prepare VE prepare→deliver/handover arasında en az 60 sn bekleme zorunlu
  // Her sipariş için ayrı geri sayım: { [orderId]: kalanSaniye }
  const [getirCountdownMap, setGetirCountdownMap] = useState<Record<string | number, number>>({});
  const getirCountdownRef = useRef<NodeJS.Timeout | null>(null);

  // Geri sayım timer'ı - tüm aktif countdown'ları her saniye düşür
  useEffect(() => {
    const activeCountdowns = Object.values(getirCountdownMap).some(v => v > 0);
    if (activeCountdowns) {
      getirCountdownRef.current = setTimeout(() => {
        setGetirCountdownMap(prev => {
          const next: Record<string | number, number> = {};
          for (const [key, val] of Object.entries(prev)) {
            if (val > 1) {
              next[key] = val - 1;
            }
            // val <= 1 ise key'i map'ten düşür (temizle)
          }
          return next;
        });
      }, 1000);
    }
    return () => {
      if (getirCountdownRef.current) {
        clearTimeout(getirCountdownRef.current);
      }
    };
  }, [getirCountdownMap]);

  // ✅ Getir restoran durumu polling (5 dakikada bir, her mağaza için ayrı)
  useEffect(() => {
    const integration = screenData?.integrationSettings;
    if (!integration?.showGetirOrders) return;

    const allIntegrations = screenData?.platformIntegrations || [];
    const getirIntegrations = allIntegrations.filter(
      p => p.platform === 'getir' && p.isEnabled
    );

    // Şubede başka platform tanımlı ama Getir yoksa sorgu yapılmaz: aksi halde
    // kimliksiz çağrı sunucuda mağaza bulamaz ve durum "KAPALI" görünür.
    if (getirIntegrations.length === 0 && allIntegrations.length > 0) return;

    // Liste boşsa entegrasyonlar henüz yüklenmemiş olabilir (by-branch çağrısı
    // başarısız) → kimliksiz sorgu, tek mağaza kuralı.
    const checkAll = () => {
      if (getirIntegrations.length === 0) {
        checkGetirRestaurantStatus();
        return;
      }
      getirIntegrations.forEach(i => checkGetirRestaurantStatus(i.id));
    };

    checkAll();

    const interval = setInterval(checkAll, 5 * 60 * 1000); // 5 dakika

    return () => clearInterval(interval);
  }, [checkGetirRestaurantStatus, screenData?.integrationSettings, screenData?.platformIntegrations]);

  // ✅ Getir token 50 dakikada bir otomatik yenileme (token 1 saat geçerli)
  const getirTokenRefreshRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const integration = screenData?.integrationSettings;
    if (!integration?.showGetirOrders || !companyId) return;

    // Şubede aktif Getir entegrasyonu yoksa token yenilenmez (Migros/Yemeksepeti
    // ile aynı kural). Ekran ayarında "Getir'i göster" açık olsa bile kimlik
    // bilgisi olmadığı için sunucu token üretemez ve uç 500 döner.
    const hasGetirIntegration = (screenData?.platformIntegrations || []).some(
      p => p.platform === 'getir' && p.isEnabled
    );
    if (!hasGetirIntegration) return;

    const getirApiUrl = process.env.REACT_APP_GETIRAPI_URL;
    if (!getirApiUrl) {
      console.warn('⚠️ [GetirToken] REACT_APP_GETIRAPI_URL tanımlı değil, token yenileme atlanıyor');
      return;
    }

    // İlk açılışta hemen token al
    refreshGetirToken();

    // Her 50 dakikada bir yenile (token 1 saat geçerli, 10 dk güvenlik marjı)
    getirTokenRefreshRef.current = setInterval(refreshGetirToken, 50 * 60 * 1000);

    return () => {
      if (getirTokenRefreshRef.current) {
        clearInterval(getirTokenRefreshRef.current);
        getirTokenRefreshRef.current = null;
      }
    };
  }, [companyId, refreshGetirToken, screenData?.integrationSettings, screenData?.platformIntegrations]);
  
  // ✅ sseConnectionStatus ve SSE ref'leri artık usePhoneCalls hook'undan geliyor
  
  // ✅ LocalStorage ayarları (Custom Hook)
  const {
    phoneRingSoundEnabled,
    setPhoneRingSoundEnabled,
    selectedRingSound,
    setSelectedRingSound,
    incomingCallModalEnabled,
    setIncomingCallModalEnabled,
    ringSoundOptions,
  } = useLocalSettings();
  
  // ✅ Zil sesi yönetimi (Custom Hook)
  const { playRingSound, stopRingSound } = useRingSound({
    enabled: phoneRingSoundEnabled,
    selectedSound: selectedRingSound,
    soundOptions: ringSoundOptions,
  });
  
  // ✅ Yeni sipariş sesi yönetimi (Custom Hook)
  const { playNewOrderSound, stopNewOrderSound, testNewOrderSound } = useNewOrderSound();
  
  // ============================================================================
  // BİRLEŞİK KUYRUK — TELEFON KARAR MODALI STATE'LERİ
  // usePhoneCalls'tan ÖNCE tanımlanır çünkü hook'a isScreenBusy /
  // onCallRequiresDecision callback'lerini veriyoruz.
  // ============================================================================

  // Zorunlu karar modalı: hangi arama için ve hangi modda soruluyor
  const [callDecisionCall, setCallDecisionCall] = useState<QueuedCall | null>(null);
  const [callDecisionMode, setCallDecisionMode] = useState<'new-call' | 'next-in-queue'>('new-call');
  // "Beklemeye Devam Et" snooze'u — bu zamana kadar promotion modalı yeniden açılmaz
  const [callDecisionSnoozeUntil, setCallDecisionSnoozeUntil] = useState(0);
  // Closure-safe dedupe ref'i (SSE event'i render dışında gelir)
  const callDecisionCallRef = useRef<QueuedCall | null>(null);

  // Aktif modal varlığını ref ile takip eder (closure güncel kalsın).
  // Asıl değer aşağıda useMemo (anyModalOpen) tarafından güncellenir.
  // Bu yapı sayesinde onNewCall callback'i sabit kalır; modal state'leri
  // her değiştiğinde setOnNewCall yeniden çağrılmaz (performans).
  const anyModalOpenRef = useRef(false);
  // Müşteri bağlamı açık mı? (currentCustomer / telefon filtresi / cevaplanmış arama)
  // — aşağıdaki customerContextBusy memo'su günceller.
  const customerContextBusyRef = useRef(false);

  // Ekran meşgul tanımı (telefon routing'i için): modal açık VEYA müşteri bağlamı açık.
  // activeCall kontrolü hook'un kendi içinde yapılır.
  const isScreenBusyForCalls = useCallback(
    () => anyModalOpenRef.current || customerContextBusyRef.current,
    [],
  );

  // Meşgulken yeni arama geldi — zorunlu karar modalını aç.
  // Zaten bir karar bekleniyorsa yenisi açılmaz; arama held olarak kuyrukta
  // bekler, sırası gelince (promotion effect / bildirim) tekrar sorulur.
  const handleCallRequiresDecision = useCallback((qc: QueuedCall) => {
    if (callDecisionCallRef.current) {
      console.log('[CallDecision] Modal zaten açık — yeni arama kuyrukta bekliyor:', qc.call.phone);
      return;
    }
    callDecisionCallRef.current = qc;
    setCallDecisionMode('new-call');
    setCallDecisionCall(qc);
  }, []);

  // ✅ Telefon aramaları yönetimi (Custom Hook) — Kuyruk mimarisi (Q6)
  const {
    sseConnectionStatus,
    incomingCall,
    isCallAnswered,
    setIsCallAnswered,
    activePhoneCallId,
    incomingCallPopupOpen,
    setIncomingCallPopupOpen,
    // Kuyruk API (Q5-Q6)
    activeCall,
    pendingCalls,
    updateActiveCallCustomerName,
    recentCalls,
    loadTodayCalls,
    searchCustomerByPhone,
    answerCall,
    endCallUnanswered,
    endCallComplete,
    takeCallAsCustomer,
    setOnCustomerFound,
    setOnNewCall,
  } = usePhoneCalls({
    companyId,
    screenId,
    branchId: screenData?.branchId,
    packageScreenId: screenData?.packageScreenId,
    localServiceUrl: screenData?.localServiceUrl,
    playRingSound,
    stopRingSound,
    incomingCallModalEnabled,
    // Birleşik kuyruk: ekran meşgulken yeni arama held-pending'e gider
    isScreenBusy: isScreenBusyForCalls,
    onCallRequiresDecision: handleCallRequiresDecision,
  });
  
  // ✅ Hook callback'lerini ayarla - Müşteri bulunduğunda
  useEffect(() => {
    setOnCustomerFound(() => (customer: PackageCustomer, defaultAddressId: number | null) => {
      setCurrentCustomer(customer);
      if (defaultAddressId) {
        setSelectedAddressId(defaultAddressId);
      } else if (customer.addresses.length > 0) {
        setSelectedAddressId(customer.addresses[0].id);
      }
    });
  }, [setOnCustomerFound]);
  
  // ✅ Müşteri bağlamı meşguliyeti — telefon geldiğinde ekranın "dolu" sayılma
  // koşulu: müşteri kartı açık VEYA telefon filtresi aktif VEYA cevaplanmış
  // arama var. usePhoneCalls isScreenBusy bunu ref üzerinden okur.
  const customerContextBusy = useMemo(
    () => !!currentCustomer || !!customerPhoneFilter || !!customerNameFilter || isCallAnswered,
    [currentCustomer, customerPhoneFilter, customerNameFilter, isCallAnswered],
  );
  useEffect(() => {
    customerContextBusyRef.current = customerContextBusy;
  }, [customerContextBusy]);

  // ✅ Hook callback'lerini ayarla - Yeni arama geldiğinde
  useEffect(() => {
    setOnNewCall(() => () => {
      // Modal açıkken (yeni müşteri kaydı, adres ekleme, ödeme, hızlı satış vb.)
      // arka plandaki müşteri context'ini SİLME — kasiyer mevcut işine devam etsin.
      // Yeni arama hâlâ incomingCall state'inde + son aramalar listesinde görünür.
      if (anyModalOpenRef.current) {
        console.log('[onNewCall] Modal acik — arka plan musteri context korundu');
        return;
      }
      // Yeni arama geldiğinde önceki müşteri seçimini temizle
      setCustomerPhoneFilter(null);
      setCustomerNameFilter(null);
      setCurrentCustomer(null);
      setSelectedAddressId(null);
    });
  }, [setOnNewCall]);
  
  // ✅ getOrderSourceFromMasaSiparisi ve convertMasaSiparisiToPackageOrder
  // artık ./utils/helpers'dan import ediliyor
  
  // ✅ fetchMasaSiparisleri ve updateMasaSiparisiStatus artık useSignalR hook'undan geliyor
  
  // ✅ Yeni sipariş sesi artık useNewOrderQueue hook'u içinde yönetiliyor.
  //    (playNewOrderSound/stopNewOrderSound hook'a iletiliyor — promotion sırasında çalar.)
  //
  // ✅ handleCloseNewOrderModal: kuyruktaki aktif siparişi tamamlandı işaretler.
  //    Hook stopNewOrderSound'u çağırır. Reactive promotion sıradaki pending'i alır.
  const handleCloseNewOrderModal = useCallback(() => {
    completeActiveNewOrderRef.current?.();
  }, []);

  // ✅ Getir iptal modalını kapat ve siparişleri yenile
  const handleCloseGetirCancelModal = useCallback(() => {
    setGetirCancelModalOpen(false);
    setCancelledOrderInfo(null);
    fetchGetirOrders();
  }, [fetchGetirOrders]);

  const handleCloseMigrosCancelModal = useCallback(() => {
    setMigrosCancelModalOpen(false);
    setMigrosCancelledOrderInfo(null);
    fetchMigrosOrders();
  }, [fetchMigrosOrders]);

  const handleCloseTrendyolCancelNotifModal = useCallback(() => {
    setTrendyolCancelNotifModalOpen(false);
    setTrendyolCancelledNotifOrder(null);
  }, []);

  // 1dk yenileme sonrası Getir/YS/Migros iptal algılama
  useEffect(() => {
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      prevGetirStatusRef.current = new Map(getirOrders.map(o => [o.id, o.status]));
      prevMigrosStatusRef.current = new Map(migrosOrders.map(o => [o.id, o.status]));
      prevYsStatusRef.current = new Map(yemeksepetiOrders.map(o => [o.id, o.status]));
      return;
    }

    const getirCancelStatuses = new Set([1500, 1600]);
    for (const order of getirOrders) {
      const prev = prevGetirStatusRef.current.get(order.id);
      if (prev !== undefined && !getirCancelStatuses.has(prev) && getirCancelStatuses.has(order.status)) {
        handleGetirOrderCancelled({
          getirId: order.getirId || String(order.id),
          status: order.status,
          confirmationId: order.confirmationId || '',
          cancelReasonText: 'Sipariş iptal edildi',
          clientName: order.clientName || undefined,
          cancelledAt: new Date().toISOString(),
        });
      }
    }
    prevGetirStatusRef.current = new Map(getirOrders.map(o => [o.id, o.status]));

    const migrosCancelStatuses = new Set(['REJECTED', 'CANCELLED']);
    for (const order of migrosOrders) {
      const prev = prevMigrosStatusRef.current.get(order.id);
      if (prev !== undefined && !migrosCancelStatuses.has(prev) && migrosCancelStatuses.has(order.status)) {
        setMigrosCancelModalOpen(true);
        setMigrosCancelledOrderInfo({ orderId: String(order.migrosOrderId || order.id), status: order.status });
        playCancelSound();
      }
    }
    prevMigrosStatusRef.current = new Map(migrosOrders.map(o => [o.id, o.status]));

    const ysCancelStatuses = new Set(['REJECTED', 'CANCELLED']);
    for (const order of yemeksepetiOrders) {
      const prev = prevYsStatusRef.current.get(order.id);
      if (prev !== undefined && !ysCancelStatuses.has(prev) && ysCancelStatuses.has(order.status)) {
        setYsCancelModalOpen(true);
        // Modal'ın zengin görünümü için tam YS sipariş objesini de iletiyoruz.
        // Hem polling hem SignalR kanallarında aynı yapı (`rawOrder`) kullanılır.
        setYsCancelledOrderInfo({
          orderId: order.token || String(order.id),
          status: order.status,
          customerName: (order as any).customerFullName || (order as any).customerName,
          cancelledAt: new Date().toISOString(),
          rawOrder: order,
        });
        playCancelSound();
      }
    }
    prevYsStatusRef.current = new Map(yemeksepetiOrders.map(o => [o.id, o.status]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getirOrders, migrosOrders, yemeksepetiOrders]);

  const handleOpenCloseRestaurantModal = useCallback(() => {
    setCloseRestaurantPlatform(selectedTab);
    setCloseRestaurantModalOpen(true);
  }, [selectedTab]);

  const handleCloseRestaurantConfirm = useCallback(async (reason?: string, integrationId?: number) => {
    if (!closeRestaurantPlatform) return;
    let success = false;
    try {
      if (closeRestaurantPlatform === 'getir') {
        success = await closeGetirRestaurant(integrationId);
      } else if (closeRestaurantPlatform === 'migros') {
        success = await closeMigrosRestaurant(integrationId);
      } else if (closeRestaurantPlatform === 'yemeksepeti') {
        success = await closeYemeksepetiVendor(reason, integrationId);
      } else if (closeRestaurantPlatform === 'trendyol') {
        success = await closeTrendyolVendor(integrationId);
      }
      if (success) {
        setSnackbar({ open: true, message: `${closeRestaurantPlatform.charAt(0).toUpperCase() + closeRestaurantPlatform.slice(1)} restoranı kapatıldı`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Restoran kapatılamadı', severity: 'error' });
      }
    } catch (error) {
      console.error('Restoran kapatma hatası:', error);
      setSnackbar({ open: true, message: 'Restoran kapatılırken hata oluştu', severity: 'error' });
    }
    setCloseRestaurantModalOpen(false);
  }, [closeRestaurantPlatform, closeGetirRestaurant, closeMigrosRestaurant, closeYemeksepetiVendor, closeTrendyolVendor]);

  // ✅ playRingSound ve stopRingSound artık useRingSound hook'undan geliyor

  // Tab tanımları - count'ları hesapla (durum filtresine göre + tarih filtresi)
  const orderTabs = useMemo(() => {
    const isVisible = (o: PackageOrder) => isInActiveDayWindow(o.orderDate);
    
    const visibleOrders = orders.filter(isVisible);
    const filteredByStatus = statusFilter === 'all' 
      ? visibleOrders 
      : visibleOrders.filter(o => o.status === statusFilter);
    
    const getirVisible = getirOrders.map(convertGetirOrderToPackageOrder).filter(isVisible);
    const migrosVisible = migrosOrders.map(convertMigrosOrderToPackageOrder).filter(isVisible);
    const ysVisible = yemeksepetiOrders.map(convertYemeksepetiOrderToPackageOrder).filter(isVisible);
    const trendyolVisible = trendyolOrders.map(convertTrendyolOrderToPackageOrder).filter(isVisible);
    const qrVisible = qrMenuOrders.map(convertMasaSiparisiToPackageOrder).filter(isVisible);
    const ecommerceVisible = ecommerceOrders.map(convertMasaSiparisiToPackageOrder).filter(isVisible);
    const kioskVisible = kioskOrders.map(convertMasaSiparisiToPackageOrder).filter(isVisible);
    
    const statusFilteredGetir = statusFilter === 'all' ? getirVisible : getirVisible.filter(o => o.status === statusFilter);
    const statusFilteredMigros = statusFilter === 'all' ? migrosVisible : migrosVisible.filter(o => o.status === statusFilter);
    const statusFilteredYs = statusFilter === 'all' ? ysVisible : ysVisible.filter(o => o.status === statusFilter);
    const statusFilteredTrendyol = statusFilter === 'all' ? trendyolVisible : trendyolVisible.filter(o => o.status === statusFilter);
    const statusFilteredQr = statusFilter === 'all' ? qrVisible : qrVisible.filter(o => o.status === statusFilter);
    const statusFilteredEcommerce = statusFilter === 'all' ? ecommerceVisible : ecommerceVisible.filter(o => o.status === statusFilter);
    const statusFilteredKiosk = statusFilter === 'all' ? kioskVisible : kioskVisible.filter(o => o.status === statusFilter);
    
    const counts = {
      all: filteredByStatus.length + statusFilteredMigros.length + statusFilteredYs.length + statusFilteredTrendyol.length + statusFilteredQr.length + statusFilteredEcommerce.length + statusFilteredKiosk.length,
      phone: filteredByStatus.filter(o => o.source === 'phone').length,
      yemeksepeti: filteredByStatus.filter(o => o.source === 'yemeksepeti').length + ysVisible.length,
      trendyol: filteredByStatus.filter(o => o.source === 'trendyol').length + trendyolVisible.length,
      getir: filteredByStatus.filter(o => o.source === 'getir').length + getirVisible.length,
      migros: filteredByStatus.filter(o => o.source === 'migros').length + migrosVisible.length,
      kiosk: filteredByStatus.filter(o => o.source === 'kiosk').length + kioskVisible.length,
      qr: filteredByStatus.filter(o => o.source === 'qr').length + qrVisible.length,
      ecommerce: filteredByStatus.filter(o => o.source === 'ecommerce').length + ecommerceVisible.length,
    };
    // ✅ YENİ: Entegrasyon ayarlarına göre tab'ları filtrele
    const integration = screenData?.integrationSettings;
    
    const allTabs = [
      { id: 'all', label: t('orderManagement.screen.tabs.all'), icon: <AllInboxIcon />, count: counts.all, visible: true },
      { id: 'phone', label: t('orderManagement.screen.tabs.phone'), icon: <PhoneIcon />, count: counts.phone, visible: integration?.showPhoneOrders ?? true },
      { id: 'yemeksepeti', label: t('orderManagement.screen.tabs.yemeksepeti'), icon: <RestaurantMenuIcon />, count: counts.yemeksepeti, visible: integration?.showYemekSepetiOrders ?? true },
      { id: 'trendyol', label: t('orderManagement.screen.tabs.trendyol'), icon: <TwoWheelerIcon />, count: counts.trendyol, visible: integration?.showTrendyolOrders ?? true },
      { id: 'getir', label: t('orderManagement.screen.tabs.getir'), icon: null, count: 0, visible: false },
      { id: 'migros', label: t('orderManagement.screen.tabs.migros'), icon: <StorefrontIcon />, count: counts.migros, visible: integration?.showMigrosOrders ?? true },
      { id: 'kiosk', label: t('orderManagement.screen.tabs.kiosk'), icon: <PointOfSaleIcon />, count: counts.kiosk, visible: integration?.showKioskOrders ?? true },
      { id: 'qr', label: t('orderManagement.screen.tabs.qr'), icon: <QrCode2Icon />, count: counts.qr, visible: integration?.showQrMenuOrders ?? true },
      { id: 'ecommerce', label: t('orderManagement.screen.tabs.ecommerce'), icon: <ShoppingBagIcon />, count: counts.ecommerce, visible: integration?.showEcommerceOrders ?? true },
    ];
    
    // Sadece visible olan tab'ları döndür
    return allTabs.filter(tab => tab.visible).map(({ visible, ...rest }) => rest);
  }, [statusFilter, screenData?.integrationSettings, qrMenuOrders.length, ecommerceOrders.length, kioskOrders.length, getirOrders.length, migrosOrders.length, yemeksepetiOrders.length, trendyolOrders.length, orders, isInActiveDayWindow, t]);

  // Bugünün (ve gece modunda dünün) siparişlerini göster — historic mod aktifken
  // tüm tarihlere izin verir (server zaten secilen gunu cekti).
  const shouldShowOrder = useCallback((order: PackageOrder): boolean => {
    return isInActiveDayWindow(order.orderDate);
  }, [isInActiveDayWindow]);

  // ==========================================================================
  // ÇOKLU MAĞAZA — marka etiketi, sütun görünürlüğü ve mağaza filtresi
  // ==========================================================================

  const foodIntegrations = useMemo<PlatformIntegration[]>(
    () => (screenData?.platformIntegrations || []).filter(p => isFoodPlatform(p.platform)),
    [screenData?.platformIntegrations],
  );

  /** Şubede aynı platformdan birden fazla aktif entegrasyon var mı? */
  const hasMultiStoreSetup = useMemo(() => {
    const perPlatform = new Map<string, number>();
    foodIntegrations.filter(p => p.isEnabled).forEach(p => {
      const key = normalizePlatformKey(p.platform);
      perPlatform.set(key, (perPlatform.get(key) || 0) + 1);
    });
    return Array.from(perPlatform.values()).some(count => count > 1);
  }, [foodIntegrations]);

  const branchDisplayName = useMemo(
    () => (screenData?.branchId ? getBranchName(screenData.branchId) : ''),
    [screenData?.branchId, getBranchName],
  );

  /**
   * Siparişin mağaza/marka etiketi. Faz 3 öncesi damgasız siparişlerde
   * dış mağaza kimliğinden, o da tutmazsa şube adından çözümlenir.
   */
  const getOrderStoreLabel = useCallback((order: PackageOrder): string => {
    const platform = ORDER_SOURCE_TO_PLATFORM[order.source];
    if (!platform) return '';
    return resolveStoreLabel(
      {
        storeName: order.storeName,
        platformIntegrationId: order.platformIntegrationId,
        platform,
        restaurantId: order.externalStoreId,
        storeId: order.externalStoreId,
      },
      foodIntegrations,
      branchDisplayName,
    );
  }, [foodIntegrations, branchDisplayName]);

  /**
   * Platform başına mağaza listesi ve her mağazanın açık/kapalı durumu.
   * Aç/kapat düğmeleri, sekme rozeti ve durum sorguları hep bunu okur.
   */
  const platformStores = useMemo<Record<string, StoreToggleInfo[]>>(() => {
    const build = (platform: string, openMap: Record<number, boolean>): StoreToggleInfo[] => {
      const integrations = foodIntegrations.filter(
        p => normalizePlatformKey(p.platform) === platform && p.isEnabled,
      );
      return integrations.map(p => ({
        integrationId: p.id,
        label: (p.storeName || '').trim() || branchDisplayName || PLATFORM_LABELS[normalizePlatformKey(p.platform)] || p.platform,
        // Durum sorgusu henüz dönmediyse açık varsayılır: yanlışlıkla "kapalı"
        // uyarısı göstermek operatörü gereksiz telaşlandırıyor.
        isOpen: openMap[p.id] ?? openMap[DEFAULT_STORE_KEY] ?? true,
      }));
    };

    const trendyolOpenMap: Record<number, boolean> = {};
    Object.entries(trendyolVendorStatusMap || {}).forEach(([id, status]) => {
      if (status === 'OPEN' || status === 'CLOSED') trendyolOpenMap[Number(id)] = status === 'OPEN';
    });

    return {
      getir: build('getir', getirRestaurantOpenMap || {}),
      migros: build('migros_yemek', migrosRestaurantOpenMap || {}),
      yemeksepeti: build('yemeksepeti', yemeksepetiVendorOpenMap || {}),
      trendyol: build('trendyol_yemek', trendyolOpenMap),
    };
  }, [
    foodIntegrations,
    branchDisplayName,
    getirRestaurantOpenMap,
    migrosRestaurantOpenMap,
    yemeksepetiVendorOpenMap,
    trendyolVendorStatusMap,
  ]);

  /** Aç/kapat durumu takip edilen sekmeler; platformStores anahtarlarıyla aynı. */
  const PLATFORM_TAB_IDS = ['migros', 'yemeksepeti', 'trendyol'];

  /** Sekme rozeti için: hepsi açık / kısmen açık / hepsi kapalı. */
  const getPlatformOpenState = useCallback((platformTabId: string): PlatformOpenState => {
    const stores = platformStores[platformTabId] || [];
    if (stores.length === 0) return 'open';
    const openCount = stores.filter(s => s.isOpen).length;
    if (openCount === stores.length) return 'open';
    if (openCount === 0) return 'closed';
    return 'partial';
  }, [platformStores]);

  /** En az bir mağaza açık mı? Entegrasyon listesi boşsa eski davranışa düşer. */
  const isAnyStoreOpen = useCallback((platformTabId: string, fallback: boolean): boolean => {
    const stores = platformStores[platformTabId] || [];
    return stores.length === 0 ? fallback : stores.some(s => s.isOpen);
  }, [platformStores]);

  /** Kısmi durumda ipucunda hangi markanın kapalı olduğu yazar. */
  const getClosedStoreNames = useCallback((platformTabId: string): string[] => {
    return (platformStores[platformTabId] || []).filter(s => !s.isOpen).map(s => s.label);
  }, [platformStores]);

  // Seçili sekmenin siparişleri (marka alt sekmesi uygulanmadan)
  const tabOrders = useMemo(() => {
    const signalRQrOrders = qrMenuOrders.map(convertMasaSiparisiToPackageOrder).filter(shouldShowOrder);
    const signalREcommerceOrders = ecommerceOrders.map(convertMasaSiparisiToPackageOrder).filter(shouldShowOrder);
    const signalRKioskOrders = kioskOrders.map(convertMasaSiparisiToPackageOrder).filter(shouldShowOrder);
    const signalRMigrosOrders = migrosOrders.map(convertMigrosOrderToPackageOrder).filter(shouldShowOrder);
    const signalRYsOrders = yemeksepetiOrders.map(convertYemeksepetiOrderToPackageOrder).filter(shouldShowOrder);
    const trendyolConvertedOrders = trendyolOrders.map(convertTrendyolOrderToPackageOrder).filter(shouldShowOrder);
    
    // Paket siparişlerini de filtrele
    const visibleOrders = orders.filter(shouldShowOrder);
    
    if (customerPhoneFilter) {
      const todayOrdersFiltered = visibleOrders.filter(o => o.customerPhone === customerPhoneFilter);
      const pastOrdersFiltered = pastOrders.filter(o => o.customerPhone === customerPhoneFilter);
      
      const allOrders = [...todayOrdersFiltered, ...pastOrdersFiltered].sort((a, b) => {
        const dateCompare = b.orderDate.localeCompare(a.orderDate);
        if (dateCompare !== 0) return dateCompare;
        return b.orderTime.localeCompare(a.orderTime);
      });
      
      return allOrders;
    }
    
    let filteredList = [...visibleOrders];
    
    if (selectedTab === 'all') {
      filteredList = [...filteredList, ...signalRQrOrders, ...signalREcommerceOrders, ...signalRKioskOrders, ...signalRMigrosOrders, ...signalRYsOrders, ...trendyolConvertedOrders];
    } else if (selectedTab === 'qr') {
      filteredList = filteredList.filter(o => o.source === 'qr');
      filteredList = [...filteredList, ...signalRQrOrders];
    } else if (selectedTab === 'ecommerce') {
      filteredList = filteredList.filter(o => o.source === 'ecommerce');
      filteredList = [...filteredList, ...signalREcommerceOrders];
    } else if (selectedTab === 'kiosk') {
      filteredList = filteredList.filter(o => o.source === 'kiosk');
      filteredList = [...filteredList, ...signalRKioskOrders];
    } else if (selectedTab === 'migros') {
      filteredList = filteredList.filter(o => o.source === 'migros');
      filteredList = [...filteredList, ...signalRMigrosOrders];
    } else if (selectedTab === 'yemeksepeti') {
      filteredList = filteredList.filter(o => o.source === 'yemeksepeti');
      filteredList = [...filteredList, ...signalRYsOrders];
    } else if (selectedTab === 'trendyol') {
      filteredList = filteredList.filter(o => o.source === 'trendyol');
      filteredList = [...filteredList, ...trendyolConvertedOrders];
    } else {
      filteredList = filteredList.filter(o => o.source === selectedTab);
    }
    
    if (statusFilter !== 'all') {
      filteredList = filteredList.filter(o => o.status === statusFilter);
    }

    const uniqueOrders = filteredList.filter((order, index, self) => 
      index === self.findIndex(o => o.id === order.id && o.source === order.source)
    );
    
    return uniqueOrders.sort((a, b) => {
      const dateCompare = b.orderDate.localeCompare(a.orderDate);
      if (dateCompare !== 0) return dateCompare;
      return b.orderTime.localeCompare(a.orderTime);
    });
  }, [selectedTab, statusFilter, customerPhoneFilter, orders, pastOrders, qrMenuOrders, ecommerceOrders, kioskOrders, getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders, shouldShowOrder]);

  /**
   * Platform sekmesinin marka alt sekmeleri (ör. Yemeksepeti › Hacıoğlu Lahmacun / Jimmy's Smash Burger).
   * Yalnız o platformda şubeye bağlı birden fazla mağaza varsa çıkar; "Tümü" ve
   * telefon / QR / kiosk sekmelerinde çıkmaz. Sayı: o markanın sekmedeki sipariş sayısı.
   */
  const brandTabs = useMemo<{ label: string; count: number }[]>(() => {
    if (customerPhoneFilter || !ORDER_SOURCE_TO_PLATFORM[selectedTab]) return [];
    const counts = new Map<string, number>();
    (platformStores[selectedTab] || []).forEach(s => {
      const label = (s.label || '').trim();
      if (label) counts.set(label, 0);
    });
    tabOrders.forEach(o => {
      const label = getOrderStoreLabel(o);
      if (label) counts.set(label, (counts.get(label) || 0) + 1);
    });
    if (counts.size < 2) return [];
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
  }, [customerPhoneFilter, selectedTab, platformStores, tabOrders, getOrderStoreLabel]);

  // Ekrandaki liste: seçili marka alt sekmesi varsa yalnız o markanın siparişleri
  const filteredOrders = useMemo(() => {
    if (storeFilter === 'all' || brandTabs.length === 0) return tabOrders;
    return tabOrders.filter(o => getOrderStoreLabel(o) === storeFilter);
  }, [tabOrders, storeFilter, brandTabs, getOrderStoreLabel]);

  // Başka sekmeye geçince marka seçimi "Tümü"ne döner.
  useEffect(() => {
    setStoreFilter('all');
  }, [selectedTab]);

  /**
   * Ekranda görünen marka etiketleri. Mağaza filtresinin seçenekleri buradan
   * gelir; filtre uygulanmışken de tam liste kalsın diye ham sipariş
   * kaynaklarından hesaplanır.
   */
  const visibleStoreLabels = useMemo(() => {
    const labels = new Set<string>();
    const collect = (list: PackageOrder[]) => list.forEach(o => {
      const label = getOrderStoreLabel(o);
      if (label) labels.add(label);
    });
    collect(orders);
    collect(getirOrders.map(convertGetirOrderToPackageOrder));
    collect(migrosOrders.map(convertMigrosOrderToPackageOrder));
    collect(yemeksepetiOrders.map(convertYemeksepetiOrderToPackageOrder));
    collect(trendyolOrders.map(convertTrendyolOrderToPackageOrder));
    foodIntegrations.filter(p => p.isEnabled && p.storeName).forEach(p => labels.add(String(p.storeName).trim()));
    return Array.from(labels).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [orders, getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders, foodIntegrations, getOrderStoreLabel]);

  /** Tek mağazalı kurulumda tablo bugünkü haliyle kalsın diye koşullu. */
  const showStoreColumn = hasMultiStoreSetup || visibleStoreLabels.length > 1;

  // Seçili marka silinir/kapatılırsa seçim boşta kalmasın.
  useEffect(() => {
    if (storeFilter !== 'all' && !brandTabs.some(b => b.label === storeFilter)) {
      setStoreFilter('all');
    }
  }, [storeFilter, brandTabs]);

  const handleSelectOrder = useCallback((orderId: number | null, source?: string) => {
    console.log('🖱️ [SELECT_ORDER] Siparişe tıklandı:', { orderId, source });
    setSelectedOrderId(orderId);
    setSelectedOrderSource(source || null);
  }, []);

  // Seçili sipariş
  const selectedOrder = useMemo(() => {
    if (!selectedOrderId) return null;

    let result: PackageOrder | null = null;
    let matchMethod = '';

    // Source bilgisi varsa hem id hem source ile eşleştir
    if (selectedOrderSource) {
      const exact = filteredOrders.find(o => o.id === selectedOrderId && o.source === selectedOrderSource);
      if (exact) {
        result = exact;
        matchMethod = 'exact (id+source)';
      }
    }

    if (!result) {
      const fromFiltered = filteredOrders.find(o => o.id === selectedOrderId);
      if (fromFiltered) {
        result = fromFiltered;
        matchMethod = 'fallback (id only from filtered)';
      }
    }
    
    if (!result) {
      const fromOrders = orders.find(o => o.id === selectedOrderId);
      const fromPast = pastOrders.find(o => o.id === selectedOrderId);
      result = fromOrders || fromPast || null;
      matchMethod = fromOrders ? 'orders array' : fromPast ? 'pastOrders array' : 'NOT FOUND';
    }

    console.log('🔍 [SELECT_ORDER] selectedOrder çözümlendi:', {
      selectedOrderId,
      selectedOrderSource,
      matchMethod,
      resultId: result?.id,
      resultSource: result?.source,
      resultStatus: result?.status,
      resultIsPaid: result?.isPaid,
      resultIsOnlinePayment: result?.isOnlinePayment,
      resultPaymentType: result?.paymentType,
      filteredOrdersCount: filteredOrders.length,
      sameIdOrders: filteredOrders.filter(o => o.id === selectedOrderId).map(o => ({ id: o.id, source: o.source })),
    });

    return result;
  }, [selectedOrderId, selectedOrderSource, orders, pastOrders, filteredOrders]);

  // ============================================================================
  // ✅ YENİ: Müşterinin Aktif Siparişleri (Kuryeye verilmemiş)
  // ============================================================================
  
  // Arayan veya seçilen müşterinin telefon numarası
  // ✅ Manuel seçilen müşteri için de aktif sipariş kontrolünün çalışması için
  // currentCustomer.phone fallback eklendi (önceden sadece arama/filter telefonuna bakıyordu).
  const activeCustomerPhone = useMemo(() => {
    return incomingCall?.phone || currentCustomer?.phone || customerPhoneFilter || null;
  }, [incomingCall, currentCustomer, customerPhoneFilter]);

  // Müşterinin henüz kuryeye verilmemiş (aktif) siparişleri
  const customerActiveOrders = useMemo(() => {
    if (!activeCustomerPhone) return [];

    // Aktif durumlar: new, accepted, preparing, ready (kuryeye verilmemiş)
    const activeStatuses: OrderStatus[] = ['new', 'accepted', 'preparing', 'ready'];

    return orders.filter(order =>
      order.customerPhone === activeCustomerPhone &&
      activeStatuses.includes(order.status)
    );
  }, [activeCustomerPhone, orders]);

  // Seçili adresin string değerini bul
  const selectedAddressString = useMemo(() => {
    if (!selectedAddressId || !currentCustomer) return null;
    const addr = currentCustomer.addresses.find(a => a.id === selectedAddressId);
    return addr?.address || null;
  }, [selectedAddressId, currentCustomer]);

  // Aktif sipariş bilgisi: Aynı adrese mi yoksa farklı adrese mi?
  // ✅ Kullanıcı kararı (güncellendi): Aynı müşteri + aynı adres için, mevcut
  // sipariş kuryeye verilmemiş olsa bile YENİ SİPARİŞ AÇILABİLİR. Eski kural
  // ("aynı adrese ikinci sipariş açılamaz, sadece ilave yapılabilir") mutfak
  // akışını kilitliyordu: müşteri tam teslim öncesi tekrar arayıp uzun sürecek
  // bir sipariş verdiğinde, hazır bekleyen önceki sipariş ilaveye bağlanınca
  // yeni ürünlerin hazırlanmasını beklemek zorunda kalıyordu. Artık kasiyer
  // duruma göre seçer: Yeni Sipariş (bağımsız fiş/kurye) veya İlave Sipariş.
  // isSameAddress bilgisi buton üzerinde bilgilendirme amaçlı gösterilmeye
  // devam eder. String karşılaştırma format farklarına (boşluk/case/noktalama)
  // toleranslı olacak şekilde normalize ediliyor.
  const activeOrderInfo = useMemo(() => {
    const hasActiveOrder = customerActiveOrders.length > 0;

    if (!hasActiveOrder) {
      return {
        hasActiveOrder: false,
        isSameAddress: false,
        activeOrderForAddress: null as PackageOrder | null,
        canCreateNewOrder: true,
        canAddToOrder: false,
      };
    }

    // Adres karşılaştırması için normalize: trim + lowercase + tek boşluk + noktalama at
    const normalize = (s: string | null | undefined): string =>
      (s || '')
        .trim()
        .toLocaleLowerCase('tr-TR')
        .replace(/\s+/g, ' ')
        .replace(/[.,;:!?]/g, '');

    const normalizedSelected = normalize(selectedAddressString);

    // Seçili adresle eşleşen aktif sipariş var mı? (normalize edilmiş karşılaştırma)
    const activeOrderForAddress = normalizedSelected
      ? customerActiveOrders.find(order => normalize(order.customerAddress) === normalizedSelected)
      : null;

    const isSameAddress = !!activeOrderForAddress;

    return {
      hasActiveOrder: true,
      isSameAddress,
      activeOrderForAddress: activeOrderForAddress || null,
      // Yeni Sipariş HER ZAMAN açılabilir (kullanıcı kararı) — aynı adrese
      // kuryeye verilmemiş aktif sipariş olsa bile bağımsız yeni sipariş
      // oluşturulur. İlave seçeneği aynı adres durumunda ek olarak sunulur.
      canCreateNewOrder: true,
      canAddToOrder: isSameAddress,
    };
  }, [customerActiveOrders, selectedAddressString]);

  const getStatusButtonInfo = useMemo(() => computeStatusButtonInfo(selectedOrder, getirCountdownMap), [selectedOrder, getirCountdownMap]);

  // Footer istatistikleri.
  //
  // ⚠ Tarihsel hata düzeltmesi:
  //   - Önceki implementasyon `orders.filter(o => o.source === 'yemeksepeti'/...)`
  //     üzerinden sayım yapıyordu; ancak `loadPackageOrders` içinde TÜM siparişlere
  //     sabit olarak `source: 'phone'` atanıyor (PackageOrders tablosu yalnızca
  //     telefon siparişlerini tutar). Bu nedenle YS/Trendyol/Getir/Migros için
  //     `orders.filter(...)` ifadeleri her zaman 0 döndürüyordu.
  //   - Doğrudan `xxxOrders.length` kullanmak da yanlıştı: bu listeler gece
  //     modunda dünün siparişlerini de içerebiliyor; iş günü filtresi atlanıyordu.
  //   - `total` hesabında `getirOrders` ve `migrosOrders` toplama eklenmemişti.
  //   - useMemo dependency array'inde `getirOrders/migrosOrders/yemeksepetiOrders`
  //     yoktu; SignalR ile yeni sipariş geldiğinde footer YENİDEN HESAPLANMIYORDU.
  //
  // ✅ Yeni mantık: `orderTabs` içindeki `counts.all` ile birebir aynı şekilde
  //   her platform için `convertXxxOrderToPackageOrder` + `isBusinessDayDate`
  //   filtresi uygulanır. Footer her zaman tüm statüleri sayar (statusFilter'dan
  //   bağımsız), tab sayılarıyla `statusFilter === 'all'` durumunda eşleşir.
  const footerStats = useMemo(() => {
    const phoneVisible = orders.filter(o => o.source === 'phone' && isInActiveDayWindow(o.orderDate));
    const ysVisible = yemeksepetiOrders.map(convertYemeksepetiOrderToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));
    const trendyolVisible = trendyolOrders.map(convertTrendyolOrderToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));
    const getirVisible = getirOrders.map(convertGetirOrderToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));
    const migrosVisible = migrosOrders.map(convertMigrosOrderToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));
    const kioskVisible = kioskOrders.map(convertMasaSiparisiToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));
    const qrVisible = qrMenuOrders.map(convertMasaSiparisiToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));
    const ecommerceVisible = ecommerceOrders.map(convertMasaSiparisiToPackageOrder).filter(o => isInActiveDayWindow(o.orderDate));

    const phone = phoneVisible.length;
    const yemeksepeti = ysVisible.length;
    const trendyol = trendyolVisible.length;
    const getir = 0;
    const migros = migrosVisible.length;
    const kiosk = kioskVisible.length;
    const qr = qrVisible.length;
    const ecommerce = ecommerceVisible.length;

    return {
      phone,
      yemeksepeti,
      trendyol,
      getir,
      migros,
      kiosk,
      qr,
      ecommerce,
      total: phone + yemeksepeti + trendyol + migros + kiosk + qr + ecommerce,
    };
  }, [orders, qrMenuOrders, ecommerceOrders, kioskOrders, getirOrders, migrosOrders, yemeksepetiOrders, trendyolOrders, isInActiveDayWindow]);

  // Ekran verilerini yükle
  useEffect(() => {
    const loadScreenData = async () => {
      if (!screenId || !companyId) {
        setError('Ekran ID veya şirket bilgisi bulunamadı');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const t0 = performance.now();
        
        const screens = await salesService.getQuickSaleScreens();
        const screen = screens.find((s: any) => s.Id === parseInt(screenId) && s.ScreenType === 'Package');
        console.log(`⏱️ [PackageScreen] Ekran listesi: ${Math.round(performance.now() - t0)}ms`);
        
        if (screen) {
          // Detay, entegrasyon ayarlari ve platform bilgilerini PARALEL yukle
          const [detailedScreen, packageScreenData, piResult] = await Promise.all([
            salesService.getQuickSaleScreenById(screen.Id),
            screen.PackageScreenId
              ? salesService.getPackageScreenById(screen.PackageScreenId).catch((e: any) => { console.error('🔌 Entegrasyon ayarları hatası:', e); return null; })
              : Promise.resolve(null),
            screen.BranchId
              ? mainApi.get(`/api/platformintegration/by-branch?companyId=${encodeURIComponent(companyId)}&branchId=${encodeURIComponent(screen.BranchId)}`).then(r => r.data).catch((e: any) => { console.error('🔌 PlatformIntegrations hatası:', e); return null; })
              : Promise.resolve(null),
          ]);
          console.log(`⏱️ [PackageScreen] Paralel yükleme (detay+entegrasyon+platform): ${Math.round(performance.now() - t0)}ms`);
          
          // PrintingSettingsJson parse
          let printingSettings = null;
          if (detailedScreen.PrintingSettingsJson) {
            try {
              const parsed = JSON.parse(detailedScreen.PrintingSettingsJson);
              printingSettings = {
                printingMode: parsed.printingMode || 'single',
                defaultReceiptPrinter: parsed.defaultReceiptPrinter || null,
                definedPrinters: parsed.definedPrinters || [],
                receiptDisplaySettings: parsed.receiptDisplaySettings || undefined,
                categoryPrinterAssignments: parsed.categoryPrinterAssignments || {},
                adisyonPrinter: parsed.adisyonPrinter || null,
              };
            } catch (e) {
              console.error('🖨️ [PackageScreen] PrintingSettingsJson parse hatası:', e);
            }
          }
          
          // PaymentOptionsJson parse
          let paymentSettings = null;
          if (detailedScreen.PaymentOptionsJson) {
            try {
              const paymentOptionsArray = JSON.parse(detailedScreen.PaymentOptionsJson);
              
              let cashPaymentCashboxId: string | null = null;
              let cashPaymentCashboxName: string | null = null;
              const posOptions: Array<{
                id: string;
                label: string;
                bankAccountId: string;
                subAccountId: string | null;
                bankAccountName: string | null;
              }> = [];
              const otherOptions: Array<{
                id: string;
                label: string;
                cashboxId: string;
                cashboxName: string | null;
              }> = [];
              
              for (const opt of paymentOptionsArray) {
                if (opt.enabled) {
                  if (opt.type === 'cash' && !cashPaymentCashboxId) {
                    cashPaymentCashboxId = opt.cashboxId || null;
                    cashPaymentCashboxName = opt.cashboxName || null;
                  } else if (opt.type === 'pos' && opt.bankAccountId) {
                    posOptions.push({
                      id: opt.id,
                      label: opt.label || 'POS',
                      bankAccountId: opt.bankAccountId,
                      subAccountId: opt.subAccountId || null,
                      bankAccountName: opt.bankAccountName || null,
                    });
                  } else if (opt.type === 'other' && opt.cashboxId && opt.label) {
                    otherOptions.push({
                      id: opt.id,
                      label: opt.label,
                      cashboxId: opt.cashboxId,
                      cashboxName: opt.cashboxName || null,
                    });
                  }
                }
              }
              
              paymentSettings = {
                cashPaymentCashboxId,
                cashPaymentCashboxName,
                posOptions,
                otherOptions,
              };
            } catch (e) {
              console.error('💳 [PackageScreen] PaymentOptionsJson parse hatası:', e);
            }
          }
          
          // StockVisibilityJson parse
          let defaultWarehouseId: string | null = null;
          let screenCategoryIds: string[] = [];
          // QuickSale ekranının ürün-kategori atamalarını taşıyan harita.
          // Anahtar: productId, Değer: bu ekranda atandığı **QuickSale kategorisi** (Guid string).
          // Telefon paket siparişlerinde kategori-bazlı yazdırmada
          // **ürünün stok kartındaki orijinal CategoryId'sini değil** bu haritayı
          // kullanıyoruz; çünkü kullanıcı yazıcıları "ekrana özel" kategorilere
          // atıyor — örnek: aynı ürün başka QuickSale ekranlarında farklı
          // kategoriler altında olabilir.
          const productScreenCategoryMap: Record<string, string> = {};
          let parsedStockVisibility: any = null;

          if (detailedScreen.StockVisibilityJson) {
            try {
              const stockVisibility = JSON.parse(detailedScreen.StockVisibilityJson);
              parsedStockVisibility = stockVisibility;
              defaultWarehouseId = stockVisibility.defaultWarehouseId || null;
              if (!defaultWarehouseId && stockVisibility.warehouses?.length > 0) {
                defaultWarehouseId = stockVisibility.warehouses[0];
              }
              // Ekrana tanımlı kategori ID'lerini çıkart
              const categoriesMap = stockVisibility.categories || {};
              Object.values(categoriesMap).forEach((cats: any) => {
                if (Array.isArray(cats)) {
                  cats.forEach((cid: string) => {
                    if (!screenCategoryIds.includes(cid)) screenCategoryIds.push(cid);
                  });
                }
              });

              // Ürün-kategori haritasını oluştur. Yapı:
              //   products[warehouseId][categoryId] = [productId, productId, ...]
              // Aynı ürün birden fazla kategoride göründüğünde ilk eşleşmeyi
              // tutuyoruz (useQuickSaleProducts.ts ile aynı davranış).
              const productsMap = stockVisibility.products || {};
              Object.values(productsMap).forEach((perWarehouse: any) => {
                if (!perWarehouse || typeof perWarehouse !== 'object') return;
                Object.entries(perWarehouse).forEach(([categoryId, productIds]) => {
                  if (!Array.isArray(productIds)) return;
                  productIds.forEach((pid: string) => {
                    if (typeof pid === 'string' && !productScreenCategoryMap[pid]) {
                      productScreenCategoryMap[pid] = categoryId;
                    }
                  });
                });
              });
            } catch (e) {
              console.error('📦 [PackageScreen] StockVisibilityJson parse hatası:', e);
            }
          }

          // Ekrana tanımlı kategorileri yükle
          if (screenCategoryIds.length > 0) {
            try {
              const { default: accountingService } = await import('../../../services/accountingService');
              const cats = await accountingService.getCategories(false);
              const allMapped = cats.map((cat: any) => ({
                id: cat.id?.toString() || cat.Id?.toString() || '',
                name: cat.name || cat.Name || 'İsimsiz Kategori',
              })).filter((c: any) => c.id);
              const filtered = allMapped.filter((c: any) => screenCategoryIds.includes(c.id));
              console.log(`[PackageOrder] Kategori debug:`, {
                screenCategoryIds,
                allCategoryCount: allMapped.length,
                sampleAllIds: allMapped.slice(0, 5).map((c: any) => c.id),
                filteredCount: filtered.length,
              });
              setAvailableCategories(filtered.length > 0 ? filtered : allMapped);
            } catch (err) {
              console.error('[PackageOrder] Kategoriler yüklenemedi:', err);
            }
          } else {
            // screenCategoryIds boşsa tüm kategorileri yükle (fallback)
            try {
              const { default: accountingService } = await import('../../../services/accountingService');
              const cats = await accountingService.getCategories(false);
              const list = cats.map((cat: any) => ({
                id: cat.id?.toString() || cat.Id?.toString() || '',
                name: cat.name || cat.Name || 'İsimsiz Kategori',
              })).filter((c: any) => c.id);
              setAvailableCategories(list);
              console.log(`[PackageOrder] Tüm kategoriler yüklendi (StockVisibility boş): ${list.length}`);
            } catch (err) {
              console.error('[PackageOrder] Kategoriler yüklenemedi:', err);
            }
          }
          
          // Entegrasyon ayarlari
          let integrationSettings = {
            showPhoneOrders: true,
            showYemekSepetiOrders: true,
            showTrendyolOrders: true,
            showGetirOrders: false,
            showMigrosOrders: true,
            showKioskOrders: true,
            showQrMenuOrders: true,
            showEcommerceOrders: true,
          };
          
          if (packageScreenData) {
            integrationSettings = {
              showPhoneOrders: packageScreenData.ShowPhoneOrders ?? true,
              showYemekSepetiOrders: packageScreenData.ShowYemekSepetiOrders ?? true,
              showTrendyolOrders: packageScreenData.ShowTrendyolOrders ?? true,
              showGetirOrders: false,
              showMigrosOrders: packageScreenData.ShowMigrosOrders ?? true,
              showKioskOrders: packageScreenData.ShowKioskOrders ?? true,
              showQrMenuOrders: packageScreenData.ShowQrMenuOrders ?? true,
              showEcommerceOrders: packageScreenData.ShowEcommerceOrders ?? true,
            };
          }
          
          // Platform entegrasyonlari
          let platformIntegrations: PlatformIntegration[] = [];

          if (piResult?.success && piResult?.data) {
            platformIntegrations = piResult.data.map((pi: any) => ({
              id: pi.id || pi.Id,
              platform: pi.platform || pi.Platform,
              category: pi.category || pi.Category,
              isEnabled: pi.isEnabled ?? pi.IsEnabled ?? true,
              branchId: pi.branchId || pi.BranchId || null,
              // Çoklu mağaza: marka adı ve markaya özel fiş başlığı. Bu iki alan
              // eşlemede yoksa mağaza sütunu ve marka fişi sessizce boş çalışır.
              storeName: pi.storeName ?? pi.StoreName ?? null,
              receiptSettingsJson: pi.receiptSettingsJson ?? pi.ReceiptSettingsJson ?? null,
              restaurantId: pi.restaurantId || pi.RestaurantId,
              sellerId: pi.sellerId || pi.SellerId || null,
              apiKey: pi.apiKey || pi.ApiKey || null,
              apiSecret: pi.apiSecret || pi.ApiSecret || null,
              isConnected: pi.isConnected ?? pi.IsConnected ?? false,
              lastSyncAt: pi.lastSyncAt || pi.LastSyncAt,
              errorCount: pi.errorCount ?? pi.ErrorCount ?? 0,
              lastErrorMessage: pi.lastErrorMessage || pi.LastErrorMessage,
            }));
            console.log(`🔌 [PackageScreen] ${platformIntegrations.length} platform entegrasyonu yüklendi:`,
              platformIntegrations.map(p => `${p.platform}(${p.isEnabled ? 'aktif' : 'pasif'})`).join(', '));
          }
          
          setScreenData({
            id: screen.Id,
            name: screen.Name,
            branchId: screen.BranchId || null,
            packageScreenId: screen.PackageScreenId || null,
            localServiceUrl: detailedScreen.LocalServiceUrl || null,
            printingSettings: printingSettings,
            useCashierSystem: detailedScreen.UseCashierSystem || false,
            paymentSettings: paymentSettings,
            defaultWarehouseId: defaultWarehouseId,
            integrationSettings: integrationSettings,
            platformIntegrations: platformIntegrations,
            autoAcceptEnabled: packageScreenData?.AutoAcceptEnabled ?? false,
            autoAcceptDelaySeconds: packageScreenData?.AutoAcceptDelaySeconds ?? 15,
            autoPrintKitchen: packageScreenData?.AutoPrintKitchen ?? false,
            autoPrintReceiptOnAccept: packageScreenData?.AutoPrintReceiptOnAccept ?? false,
            // QuickSale ekranı bazlı yazdırma için (telefon paket siparişlerinde
            // ürün → kategori override haritası kullanılır; SettingsModal kategori
            // listesi de bu ekrana atanmış kategorilere filtrelenir).
            stockVisibility: parsedStockVisibility,
            screenCategoryIds,
            productScreenCategoryMap,
          });
          console.log(`⏱️ [PackageScreen] Ekran hazır: ${Math.round(performance.now() - t0)}ms - QuickSaleScreenId:`, screen.Id, 'PackageScreenId:', screen.PackageScreenId);
        } else {
          setError('Paket ekranı bulunamadı');
        }
      } catch (err) {
        console.error('Ekran verileri yüklenirken hata:', err);
        setError('Ekran verileri yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    loadScreenData();
  }, [screenId, companyId, screenDataRefreshKey]);

  // ✅ PR2 (Invoice Seller Snapshot Refactor): screenData yüklendikten sonra seller snapshot çek.
  // PackageScreens varsa "Package" tipinde, yoksa fallback olarak QuickSaleScreens.Id kullan.
  useEffect(() => {
    const pkgScreenId = screenData?.packageScreenId;
    const qsScreenId = screenData?.id;
    const screenIdNum = pkgScreenId || qsScreenId;
    const screenType = pkgScreenId ? 'Package' : 'QuickSale';
    if (!screenIdNum || screenIdNum <= 0) {
      setSeller(null);
      return;
    }
    let cancelled = false;
    getSellerByScreen(screenIdNum, screenType as 'Package' | 'QuickSale')
      .then((snapshot) => {
        if (cancelled) return;
        setSeller(snapshot);
        console.log(
          '[PackageOrderScreen] 🏪 Seller snapshot:',
          `${snapshot.sellerCompanyName} (${snapshot.source}, franchise=${snapshot.isFranchiseInvoice})`,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[PackageOrderScreen] Seller fetch hatası:', err);
        setSeller(null);
      });
    return () => {
      cancelled = true;
    };
  }, [screenData?.packageScreenId, screenData?.id]);

  // Platform kategori eşleştirmelerini yükle (ekran verisi hazır olduğunda)
  useEffect(() => {
    if (!screenData?.packageScreenId && !screenData?.id) return;
    const pkgScreenId = screenData.packageScreenId || screenData.id;
    
    const loadMappings = async () => {
      try {
        const mappings = await platformCategoryMappingService.getByScreen(pkgScreenId);
        setPlatformCategoryMappings(mappings);
        console.log('[PackageOrder] Platform kategori eşleştirmeleri yüklendi:', mappings.length);
      } catch (err) {
        console.error('[PackageOrder] Kategori eşleştirmeleri yüklenemedi:', err);
      }
    };
    loadMappings();
  }, [screenData?.packageScreenId, screenData?.id]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // MÜŞTERİ EKRANI (CUSTOMER DISPLAY) - İkinci ekranda reklam gösterimi
  // ═══════════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const screenIdNum = screenData?.id;
    if (!screenIdNum || !companyId) return;

    // @ts-ignore
    const electronAPI = window.electronAPI;
    const isElectron = !!electronAPI?.customerDisplay;

    if (!isElectron) {
      // @ts-ignore
      const existingWindow = window.__customerDisplayWindow as Window | null;
      if (existingWindow && !existingWindow.closed) return;
    }

    let isMounted = true;

    const checkAndOpenCustomerDisplay = async () => {
      if (!isMounted) return;

      try {
        let isEnabled = false;
        try {
          const response = await getCustomerDisplaySettings(screenIdNum);
          if (!isMounted) return;
          isEnabled = response.Success && response.Data?.IsEnabled === true;
        } catch {
          return;
        }

        if (!isEnabled || !isMounted) return;

        localStorage.setItem('__customerDisplayCompanyId', companyId);

        try {
          const idToken = await ownAuthSession.getAccessToken();
          if (idToken) {
            localStorage.setItem('__customerDisplayAuthToken', idToken);
          }
        } catch {}

        const displayUrl = `/#/customer-display/${screenIdNum}`;

        if (isElectron) {
          try {
            const statusRes = await electronAPI.customerDisplay.isOpen();
            if (statusRes?.isOpen) return;
          } catch {}

          if (!isMounted) return;

          const baseUrl = window.location.origin || 'http://localhost:3003';
          const fullUrl = `${baseUrl}${displayUrl}`;

          try {
            await electronAPI.customerDisplay.open(fullUrl);
          } catch (err) {
            console.error('[PackageScreen] Electron müşteri ekranı hatası:', err);
          }
          return;
        }

        let targetLeft = window.screen.width;
        let targetTop = 0;
        let targetWidth = window.screen.width;
        let targetHeight = window.screen.height;

        try {
          if ('getScreenDetails' in window) {
            // @ts-ignore
            const screenDetails = await (window as any).getScreenDetails();
            if (!isMounted) return;
            const screens = screenDetails?.screens;
            if (screens && screens.length > 0) {
              const secondaryScreen = screens.find((s: any) => !s.isPrimary);
              if (secondaryScreen) {
                targetLeft = secondaryScreen.left || targetLeft;
                targetTop = secondaryScreen.top || 0;
                targetWidth = secondaryScreen.width || targetWidth;
                targetHeight = secondaryScreen.height || targetHeight;
              }
            }
          }
        } catch {}

        if (!isMounted) return;

        const windowFeatures = [
          `width=${targetWidth}`,
          `height=${targetHeight}`,
          `left=${targetLeft}`,
          `top=${targetTop}`,
          'menubar=no',
          'toolbar=no',
          'location=no',
          'status=no',
          'resizable=yes',
          'scrollbars=no',
        ].join(',');

        const newWindow = window.open(displayUrl, 'CustomerDisplay', windowFeatures);

        if (newWindow && isMounted) {
          // @ts-ignore
          window.__customerDisplayWindow = newWindow;
          setTimeout(() => {
            try {
              if (newWindow && !newWindow.closed) {
                newWindow.moveTo(targetLeft, targetTop);
                newWindow.resizeTo(targetWidth, targetHeight);
              }
            } catch {}
          }, 500);
        }
      } catch (err) {
        console.error('[PackageScreen] Müşteri ekranı açılırken hata:', err);
      }
    };

    const timer = setTimeout(checkAndOpenCustomerDisplay, 2000);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [screenData?.id, companyId]);

  // Paket ekranından ayrılırken müşteri ekranını kapat
  useEffect(() => {
    return () => {
      // @ts-ignore
      const electronAPI = window.electronAPI;
      if (electronAPI?.customerDisplay) {
        electronAPI.customerDisplay.close();
      } else {
        // @ts-ignore
        const customerWindow = window.__customerDisplayWindow as Window | null;
        if (customerWindow && !customerWindow.closed) {
          try { customerWindow.close(); } catch {}
          // @ts-ignore
          window.__customerDisplayWindow = null;
        }
      }
    };
  }, []);

  // Migros siparis tazeleme - 60sn'de bir YEREL DB'den yenile (Migros API'ye polling YOK).
  // Yeni siparisler ve durum/teslimat degisiklikleri webhook + SignalR ile anlik gelir;
  // bu interval yalnizca kendi DB'mizi okuyan bir emniyet agidir.
  // NOT: Migros talebi geregi /Order/PendingOrdersWithStores polling'i kaldirildi.
  useEffect(() => {
    if (!companyId || !screenData?.integrationSettings?.showMigrosOrders) return;
    // Şubede birden fazla Migros mağazası olabilir; tazeleme yerel DB'den
    // şube bazlı yapıldığı için tek bir aktif kayıt olması yeterli.
    const hasMigrosIntegration = screenData.platformIntegrations?.some(
      p => p.platform === 'migros_yemek' && p.isEnabled
    );
    if (!hasMigrosIntegration) return;

    fetchMigrosOrders();

    const refreshInterval = setInterval(async () => {
      try {
        await fetchMigrosOrders();
      } catch (err) {
        console.error('[Migros] Yerel siparis tazeleme hatasi:', err);
      }
    }, 60000);

    return () => clearInterval(refreshInterval);
  }, [companyId, screenData?.integrationSettings?.showMigrosOrders, screenData?.platformIntegrations, fetchMigrosOrders]);

  // Migros restoran durum kontrolu - 5dk'da bir, her magaza icin ayri
  useEffect(() => {
    if (!companyId || !screenData?.integrationSettings?.showMigrosOrders) return;
    const migrosIntegrations = (screenData.platformIntegrations || []).filter(
      p => p.platform === 'migros_yemek' && p.isEnabled
    );
    if (migrosIntegrations.length === 0) return;

    const checkAll = () => migrosIntegrations.forEach(i => checkMigrosRestaurantStatus(i.id));
    checkAll();

    const statusInterval = setInterval(checkAll, 300000);

    return () => clearInterval(statusInterval);
  }, [companyId, screenData?.integrationSettings?.showMigrosOrders, screenData?.platformIntegrations, checkMigrosRestaurantStatus]);

  // Yemeksepeti vendor durumu ve siparis kontrolu
  useEffect(() => {
    if (!companyId || !screenData?.integrationSettings?.showYemekSepetiOrders) return;
    const ysIntegrations = (screenData.platformIntegrations || []).filter(
      p => p.platform === 'yemeksepeti' && p.isEnabled
    );
    if (ysIntegrations.length === 0) return;

    const checkAll = () => ysIntegrations.forEach(i => checkYemeksepetiVendorStatus(i.id));
    checkAll();
    fetchYemeksepetiOrders();

    const statusInterval = setInterval(checkAll, 300000);

    return () => clearInterval(statusInterval);
  }, [companyId, screenData?.integrationSettings?.showYemekSepetiOrders, screenData?.platformIntegrations, checkYemeksepetiVendorStatus, fetchYemeksepetiOrders]);

  // Kasiyer sistemi aktifse ekran acildiginda kasiyer giris modalini ac
  useEffect(() => {
    if (screenData?.useCashierSystem && !currentCashier && !loading) {
      console.log('🔐 [PackageOrderScreen] Kasiyer sistemi aktif, kasiyer girişi gerekli');
      setIsCashierLoginModalOpen(true);
    }
  }, [screenData?.useCashierSystem, currentCashier, loading]);

  // ✅ YENİ: Şirket adını Firebase'den yükle ve cache'le (Fatura numarası için)
  useEffect(() => {
    async function loadCompanyName() {
      if (!companyId) {
        setCompanyNameCache(null);
        return;
      }

      try {
        const companyInfo = await getCompanyInfo(companyId);
        if (companyInfo?.companyName) {
          setCompanyNameCache(companyInfo.companyName);
          console.log('🏢 [PackageOrder] Şirket adı cache\'lendi:', companyInfo.companyName);
        } else {
          setCompanyNameCache(null);
          console.warn('⚠️ [PackageOrder] companyName alanı bulunamadı');
        }
      } catch (err) {
        console.error('❌ [PackageOrder] Şirket adı alınırken hata:', err);
        setCompanyNameCache(null);
      }
    }

    loadCompanyName();
  }, [companyId]);

  // ============================================================================
  // CALLER ID - Phone Handlers (extracted to usePhoneHandlers hook)
  // ============================================================================
  const { handleManualPhoneSearch, handleSelectRecentCall, handleAddressAdded } = usePhoneHandlers({
    manualPhoneNumber,
    incomingCall,
    currentCustomer,
    setSnackbar,
    setIsSearchingPhone,
    setIsManualPhoneModalOpen,
    setCustomerPhoneFilter,
    setCustomerNameFilter,
    setCurrentCustomer,
    setSelectedAddressId,
    setIsCallAnswered,
    setNewCustomerPhone,
    setAddCustomerModalOpen,
    searchCustomerByPhone,
  });
  
  // ✅ loadTodayCalls useEffect artık usePhoneCalls hook'unda
  
  // ✅ YENİ: Paket siparişlerini API'den yükle
  const loadPackageOrders = useCallback(async () => {
    if (!companyId) return;
    
    const t0 = performance.now();
    try {
      const { isExtendedDay, yesterday } = getBusinessDayInfo();
      console.log('📦 [PackageOrder] Siparişler yükleniyor...', { companyId, branchId: screenData?.branchId, isExtendedDay });
      
      const fetchPackageOrders = () => {
        if (isExtendedDay && yesterday) {
          const startDate = new Date(yesterday + 'T00:00:00');
          return getPackageOrdersByDateRange(startDate, new Date(), screenData?.branchId || undefined);
        }
        return getTodayPackageOrders(screenData?.branchId || undefined);
      };
      
      const [packageResponse] = await Promise.all([
        fetchPackageOrders(),
        fetchMasaSiparisleri().catch(e => console.warn('⚠️ [PackageOrder] MasaSiparisi hatası:', e)),
        Promise.resolve(),
      ]);

      console.log(`⏱️ [PackageOrder] Paralel yükleme: ${Math.round(performance.now() - t0)}ms`);
      
      if (packageResponse?.Success && packageResponse?.Data) {
        const mappedOrders: PackageOrder[] = packageResponse.Data.map((order: PackageOrderDto) => {
          const status = mapOrderStatus(order.OrderStatus || 'PENDING');
          const createdAt = new Date(order.CreatedAt);
          
          let waitingMinutes = order.WaitingMinutes || 0;
          if (status === 'delivered' || status === 'cancelled' || status === 'undelivered') {
            if (order.CompletedAt) {
              const completedAt = new Date(order.CompletedAt);
              waitingMinutes = Math.floor((completedAt.getTime() - createdAt.getTime()) / 60000);
            }
          }
          
          return {
          id: order.Id,
          shortOrderNo: order.ShortCode || '',
          externalOrderNo: order.OrderNumber || undefined,
          source: 'phone' as OrderSource,
          customerName: order.AccountName || '',
          customerPhone: order.CustomerPhone || '',
          customerAddress: order.CustomerAddress || '',
            orderDate: createdAt.toISOString().split('T')[0],
            orderTime: createdAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }),
          totalAmount: order.TotalAmount || 0,
            status: status,
          itemCount: order.ItemCount || order.Items?.length || 0,
            waitingMinutes: waitingMinutes,
          paymentType: mapPaymentType(order.PaymentMethod),
          courierName: order.CourierName || undefined,
          courierStatus: order.CourierStatus || undefined,
          isPaid: order.PaymentStatus === 'PAID',
          totalPaid: order.TotalPaid || 0,
          paymentMethod: order.PaymentMethod || undefined,
            createdAt: order.CreatedAt,
            completedAt: order.CompletedAt || undefined,
            invoiceId: order.InvoiceId || null,
            invoiceNumber: order.InvoiceNumber || null,
            referenceNumber: order.ReferenceNumber || null,
          };
        });
        
        console.log(`✅ [PackageOrder] Siparişler yüklendi: ${mappedOrders.length} (${Math.round(performance.now() - t0)}ms)`, 
          mappedOrders.map(o => ({ id: o.id, shortOrderNo: o.shortOrderNo, itemCount: o.itemCount, totalAmount: o.totalAmount, status: o.status }))
        );
        // Detaylı item bilgisi için raw data log
        packageResponse.Data.forEach((order: PackageOrderDto) => {
          console.log(`📋 [PackageOrder] Sipariş #${order.Id} detay:`, {
            itemCount: order.ItemCount,
            itemsLength: order.Items?.length,
            totalAmount: order.TotalAmount,
            items: order.Items?.map((i: any) => ({ id: i.Id, name: i.ProductName, qty: i.Quantity, total: i.TotalAmount }))
          });
        });
        setLiveOrders(mappedOrders);
      } else {
        console.warn('⚠️ [PackageOrder] Sipariş yüklenemedi:', packageResponse?.Message);
      }
      
    } catch (error) {
      console.error('❌ [PackageOrder] Sipariş yükleme hatası:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, screenData?.branchId]);
  
  // ✅ mapOrderStatus ve mapPaymentType artık hooks/useOrders.ts'den import ediliyor

  // Tarih filtresi az once kapatildi mi? Eger oyleyse canli state'leri ZORLA
  // yeniden cek — historic mod aktifken SignalR push'lari ile diger sube
  // siparisleri canli state'e sizmis olabilir (savunma katmani).
  // selectedHistoricDate'i deps'e koymuyoruz; justClearedHistoricRef flag'i
  // yukaridaki effect'te set ediliyor ve burada tuketiliyor.
  useEffect(() => {
    if (!selectedHistoricDate && justClearedHistoricRef.current) {
      justClearedHistoricRef.current = false;
      console.log('[HistoricFilter] Filtre kapatildi, canli state yeniden yukleniyor (anti-leak)...');
      loadPackageOrders().catch(() => {});
      fetchMasaSiparisleri().catch(() => {});
      fetchGetirOrders().catch(() => {});
      fetchMigrosOrders().catch(() => {});
      fetchYemeksepetiOrders().catch(() => {});
      refreshTrendyolOrders?.().catch(() => {});
    }
  }, [selectedHistoricDate, loadPackageOrders, fetchMasaSiparisleri, fetchGetirOrders, fetchMigrosOrders, fetchYemeksepetiOrders, refreshTrendyolOrders]);

  // Sayfa yüklendiğinde siparişleri getir + 1 dakikada bir otomatik yenile
  useEffect(() => {
    if (companyId && screenData) {
      // ✅ Önce şubeye ait QR menü ID'lerini yükle, sonra siparişleri çek
      const t0 = performance.now();
      console.log('🚀 [PackageOrder] Ekran açıldı, tüm siparişler paralel yükleniyor...');
      
      Promise.all([
        loadAllowedQrMenuIds(),
        loadPackageOrders(),
        fetchYemeksepetiOrders(),
        fetchMigrosOrders(),
      ]).then(() => {
        console.log(`⏱️ [PackageOrder] İlk yükleme tamamlandı: ${Math.round(performance.now() - t0)}ms`);
      });
      
      // 1 dakikada bir otomatik yenileme (loadPackageOrders zaten getir+masa iceriyor)
      const intervalId = setInterval(() => {
        const rt0 = performance.now();
        console.log('🔄 [PackageOrder] Otomatik yenileme (1 dk)...');
        Promise.all([
          loadPackageOrders(),
          fetchYemeksepetiOrders(),
          fetchMigrosOrders(),
        ]).then(() => {
          console.log(`⏱️ [PackageOrder] Otomatik yenileme tamamlandı: ${Math.round(performance.now() - rt0)}ms`);
        });
      }, 60000);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, screenData]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // PAVOPAY AKTIF ODEMELERI TAKIP
  // ═══════════════════════════════════════════════════════════════════════════════
  const [activePavoPayments, setActivePavoPayments] = useState<PavoPaymentRequest[]>([]);
  const [pavoTrackingOpen, setPavoTrackingOpen] = useState(false);

  const loadActivePavoPayments = useCallback(async () => {
    if (!companyId) return;
    try {
      const payments = await pavoApiService.getActivePayments(companyId, screenData?.branchId || undefined);
      // Sadece paket sipariş ödemelerini göster (restoran masa ödemeleri ile karışmasın)
      setActivePavoPayments(payments.filter(p => p.sourceType === 'package_order'));
    } catch (err) {
      // sessiz
    }
  }, [companyId, screenData?.branchId]);

  useEffect(() => {
    if (!companyId) return;
    loadActivePavoPayments();
    const interval = setInterval(loadActivePavoPayments, 10000);
    return () => clearInterval(interval);
  }, [companyId, loadActivePavoPayments]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // PAVOPAY ILE ODENMIS SIPARISLER (TAMAMLANMIS) - Tabloda "PAVO" rozetini gostermek icin
  // ═══════════════════════════════════════════════════════════════════════════════
  const [pavoPaidOrderKeys, setPavoPaidOrderKeys] = useState<Set<string>>(new Set());

  const loadPavoPaidOrders = useCallback(async () => {
    if (!companyId) return;
    try {
      // Bugunun baslangicindan suana kadar
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      // Bu poll 30 sn'de bir çalıştığı için daraltmayı sunucuya bırakıyoruz: yalnızca
      // tamamlanmış paket siparişi ödemeleri gerekiyor ve kalem detayı hiç okunmuyor.
      // branchId bilinçli olarak gönderilmiyor — aşağıdaki şube kontrolü, şubesi boş
      // kaydedilmiş ödemeleri de kabul ediyor; sunucuda filtrelemek onları düşürürdü.
      const payments = await pavoApiService.getPaymentHistory(
        companyId,
        start.toISOString(),
        end.toISOString(),
        { status: 'completed', sourceType: 'package_order' },
      );
      const branch = screenData?.branchId;
      const keys = new Set<string>();
      for (const p of payments) {
        if (p.status !== 'completed') continue;
        if (p.sourceType !== 'package_order') continue;
        if (branch && p.branchId && p.branchId !== branch) continue;
        const rawOrderId = (p.orderId || '').trim();
        const rawSource = (p.orderSource || 'phone').toLowerCase().trim();
        if (rawOrderId) {
          keys.add(`${rawSource}_${rawOrderId}`);
        }
        // Pavo backend'i orderId'nin yaninda sourceReference'i de saklar; bu deger
        // genelde shortOrderNo (Trendyol icin "DM7", Yemeksepeti icin "9012345" vb).
        // UI tarafi `order.shortOrderNo` ve `order.externalOrderNo` ile karsilastirma
        // yaptigi icin Trendyol gibi orderId=trendyolPackageId (uzun hash) kullanan
        // platformlarda eslesme tek basina orderId uzerinden tutmuyordu - sourceReference
        // bazli ek anahtarlari da ekleyerek butun platformlar icin "PAVO" rozetinin
        // dogru basmasini sagliyoruz. Anahtarlar HER ZAMAN `${source}_*` formatindadir;
        // source-agnostic (`_*`) anahtarlar iki farkli platformun ayni shortOrderNo'su
        // arasinda yanlis pozitif eslesmeye yol acabildigi icin kullanilmaz.
        const rawSourceRef = (p.sourceReference || '').trim();
        if (rawSourceRef) {
          keys.add(`${rawSource}_${rawSourceRef}`);
        }
      }
      setPavoPaidOrderKeys(keys);
    } catch {
      // sessiz
    }
  }, [companyId, screenData?.branchId]);

  useEffect(() => {
    if (!companyId) return;
    loadPavoPaidOrders();
    const interval = setInterval(loadPavoPaidOrders, 30000);
    return () => clearInterval(interval);
  }, [companyId, loadPavoPaidOrders]);

  // Kuryenin tahsil ettiği siparişler ("Kurye tahsil etti" rozeti; bugünün tahsilatları)
  const [courierCollections, setCourierCollections] = useState<Map<string, CourierCollection>>(new Map());
  const loadCourierCollections = useCallback(async () => {
    if (!companyId) return;
    try {
      const list = await courierModuleService.collections({ branchId: screenData?.branchId });
      const map = new Map<string, CourierCollection>();
      list.forEach((c) => {
        const key = `${c.orderSource}:${c.orderRef}`;
        if (!map.has(key)) map.set(key, c);
      });
      setCourierCollections(map);
    } catch {
      // Kurye modülü yoksa rozet gösterilmez
    }
  }, [companyId, screenData?.branchId]);
  useEffect(() => {
    void loadCourierCollections();
    const interval = setInterval(loadCourierCollections, 30000);
    return () => clearInterval(interval);
  }, [loadCourierCollections]);
  const getCourierCollection = useCallback(
    (order: { id: number; source: string }) => courierCollections.get(`${toApiSource(order.source)}:${order.id}`),
    [courierCollections],
  );

  const isOrderPavoPaid = useCallback(
    (order: { id: number; source: string; shortOrderNo?: string; externalOrderNo?: string }) => {
      if (!order) return false;
      const source = (order.source || '').toLowerCase();
      const idStr = String(order.id);
      // Tum kontroller source-bagimli: ayni shortOrderNo'ya sahip iki farkli platform
      // siparisinin yanlis "PAVO" rozeti almasini engellemek icin source prefix'i zorunlu.
      if (pavoPaidOrderKeys.has(`${source}_${idStr}`)) return true;
      if (order.shortOrderNo && pavoPaidOrderKeys.has(`${source}_${order.shortOrderNo}`)) return true;
      if (order.externalOrderNo && pavoPaidOrderKeys.has(`${source}_${order.externalOrderNo}`)) return true;
      return false;
    },
    [pavoPaidOrderKeys],
  );

  // PavoSignalR baglantisini bu ekranda acikken kur - aksi halde OnPaymentCompleted event'leri gelmez
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        if (!pavoSignalRService.isConnected) {
          await pavoSignalRService.connect(companyId, screenData?.branchId || undefined);
          console.log('[PackageOrderScreen] PavoSignalR baglandi');
        }
      } catch (e) {
        console.warn('[PackageOrderScreen] PavoSignalR baglanamadi', e);
      }
    })();
  }, [companyId, screenData?.branchId]);

  // SignalR ile Pavo ödeme durumu değişince listeyi ve siparişleri güncelle
  useEffect(() => {
    if (!companyId) return;
    const cbId = 'package_pavo_tracking';
    pavoSignalRService.onPaymentCompleted(cbId, async (requestId) => {
      console.log('[PackageOrderScreen] Pavo odeme tamamlandi:', requestId);
      setSnackbar({
        open: true,
        message: 'PavoPay ödemesi başarıyla tamamlandı! Sipariş durumu güncelleniyor...',
        severity: 'success',
      });
      loadActivePavoPayments();
      // Server-side fatura/stok + platform update-payment-status birkac saniye surebilir.
      // Hangi platform oldugunu bilmedigimiz icin tum listeleri birkac kez refresh edelim.
      const refreshAll = () => {
        loadPackageOrders();
        fetchYemeksepetiOrders().catch(() => {});
        fetchMigrosOrders().catch(() => {});
        fetchGetirOrders().catch(() => {});
        refreshTrendyolOrders().catch(() => {});
        loadPavoPaidOrders();
      };
      refreshAll();
      setTimeout(refreshAll, 1500);
      setTimeout(refreshAll, 4000);
      setTimeout(refreshAll, 8000);
    });
    pavoSignalRService.onPaymentFailed(cbId, (requestId, _code, msg) => {
      console.log('[PackageOrderScreen] Pavo odeme basarisiz:', requestId, msg);
      setSnackbar({
        open: true,
        message: msg || 'PavoPay ödemesi başarısız oldu',
        severity: 'error',
      });
      loadActivePavoPayments();
    });
    pavoSignalRService.onPaymentCancelled(cbId, async (requestId) => {
      console.log('[PackageOrderScreen] Pavo odeme iptal:', requestId);
      setSnackbar({
        open: true,
        message: 'PavoPay ödemesi iptal edildi',
        severity: 'warning',
      });
      loadActivePavoPayments();
      loadPackageOrders();
    });
    return () => pavoSignalRService.removeCallbacks(cbId);
  }, [
    companyId,
    loadActivePavoPayments,
    loadPackageOrders,
    fetchYemeksepetiOrders,
    fetchMigrosOrders,
    fetchGetirOrders,
    refreshTrendyolOrders,
    loadPavoPaidOrders,
  ]);

  // Seçili siparişin Pavo'ya gönderilmiş (pending/processing) aktif bir ödemesi var mı?
  const hasActivePavoPaymentForSelected = useMemo(() => {
    if (!selectedOrder) return false;
    const ref = selectedOrder.shortOrderNo || String(selectedOrder.id);
    return activePavoPayments.some(p =>
      (p.status === 'pending' || p.status === 'processing') &&
      (p.sourceReference === ref || p.sourceReference?.startsWith(`${ref}|`) || p.sourceReference?.startsWith(`${ref} `))
    );
  }, [selectedOrder, activePavoPayments]);

  // ============================================================================
  // PR1: Telefon Siparişi İptal Akışı
  // ============================================================================
  // Modal açıkken seçim değişebilir (kullanıcı arka planda farklı bir sipariş
  // seçebilir); o yüzden modal açılırken o anki sipariş snapshot'ı saklanır.
  const [cancelPhoneOrderModalOpen, setCancelPhoneOrderModalOpen] = useState(false);
  const [cancelPhoneOrderTargetOrder, setCancelPhoneOrderTargetOrder] = useState<typeof selectedOrder>(null);

  // Selected order için iptal izni — OrderDetailModal butonunun aktif/disable
  // ve tooltip'inin (REASON_I18N_KEY) belirlenmesi için.
  const cancelPhoneOrderPermissionForSelected: PermissionResult = useMemo(() => {
    if (!selectedOrder) return { allowed: false, reason: 'not_phone_source' };
    return canCancelPhoneOrder(selectedOrder, {
      hasActivePavoPayment: hasActivePavoPaymentForSelected,
      isPavoPaid: isOrderPavoPaid(selectedOrder),
    });
  }, [selectedOrder, hasActivePavoPaymentForSelected, isOrderPavoPaid]);

  const handleRequestCancelPhoneOrder = useCallback(() => {
    if (!selectedOrder) return;
    if (!cancelPhoneOrderPermissionForSelected.allowed) return;
    setCancelPhoneOrderTargetOrder(selectedOrder);
    setCancelPhoneOrderModalOpen(true);
  }, [selectedOrder, cancelPhoneOrderPermissionForSelected.allowed]);

  const handleCloseCancelPhoneOrderModal = useCallback(() => {
    setCancelPhoneOrderModalOpen(false);
    // Snapshot'ı bir sonraki açılışta sıfırlamak için yarım saniye gecikmeli temizleme
    // gerekmiyor; modal kapanma animasyonu sırasında ihtiyaç yok.
    setCancelPhoneOrderTargetOrder(null);
  }, []);

  // ============================================================================
  // PR2: Telefon Siparişi Sepet Düzenleme Akışı
  // ============================================================================
  const [editCartModalOpen, setEditCartModalOpen] = useState(false);
  const [editCartTargetOrder, setEditCartTargetOrder] = useState<typeof selectedOrder>(null);
  // PR2: EditCart modal her mutation sonrası bu sayacı artırır; OrderDetailModal
  // useEffect dependency'sinde izler ve sipariş detayını yeniden çeker — kullanıcı
  // detay modal'ına geri döndüğünde silinen kalem anında yansır.
  const [orderDetailRefreshKey, setOrderDetailRefreshKey] = useState(0);

  const editCartPermissionForSelected: PermissionResult = useMemo(() => {
    if (!selectedOrder) return { allowed: false, reason: 'not_phone_source' };
    return canEditPhoneOrderItems(selectedOrder, {
      hasActivePavoPayment: hasActivePavoPaymentForSelected,
      isPavoPaid: isOrderPavoPaid(selectedOrder),
    });
  }, [selectedOrder, hasActivePavoPaymentForSelected, isOrderPavoPaid]);

  // F3.1 / O3 — Hibrit telefon-arama + yeni sipariş kuyruğu için "herhangi bir
  // modal açık mı?" flag'i. Üç yerde kullanılır:
  //   1) onNewCall callback (F1.5) — modal açıkken arka plan müşteri context'ini koru
  //   2) Render switch (F3.2) — modal açıkken IncomingCallPopupModal yerine
  //      IncomingCallNotification (sağ üst köşe) göster.
  //   3) Yeni sipariş kuyruğu (O3) — "ekran müsait" hesabı için (anyOtherModalOpen).
  //
  // İKI KATMANLI HESAPLAMA (çevrim kırılması için):
  //   - anyOtherModalOpen: NewOrderModal hariç tüm diğer modal'lar
  //     → useNewOrderQueue çağrısından ÖNCE hesaplanır, hook'a isScreenAvailable
  //       parametresinde verilir.
  //   - anyModalOpen: anyOtherModalOpen || !!activeNewOrder
  //     → useNewOrderQueue çağrısından SONRA hesaplanır, telefon F3.1 mantığı kullanır.
  const anyOtherModalOpen = useMemo(() => (
    addCustomerModalOpen ||
    addAddressModalOpen ||
    customerDetailModalOpen ||
    addressDetailModalOpen ||
    editCartModalOpen ||
    quickSaleModalOpen ||
    isPaymentModalOpen ||
    isManualPhoneModalOpen ||
    cancelPhoneOrderModalOpen ||
    detailModalOpen ||
    selectCourierModalOpen ||
    courierViewOpen ||
    allCallsModalOpen ||
    verificationModalOpen ||
    categoryMappingModalOpen ||
    categoryManagementModalOpen ||
    settingsModalOpen ||
    isCashierLoginModalOpen ||
    qrOrderDetailModalOpen ||
    getirDetailModalOpen ||
    getirCancelModalOpen ||
    getirRestaurantClosedModalOpen ||
    migrosCancelModalOpen ||
    ysCancelModalOpen ||
    migrosRestaurantClosedModalOpen ||
    ysVendorClosedModalOpen ||
    trendyolDetailModalOpen ||
    trendyolCancelModalOpen ||
    trendyolCancelNotifModalOpen ||
    trendyolVendorClosedModalOpen ||
    closeRestaurantModalOpen ||
    migrosDetailModalOpen ||
    ysDetailModalOpen ||
    migrosRejectModalOpen ||
    ysRejectModalOpen ||
    // ✅ C1 fix: önceden listede olmayan 6 modal flag — bunlardan biri açıkken
    // (1) yeni telefon araması onNewCall müşteri context wipe guard'ını atlardı,
    // (2) yeni yemek siparişi reactive promotion ile NewOrderModal arkadan açılırdı.
    getirScheduledBlockOpen ||
    showLocalServiceWarningModal ||
    showLocalServiceUpdateModal ||
    pavoTrackingOpen ||
    pavoP2pSelectOpen ||
    restP2pReceiptSelectOpen ||
    // Birleşik kuyruk: telefon karar modalı açıkken yemek NewOrderModal'ı
    // arkadan promote edilmesin
    !!callDecisionCall
  ), [
    addCustomerModalOpen, addAddressModalOpen, customerDetailModalOpen,
    addressDetailModalOpen, editCartModalOpen,
    quickSaleModalOpen, isPaymentModalOpen, isManualPhoneModalOpen,
    cancelPhoneOrderModalOpen, detailModalOpen, selectCourierModalOpen, courierViewOpen,
    allCallsModalOpen, verificationModalOpen, categoryMappingModalOpen,
    categoryManagementModalOpen, settingsModalOpen, isCashierLoginModalOpen,
    qrOrderDetailModalOpen, getirDetailModalOpen, getirCancelModalOpen,
    getirRestaurantClosedModalOpen, migrosCancelModalOpen, ysCancelModalOpen,
    migrosRestaurantClosedModalOpen, ysVendorClosedModalOpen,
    trendyolDetailModalOpen, trendyolCancelModalOpen, trendyolCancelNotifModalOpen,
    trendyolVendorClosedModalOpen, closeRestaurantModalOpen,
    migrosDetailModalOpen, ysDetailModalOpen, migrosRejectModalOpen,
    ysRejectModalOpen,
    // ✅ C1 fix: 6 yeni flag deps
    getirScheduledBlockOpen, showLocalServiceWarningModal,
    showLocalServiceUpdateModal, pavoTrackingOpen,
    pavoP2pSelectOpen, restP2pReceiptSelectOpen,
    callDecisionCall,
  ]);

  // ============================================================================
  // O3 — Sipariş Bildirim Kuyruğu (FIFO + Reactive Promotion)
  // ============================================================================
  // Ekran müsait tanımı (yemek için):
  //   - !anyOtherModalOpen — kullanıcı başka modal'la meşgul değil (karar modalı dahil)
  //   - !activeCall — telefon ne çalıyor ne konuşuluyor
  //     (Eski !incomingCall kontrolü kaldırıldı: held aramalar pendingCalls'ta
  //      kalıcı yaşadığı için yemekleri sonsuza dek bloklardı. Bekleyen telefon
  //      sırası artık phoneQueueEarliestTs ile birleşik FIFO üzerinden korunuyor.)
  //   - (activeNewOrder === null kontrolü hook içinde otomatik yapılıyor)
  //   - phoneQueueEarliestTs: kuyruktaki en eski telefon, sıradaki yemekten
  //     önce geldiyse yemek bekler (katı kronolojik FIFO).
  const {
    activeNewOrder,
    pendingNewOrders,
    enqueueNewOrder,
    completeActiveNewOrder,
    removePendingNewOrder,
    activeNewOrderRef,
  } = useNewOrderQueue({
    isScreenAvailable: !anyOtherModalOpen && !activeCall,
    phoneQueueEarliestTs: pendingCalls.length > 0 ? pendingCalls[0].startedAt : null,
    playNewOrderSound,
    stopNewOrderSound,
  });

  // Handler'lar (handleSignalRNewOrder, handleTrendyolNewOrder, handleCloseNewOrderModal)
  // hook çağrısından çok önce tanımlandığı için ref aracılığıyla erişiyorlar.
  // Burada ref'leri güncel tutuyoruz.
  useEffect(() => {
    enqueueNewOrderRef.current = enqueueNewOrder;
  }, [enqueueNewOrder]);
  useEffect(() => {
    completeActiveNewOrderRef.current = completeActiveNewOrder;
  }, [completeActiveNewOrder]);

  // Üst-düzey anyModalOpen — F3.1 telefon mantığı + O3 yeni sipariş için
  const anyModalOpen = useMemo(
    () => anyOtherModalOpen || !!activeNewOrder,
    [anyOtherModalOpen, activeNewOrder],
  );

  // F1.5 ile birlikte: onNewCall callback'i bu ref'i okur (closure güncel kalsın).
  useEffect(() => {
    anyModalOpenRef.current = anyModalOpen;
  }, [anyModalOpen]);

  // ============================================================================
  // BİRLEŞİK KRONOLOJİK FIFO — Telefon + Platform Siparişleri
  // ============================================================================

  // Kuyruktaki en eski bekleyen iş telefon mu yemek mi? (startedAt / enqueuedAt)
  // Her iki kuyruk da kendi içinde FIFO olduğundan [0] elemanları en eskidir.
  const earliestQueuedKind = useMemo<'phone' | 'food' | null>(() => {
    const phoneTs = pendingCalls.length > 0 ? pendingCalls[0].startedAt : null;
    const foodTs = pendingNewOrders.length > 0 ? pendingNewOrders[0].enqueuedAt : null;
    if (phoneTs == null && foodTs == null) return null;
    if (phoneTs == null) return 'food';
    if (foodTs == null) return 'phone';
    return phoneTs <= foodTs ? 'phone' : 'food';
  }, [pendingCalls, pendingNewOrders]);

  // TELEFON PROMOTION EFFECT: ekran tamamen serbest kaldığında (modal yok +
  // müşteri bağlamı temiz + aktif arama yok) ve sıradaki en eski bekleyen iş
  // bir telefon aramasıysa → karar modalı 'next-in-queue' modunda açılır.
  // (Yemek siparişlerinin karşılığı useNewOrderQueue reactive promotion'ı.)
  useEffect(() => {
    if (callDecisionCall) return;          // karar modalı zaten açık
    if (activeCall) return;                // telefonla meşgul
    if (anyModalOpen) return;              // herhangi bir modal açık (yemek dahil)
    if (customerContextBusy) return;       // önceki müşterinin işi bitmedi
    if (pendingCalls.length === 0) return;
    if (earliestQueuedKind !== 'phone') return; // sıra yemekte — o kendi hook'unda promote olur

    // Snooze ("Beklemeye Devam Et") süresi dolana kadar bekle; değilse kısa
    // bir gecikme ile aç (modal kapanma animasyonu / state cleanup koruması).
    const delay = Math.max(400, callDecisionSnoozeUntil - Date.now() + 100);
    const timer = setTimeout(() => {
      if (callDecisionCallRef.current) return;
      const next = pendingCalls[0];
      if (!next) return;
      console.log('[CallDecision] Ekran serbest — sıradaki telefon için karar modalı açılıyor:', next.call.phone);
      callDecisionCallRef.current = next;
      setCallDecisionMode('next-in-queue');
      setCallDecisionCall(next);
    }, delay);
    return () => clearTimeout(timer);
  }, [callDecisionCall, activeCall, anyModalOpen, customerContextBusy, pendingCalls, earliestQueuedKind, callDecisionSnoozeUntil]);

  // Karar modalını kapat (ref + state senkron)
  const closeCallDecision = useCallback(() => {
    callDecisionCallRef.current = null;
    setCallDecisionCall(null);
  }, []);

  // "Aramayı Beklet" / "Beklemeye Devam Et" — arama zaten kuyrukta held olarak
  // duruyor; sadece modal kapanır. next-in-queue modunda 60 sn snooze uygulanır
  // (ekran boşken modal anında yeniden açılmasın; bildirim sağ üstte kalır).
  const handleCallDecisionHold = useCallback(() => {
    if (callDecisionMode === 'next-in-queue') {
      setCallDecisionSnoozeUntil(Date.now() + 60000);
    }
    console.log('[CallDecision] Beklet seçildi — arama kuyrukta bekliyor');
    closeCallDecision();
  }, [callDecisionMode, closeCallDecision]);

  // "Yeni Müşteriye Geç" / "Müşteriye Geç" — mevcut iş kapatılır, önceki
  // müşteri bağlamı temizlenir ve bekleyen arama aktif müşteri olarak alınır.
  const handleCallDecisionSwitch = useCallback(async () => {
    const qc = callDecisionCallRef.current;
    closeCallDecision();
    if (!qc) return;
    console.log('[CallDecision] Müşteriye geç:', qc.call.phone);
    // Açık sipariş giriş modalını kapat (yarım sepet kasiyer kararıyla bırakılır)
    setQuickSaleModalOpen(false);
    setAddOnOrderId(null);
    // Önceki müşteri bağlamını temizle
    setCustomerPhoneFilter(null);
    setCustomerNameFilter(null);
    setCurrentCustomer(null);
    setSelectedAddressId(null);
    setCallDecisionSnoozeUntil(0);
    // Bekleyen aramayı aktif müşteri yap (eski aktif arama hook içinde kapatılır,
    // müşteri bulunursa onCustomerFound sol paneli doldurur)
    await takeCallAsCustomer(qc.call.phone);
  }, [closeCallDecision, takeCallAsCustomer]);

  // Sağ üstteki held arama bildiriminden "Müşteriye Geç" tıklanınca karar
  // modalını o arama için 'next-in-queue' modunda aç.
  const handleRequestCallDecision = useCallback((qc: QueuedCall) => {
    callDecisionCallRef.current = qc;
    setCallDecisionMode('next-in-queue');
    setCallDecisionCall(qc);
  }, []);

  // Sipariş tamamlandığında (QuickSale onPackageOrderComplete) kuyrukta bekleyen
  // telefon varsa karar modalını hemen aç — kullanıcı kuralı: sessiz otomatik
  // geçiş değil, modal ile sorulur. (Müşteri bağlamı hâlâ açık olduğundan
  // yukarıdaki promotion effect'i tetiklenmez; bu handler onu devre dışı bırakır.)
  const handleOrderFlowCompleted = useCallback(() => {
    if (callDecisionCallRef.current) return;
    if (pendingCalls.length === 0) return;
    if (earliestQueuedKind !== 'phone') return; // sıra yemekte — reactive promotion halleder
    console.log('[CallDecision] Sipariş tamamlandı — sıradaki telefon için karar modalı açılıyor');
    setCallDecisionSnoozeUntil(0);
    callDecisionCallRef.current = pendingCalls[0];
    setCallDecisionMode('next-in-queue');
    setCallDecisionCall(pendingCalls[0]);
  }, [pendingCalls, earliestQueuedKind]);

  const handleRequestEditCart = useCallback(() => {
    if (!selectedOrder) return;
    if (!editCartPermissionForSelected.allowed) return;
    setEditCartTargetOrder(selectedOrder);
    setEditCartModalOpen(true);
  }, [selectedOrder, editCartPermissionForSelected.allowed]);

  const handleCloseEditCartModal = useCallback(() => {
    setEditCartModalOpen(false);
    setEditCartTargetOrder(null);
    // Modal kapanırken parent listesini bir kez tazele — kullanıcı modal içinde
    // mutation yaptıysa toplam ve kalem sayısı güncel olsun.
    loadPackageOrders();
  }, [loadPackageOrders]);

  const handleEditCartMutated = useCallback(() => {
    // Her başarılı mutation sonrasında arka planda listeyi tazele;
    // selectedOrder ve totalAmount referansı senkron olur.
    loadPackageOrders();
    // PR2: OrderDetailModal'ı zorla yeniden fetch ettir — kullanıcı edit modal'ı
    // kapattığında detay modal eski veriyle açık kalmasın.
    setOrderDetailRefreshKey((k) => k + 1);
  }, [loadPackageOrders]);

  const handleEditCartAddNewItem = useCallback((orderId: number) => {
    // Modal'ı kapat, mevcut "İlave Sipariş" akışını tetikle (QuickSale modal).
    setEditCartModalOpen(false);
    setEditCartTargetOrder(null);
    setAddOnOrderId(orderId);
    setQuickSaleModalOpen(true);
  }, [setAddOnOrderId, setQuickSaleModalOpen]);

  // handleCancelPhoneOrderConfirmed — `handleKitchenPrint` referansı gerektirdiğinden
  // (iptal sonrası mutfak iptal fişi), `useKitchenPrintHandlers` çağrısının ALTINA
  // taşındı (bkz. ~2917). Burada bilinçli olarak tanımlanmıyor.

  // ✅ answerCall, endCallUnanswered, endCallComplete artık usePhoneCalls hook'undan geliyor
  
  // ✅ endCallComplete'ten sonra müşteri bilgilerini temizle
  const handleEndCallComplete = useCallback(async () => {
    await endCallComplete();
    setCurrentCustomer(null);
    setSelectedAddressId(null);
  }, [endCallComplete]);
  
  // ✅ endCallUnanswered'tan sonra müşteri bilgilerini temizle
  const handleEndCallUnanswered = useCallback(async () => {
    await endCallUnanswered();
    setCurrentCustomer(null);
    setSelectedAddressId(null);
  }, [endCallUnanswered]);

  // ✅ Üst bar "Tümüne Dön": arayan/müşteri bağlamını tamamen temizler.
  // Telefon kapansa bile arayan bilgisi ekranda kalabiliyor — bu buton her durumda
  // temiz bir "tüm siparişler" görünümüne döndürür. Aktif (cevaplanmış) görüşme
  // hâlâ açık görünüyorsa önce tamamlanmış olarak kapatılır (DB kaydı da güncellenir).
  const handleReturnToAll = useCallback(async () => {
    if (activeCall) {
      // Cevaplanmışsa "tamamlandı", cevaplanmamışsa "cevapsız" olarak kapat
      if (activeCall.isAnswered) {
        await endCallComplete();
      } else {
        await endCallUnanswered();
      }
    }
    setCustomerPhoneFilter(null);
    setCustomerNameFilter(null);
    setCurrentCustomer(null);
    setSelectedAddressId(null);
    setIsCallAnswered(false);
  }, [activeCall, endCallComplete, endCallUnanswered, setIsCallAnswered]);
  
  // ✅ SSE/Electron useEffect artık usePhoneCalls hook'unda

  const branchName = screenData?.branchId ? getBranchName(screenData.branchId) : 'Tüm Şubeler';

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, orderId: number, source?: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuOrderId(orderId);
    setMenuOrderSource(source || null);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuOrderId(null);
  };

  // Detail Modal handlers
  const handleOpenDetail = () => {
    if (!selectedOrder) return;
    
    // ✅ QR/E-Ticaret/Kiosk siparişleri için özel detay modalı (MasaSiparisleri tablosundan)
    if (['qr', 'ecommerce', 'kiosk'].includes(selectedOrder.source)) {
      const masaSiparisi = [...qrMenuOrders, ...ecommerceOrders, ...kioskOrders].find(o => o.id === selectedOrder.id);
      console.log('🔍 [Detail] Kiosk/QR sipariş aranıyor:', { id: selectedOrder.id, source: selectedOrder.source, bulundu: !!masaSiparisi, kioskOrdersSayisi: kioskOrders.length });
      if (masaSiparisi) {
        setSelectedQrOrder(masaSiparisi);
        setQrOrderDetailModalOpen(true);
        return;
      }
    }
    
    // Getir siparisleri icin DB detay modali
    if (selectedOrder.source === 'getir') {
      setGetirDetailOrderId(selectedOrder.id);
      setGetirDetailModalOpen(true);
      return;
    }

    // Migros siparisleri icin detay modali
    if (selectedOrder.source === 'migros') {
      setMigrosDetailOrderId(selectedOrder.id);
      setMigrosDetailModalOpen(true);
      return;
    }

    // Yemeksepeti siparisleri icin detay modali
    if (selectedOrder.source === 'yemeksepeti') {
      setYsDetailOrderId(selectedOrder.id);
      setYsDetailModalOpen(true);
      return;
    }

    // Trendyol Yemek siparisleri icin detay modali
    if (selectedOrder.source === 'trendyol' && selectedOrder.trendyolPackageId) {
      const trdOrder = trendyolOrders.find(o => o.id === selectedOrder.trendyolPackageId);
      if (trdOrder) {
        setTrendyolDetailOrder(trdOrder);
        setTrendyolDetailModalOpen(true);
        return;
      }
    }
    
    // Normal sipariş için mevcut detay modalı
    setDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailModalOpen(false);
  };

  // Kitchen & Receipt Print Handlers (extracted to useKitchenPrintHandlers hook)
  const { handleKitchenPrint, handleReceiptPrint } = useKitchenPrintHandlers({
    companyId: companyId ?? null,
    screenData,
    filteredOrders,
    trendyolOrders,
    qrMenuOrders,
    ecommerceOrders,
    kioskOrders,
    printPackageKitchenReceipt,
    printPackageKitchenReceiptByCategory,
    refreshGetirToken,
    trendyolProductDescriptions,
    platformCategoryMappingLookup: mappingLookup,
    reportFailure: capturePrintFailure,
  });

  // Telefon paket siparişi iptal onayı sonrası:
  //  1) Backend cancelPackageOrder çağrısı
  //  2) UI listeleri/state tazele + detay modalı kapat + success snackbar
  //  3) Mutfak iptal fişi otomatik bastır (kategori-bazlı, isCancellationOrder=true
  //     handleKitchenPrint içinde OrderStatus=CANCELLED görünce kendiliğinden set olur)
  //  - handleKitchenPrint referansı için bu blok useKitchenPrintHandlers'tan sonraya alındı.
  const handleCancelPhoneOrderConfirmed = useCallback(
    async (payload: CancelPhoneOrderPayload): Promise<void> => {
      if (!cancelPhoneOrderTargetOrder) {
        throw new Error('Hedef sipariş bulunamadı.');
      }
      // Server-side guard'ları client-side de doğrula (snapshot'tan sonra durum
      // değişmiş olabilir; özellikle Pavo aktif ödeme).
      const ctxNow = {
        hasActivePavoPayment: hasActivePavoPaymentForSelected,
        isPavoPaid: isOrderPavoPaid(cancelPhoneOrderTargetOrder),
      };
      const guardNow = canCancelPhoneOrder(cancelPhoneOrderTargetOrder, ctxNow);
      if (!guardNow.allowed && guardNow.reason) {
        throw new Error(t(REASON_I18N_KEY[guardNow.reason]));
      }

      const orderIdToCancel = cancelPhoneOrderTargetOrder.id;
      const result = await cancelPackageOrder(
        orderIdToCancel,
        payload.reasonText,
        payload.note,
        currentUser?.uid || undefined,
        userDisplayName || undefined,
      );
      if (!result.Success) {
        throw new Error(result.Message || t('orderManagement.cancelPhoneOrder.snackbar.error', { message: '' }));
      }

      // Kuryeye atanmışsa kurye uygulamasından anında kaldır (best-effort).
      mainApi.post('/api/CourierActiveOrders/notify-cancel', { packageOrderId: orderIdToCancel })
        .catch((err: unknown) => console.warn('[CancelPhone] Kuryeye iptal bildirilemedi:', err));

      // UI tazele: aktif sipariş listesi + Pavo aktif ödeme listesi.
      loadPackageOrders();
      loadActivePavoPayments();

      // Detay modalı açıksa kapat (sipariş artık iptal — detay anlamını yitirir).
      setDetailModalOpen(false);

      setSnackbar({
        open: true,
        message: t('orderManagement.cancelPhoneOrder.snackbar.success'),
        severity: 'success',
      });

      // Mutfak iptal fişi (best-effort — yazdırma hatası iptali bozmasın).
      // `forceCancellation: true` ile çağırıyoruz çünkü cancelPackageOrder API
      // başarısı kesin: backend snapshot'a (OrderStatus alanı) bakmaya gerek yok;
      // PackageKitchenPrintService "❌ İPTAL SİPARİŞ ❌" başlığıyla aynı
      // kategori-yazıcı eşlemesine fiş basacak.
      try {
        await handleKitchenPrint(orderIdToCancel, 'phone', { forceCancellation: true });
      } catch (err) {
        console.warn('[CancelPhone] Mutfak iptal fişi yazdırılamadı:', err);
        setSnackbar({
          open: true,
          message: 'Sipariş iptal edildi, ancak mutfak iptal fişi yazdırılamadı',
          severity: 'warning',
        });
      }
    },
    [
      cancelPhoneOrderTargetOrder,
      hasActivePavoPaymentForSelected,
      isOrderPavoPaid,
      currentUser?.uid,
      userDisplayName,
      loadPackageOrders,
      loadActivePavoPayments,
      handleKitchenPrint,
      t,
    ],
  );

  // Kabul+yazdırma yapılan/yapılmakta olan kuyruk siparişlerinin anahtarları.
  // Oto geri sayım ile manuel "Onayla ve Yazdır" butonunun aynı sipariş için
  // çakışmasını (çift onay + çift fiş) ekran tarafında kesin olarak engeller.
  const processedAcceptKeysRef = useRef<Set<string>>(new Set());

  // ══════════════════════════════════════════════════════════════════════════
  // OTO ONAY/YAZDIRMA HATA UYARISI
  // ══════════════════════════════════════════════════════════════════════════
  // Kabul + yazdırma akışında bir şey ters gittiğinde (platform onayı reddetti,
  // yazıcıya ulaşılamadı, sipariş detayı çekilemedi...) yeni sipariş modalı gibi
  // dikkat çekici bir uyarı gösterilir ve AYRI bir alarm sesi çalar. Amaç: geri
  // sayım bitip modal kapandıktan sonra "sipariş işlendi" sanılmasını önlemek.
  //
  // Kuyruk: yoğun anda birden fazla sipariş patlayabilir; uyarılar sırayla
  // gösterilir, kullanıcı her birini tek tek onaylar.
  const { playFailureSound, stopFailureSound } = useAutoAcceptFailureSound();
  const [autoAcceptFailures, setAutoAcceptFailures] = useState<AutoAcceptFailure[]>([]);
  const activeAutoAcceptFailure = autoAcceptFailures[0] ?? null;

  // Yeni uyarı öne geçtiğinde alarmı baştan başlat (sonraki uyarı için de çalar).
  useEffect(() => {
    if (!activeAutoAcceptFailure) return;
    playFailureSound();
  }, [activeAutoAcceptFailure?.key, playFailureSound]);

  const handleAcknowledgeAutoAcceptFailure = useCallback(() => {
    stopFailureSound();
    setAutoAcceptFailures(prev => prev.slice(1));
  }, [stopFailureSound]);

  // Kabul + yazdırma ÇEKİRDEĞİ — verilen kuyruk siparişini platformda onaylar
  // ve fişlerini yazdırır. ÜÇ tüketici:
  //   1. Oto kabul geri sayımı       → acceptAndPrintActiveOrder(manual=false)
  //   2. Modal "Onayla ve Yazdır"    → acceptAndPrintActiveOrder(manual=true)
  //   3. Bildirim "Onayla ve Yazdır" → handleAcceptAndPrintPending (manual=true,
  //      sipariş hiç active olmadan pending kuyruğundan işlenir)
  //
  // `manual=true` (butondan tetiklendi):
  //   - Oto kabul AÇIKSA ayarlardaki fiş bayrakları izlenir (oto akışla birebir aynı,
  //     sadece geri sayımı beklemeden çalışır).
  //   - Oto kabul KAPALIYSA her iki fiş de yazdırılır (buton herkese açık; ayar yoksa
  //     "yazdır" beklentisi mutfak + sipariş fişi demektir).
  //
  // NOT: idempotency guard'ı (processedAcceptKeysRef) ve kuyruk yönetimi
  // (completeActiveNewOrder / removePendingNewOrder) ÇAĞIRANLARDADIR — bu
  // fonksiyon yalnızca onay + yazdırma işini yapar.
  const performAcceptAndPrint = useCallback(async (activeQ: QueuedNewOrder, manual: boolean, trigger: string) => {
    const order = activeQ.masaSiparisi ?? null;
    const trdOrder = activeQ.trendyolOrder ?? null;

    // Bu akışta biriken tüm hatalar. Yazdırma katmanı `capturePrintFailure`
    // üzerinden buraya yazar (bkz. printFailureSinkRef); onay adımının hatasını
    // ve beklenmedik istisnaları da aşağıda elle ekliyoruz.
    const problems: string[] = [];
    let acceptFailed = false;
    // Save/restore: iç içe çağrılarda (ve nadiren de olsa bildirim butonuyla oto
    // akışın araya girmesinde) önceki toplayıcı kaybolmasın.
    const previousSink = printFailureSinkRef.current;
    printFailureSinkRef.current = problems;

    const printKitchen = manual
      ? (screenData?.autoAcceptEnabled ? !!screenData?.autoPrintKitchen : true)
      : !!screenData?.autoPrintKitchen;
    const printReceipt = manual
      ? (screenData?.autoAcceptEnabled ? !!screenData?.autoPrintReceiptOnAccept : true)
      : !!screenData?.autoPrintReceiptOnAccept;

    console.log('🤖 [AutoAccept] ═══════════════════════════════════════');
    console.log(`🤖 [AutoAccept] KABUL VE YAZDIRMA BAŞLADI (${trigger})`);
    console.log('🤖 [AutoAccept] Ayarlar:', {
      manual,
      printKitchen,
      printReceipt,
      autoPrintKitchen: screenData?.autoPrintKitchen,
      autoPrintReceiptOnAccept: screenData?.autoPrintReceiptOnAccept,
      printingMode: screenData?.printingSettings?.printingMode,
      defaultReceiptPrinter: screenData?.printingSettings?.defaultReceiptPrinter?.name || null,
      adisyonPrinter: screenData?.printingSettings?.adisyonPrinter?.name || null,
      activeQueueKey: activeQ.key,
      hasNewOrderData: !!order,
      hasNewTrendyolOrderData: !!trdOrder,
    });

    // Beklenmedik hatalar yutulur (loglanır) — onay ve yazdırma adımlarının
    // kendi try/catch'leri var; buradaki katman çağıranın kuyruk yönetimini
    // (complete/remove) bozacak bir unhandled rejection oluşmasın diye.
    try {
      let orderId: number | null = null;
      let source = '';
      let trendyolPackageId: string | null = null;

      if (trdOrder) {
        const converted = convertTrendyolOrderToPackageOrder(trdOrder);
        orderId = converted.id;
        source = 'trendyol';
        trendyolPackageId = trdOrder.id;
      } else if (order) {
        orderId = order.id;
        source = (order.siparisKaynagi || '').toLowerCase();
      }

      // Kuyruğun `platform` alanı sipariş kuyruğa girerken bir kez çözülür ve
      // platform siparişleri için sabittir. `siparisKaynagi` beklenmedik bir değer
      // taşırsa aşağıdaki eşleşmelerin hiçbiri tutmaz ve onay adımı "kabul gerekmiyor"
      // dalına düşüp sessizce atlanır: fişler basılır, sipariş onaysız kalır, hata
      // üretilmediği için uyarı modalı da açılmaz.
      const platformRequiresAccept =
        activeQ.platform === 'getir' || activeQ.platform === 'migros'
        || activeQ.platform === 'yemeksepeti' || activeQ.platform === 'trendyol';
      if (platformRequiresAccept && !source.includes(activeQ.platform)) {
        console.warn('🤖 [AutoAccept] ⚠️ siparisKaynagi kuyruk platformuyla uyuşmuyor, platform baz alınıyor:', {
          siparisKaynagi: order?.siparisKaynagi, source, platform: activeQ.platform,
        });
        source = activeQ.platform;
      }

      console.log('🤖 [AutoAccept] Sipariş bilgileri:', { orderId, source, platform: activeQ.platform, trendyolPackageId });

      if (!orderId) {
        console.warn('🤖 [AutoAccept] ⚠️ Sipariş ID bulunamadı!');
        problems.push('Siparişin kimliği (ID) çözümlenemedi; onay ve yazdırma yapılamadı.');
      } else {
        // 1. Platform bazlı kabul (try/catch, başarısız olursa geçilir)
        //
        // DİKKAT: Platform onay fonksiyonlarının HİÇBİRİ hata fırlatmaz; hepsi
        // { success, message } döndürür. Sonucu okumazsak başarısız onay sessizce
        // geçer, fişler basılır ve `problems` boş kaldığı için uyarı modalı hiç
        // açılmaz — sipariş onaylanmamış olduğu halde kimse fark etmez.
        try {
          if (source.includes('getir')) {
            console.log('[AutoAccept] Getir siparişi onaylanıyor...', orderId);
            const getirResult = await updateGetirOrderStatus(orderId, 'verify');
            if (!getirResult.success) {
              acceptFailed = true;
              problems.push(`Getir onayı başarısız: ${getirResult.message || 'Bilinmeyen hata'}`);
            }
            await fetchGetirOrders();
          } else if (source === 'trendyol' && trendyolPackageId) {
            console.log('[AutoAccept] Trendyol siparişi onaylanıyor...', trendyolPackageId);
            const trdResult = await updateTrendyolOrderStatus(trendyolPackageId, 'accept');
            if (!trdResult.success) {
              // statusMismatch: sipariş Trendyol'da zaten ileri bir durumda, self-heal
              // yapıldı — onay kaybı yok. Ancak durum doğrulanamadıysa ('not-listed',
              // 'refresh-failed') onaylandığını varsayamayız, uyarı çıkmalı.
              const unverified = trdResult.statusMismatch
                && (trdResult.currentStatus === 'not-listed' || trdResult.currentStatus === 'refresh-failed');
              if (!trdResult.statusMismatch) {
                acceptFailed = true;
                problems.push(`Uber Eats onayı başarısız: ${trdResult.message || 'Bilinmeyen hata'}`);
              } else if (unverified) {
                acceptFailed = true;
                problems.push('Uber Eats onayı doğrulanamadı: siparişin güncel durumu Uber Eats\'ten okunamadı.');
              } else {
                console.log('[AutoAccept] Trendyol siparişi zaten ileri durumda, onay adımı atlandı:', trdResult.currentStatus);
              }
            }
          } else if (source.includes('yemeksepeti')) {
            console.log('[AutoAccept] Yemeksepeti siparişi onaylanıyor...', orderId);
            const ysResult = await updateYemeksepetiOrderStatus(orderId, 'accept');
            if (!ysResult.success) {
              acceptFailed = true;
              problems.push(`Yemeksepeti onayı başarısız: ${ysResult.message || 'Bilinmeyen hata'}`);
            }
            await fetchYemeksepetiOrders();
          } else if (source.includes('migros')) {
            // Sipariş Migros'ta zaten onaylanmışsa (okuma sözlüğünde COLLECTING ve
            // sonrası) tekrar 'Approved' göndermek reddedilir; onay adımını atlayıp
            // doğrudan fiş yazdırmaya geçiyoruz.
            const migrosStatus = normalizeMigrosStatus(migrosOrders.find(o => o.id === orderId)?.status);
            if (migrosStatus === 'NEW_PENDING' || migrosStatus === 'UNKNOWN') {
              console.log('[AutoAccept] Migros siparişi onaylanıyor...', orderId);
              const migrosResult = await updateMigrosOrderStatus(orderId, 'Approved');
              // Bu fonksiyon hata fırlatmaz; başarısızlığı dönüş değerinden okumak
              // zorundayız, aksi halde onaylanmamış sipariş onaylanmış gibi görünür.
              if (!migrosResult.success && !migrosResult.alreadyInStatus && !migrosResult.inFlight) {
                acceptFailed = true;
                problems.push(`Migros onayı başarısız: ${migrosResult.message || 'Bilinmeyen hata'}`);
              }
            } else {
              console.log('[AutoAccept] Migros siparişi zaten onaylı, onay adımı atlandı:', { orderId, migrosStatus });
            }
            await fetchMigrosOrders();
          } else if (platformRequiresAccept) {
            // Buraya düşmek, onay isteğinin HİÇ gönderilmediği anlamına gelir.
            // Eskiden sessizce "kabul gerekmiyor" sayılıyordu.
            acceptFailed = true;
            problems.push(
              `Onay isteği hiç gönderilemedi: sipariş kaynağı çözümlenemedi `
              + `(platform=${activeQ.platform}, kaynak="${source}"`
              + `${activeQ.platform === 'trendyol' && !trendyolPackageId ? ', paket kimliği yok' : ''}).`,
            );
          } else {
            console.log('[AutoAccept] QR/Kiosk/Telefon siparişi, kabul adımı gerekmiyor:', source);
          }
          console.log('[AutoAccept] Sipariş kabul işlemi tamamlandı:', {
            platform: activeQ.platform, source, orderId, acceptFailed,
          });
        } catch (err: any) {
          console.warn('[AutoAccept] Sipariş kabul başarısız (fişler yine de yazdırılacak):', err);
          acceptFailed = true;
          const detail = err?.response?.data?.message
            || err?.response?.data?.Message
            || (err instanceof Error ? err.message : null)
            || 'Bilinmeyen hata';
          problems.push(`Platform onayı başarısız: ${detail}`);
        }
      }

      // 2. Fişler HER ZAMAN yazdırılır (kabul başarısız olsa bile)
      //
      // Trendyol oto-yazdırma fix: Hook'lar siparişi normalde `trendyolOrders`
      // state listesinden bulur. Ancak yeni gelen sipariş modal'dan tetiklendiğinde
      // henüz listeye girmemiş veya `accept` çağrısı ile state refresh yaşandığı
      // için arama başarısız olabiliyordu. Elimizde olan tam Trendyol objesini
      // (`trdOrder`) `preFetchedTrendyolOrder` ile doğrudan hook'lara iletiyoruz.
      const trendyolPrintOptions = trdOrder ? { preFetchedTrendyolOrder: trdOrder } : undefined;

      if (orderId) {
        if (printKitchen) {
          try {
            console.log('🤖 [AutoAccept] 🖨️ MUTFAK FİŞİ yazdırılıyor...', { orderId, source, printingMode: screenData?.printingSettings?.printingMode, hasPreFetchedTrd: !!trendyolPrintOptions });
            await handleKitchenPrint(orderId, source || undefined, trendyolPrintOptions);
            console.log('🤖 [AutoAccept] ✅ Mutfak fişi başarılı');
          } catch (err) {
            console.error('🤖 [AutoAccept] ❌ Mutfak fişi HATA:', err);
            problems.push(`Mutfak fişi yazdırılamadı: ${err instanceof Error ? err.message : 'Bilinmeyen hata'}`);
          }
        } else {
          console.log('🤖 [AutoAccept] ℹ️ Mutfak fişi KAPALI (autoPrintKitchen=false)');
        }
        if (printReceipt) {
          try {
            const receiptPrinter = screenData?.printingSettings?.defaultReceiptPrinter || screenData?.printingSettings?.adisyonPrinter;
            console.log('🤖 [AutoAccept] 🧾 SİPARİŞ FİŞİ yazdırılıyor...', { orderId, source, printer: receiptPrinter?.name || '(YOK)', hasPreFetchedTrd: !!trendyolPrintOptions });
            await handleReceiptPrint(orderId, source || undefined, trendyolPrintOptions);
            console.log('🤖 [AutoAccept] ✅ Sipariş fişi başarılı');
          } catch (err) {
            console.error('🤖 [AutoAccept] ❌ Sipariş fişi HATA:', err);
            problems.push(`Sipariş fişi yazdırılamadı: ${err instanceof Error ? err.message : 'Bilinmeyen hata'}`);
          }
        } else {
          console.log('🤖 [AutoAccept] ℹ️ Sipariş fişi KAPALI (autoPrintReceiptOnAccept=false)');
        }
      } else {
        console.warn('🤖 [AutoAccept] ⚠️ orderId yok, yazdırma atlandı');
      }

      console.log('🤖 [AutoAccept] ═══════════════════════════════════════');
      console.log('🤖 [AutoAccept] KABUL VE YAZDIRMA TAMAMLANDI');
    } catch (err) {
      console.error('🤖 [AutoAccept] ❌ Beklenmedik hata (kabul+yazdırma):', err);
      problems.push(`Beklenmedik hata: ${err instanceof Error ? err.message : 'Bilinmeyen hata'}`);
    } finally {
      printFailureSinkRef.current = previousSink;

      if (problems.length > 0) {
        const platformLabel = (() => {
          if (trdOrder) return 'Uber Eats';
          const src = (order?.siparisKaynagi || '').toLowerCase();
          if (src.includes('getir')) return 'Getir Yemek';
          if (src.includes('migros')) return 'Migros Yemek';
          if (src.includes('yemeksepeti')) return 'Yemeksepeti';
          if (src.includes('kiosk')) return 'Kiosk';
          if (src.includes('ecommerce')) return 'Web Sitesi';
          return order?.siparisKaynagi?.replace('_', ' ') || 'QR Menü';
        })();

        const failure: AutoAcceptFailure = {
          // Aynı sipariş için ikinci bir uyarı üretilse de kuyrukta ayrı satır
          // olsun ve ses efekti yeniden tetiklensin diye zaman damgası ekli.
          key: `${activeQ.key}#${Date.now()}`,
          platformLabel,
          orderNo: trdOrder
            ? (trdOrder.orderNumber || trdOrder.orderCode || String(trdOrder.id))
            : (order?.siparisId ? String(order.siparisId) : '-'),
          customerName: trdOrder
            ? `${trdOrder.customer?.firstName || ''} ${trdOrder.customer?.lastName || ''}`.trim() || undefined
            : (order?.musteriAdi || order?.kullaniciAdi || undefined),
          totalAmount: trdOrder ? trdOrder.totalPrice : order?.toplamTutar,
          automatic: !manual,
          acceptFailed,
          problems,
          occurredAt: Date.now(),
        };

        console.error('🤖 [AutoAccept] ⛔ KABUL/YAZDIRMA BAŞARISIZ — kullanıcı uyarılıyor:', failure);
        setAutoAcceptFailures(prev => [...prev, failure]);
      }
    }
  }, [
    screenData,
    updateGetirOrderStatus, fetchGetirOrders,
    updateTrendyolOrderStatus,
    updateYemeksepetiOrderStatus, fetchYemeksepetiOrders,
    updateMigrosOrderStatus, fetchMigrosOrders,
    handleKitchenPrint, handleReceiptPrint,
  ]);

  // Aktif (modalda görünen) siparişi kabul et + yazdır + kuyruğu ilerlet.
  //
  // Çift yazdırma koruması İKİ katmanlıdır:
  //   (a) Modal tarafı: buton geri sayımı iptal eder (autoAcceptCalledRef) ve
  //       oto akış başlarken butonları kilitler (acceptAndPrintBusy).
  //   (b) Ekran tarafı (BURASI): sipariş anahtarı bazlı idempotency guard —
  //       aynı kuyruk siparişi için ikinci çağrı (oto geri sayım + manuel buton
  //       çakışması, çift tıklama, bildirimden zaten işlenmiş siparişin yarışla
  //       aktif olması) reddedilir. Set'ten kasıtlı olarak silinmez (redelivery
  //       fişi tekrar bastıramaz; unmount'ta temizlenir).
  const acceptAndPrintActiveOrder = useCallback(async (manual: boolean) => {
    // O3 — Snapshot aktif sipariş kuyruktan (ref ile, atomic setter sayesinde senkron).
    const activeQ = activeNewOrderRef.current;
    if (!activeQ) {
      console.warn('🤖 [AutoAccept] ⚠️ Aktif sipariş yok, kabul+yazdırma atlandı');
      completeActiveNewOrder();
      return;
    }

    if (processedAcceptKeysRef.current.has(activeQ.key)) {
      console.warn(`🤖 [AutoAccept] ⛔ DUPLICATE ENGELLENDİ (${manual ? 'MANUEL BUTON' : 'OTO GERİ SAYIM'}) — bu sipariş zaten işlendi/işleniyor:`, activeQ.key);
      // Sipariş bildirimden zaten işlendiyse modal kilitli kalmasın — kuyruğu ilerlet.
      completeActiveNewOrder();
      return;
    }
    processedAcceptKeysRef.current.add(activeQ.key);

    try {
      await performAcceptAndPrint(activeQ, manual, manual ? 'MANUEL BUTON' : 'OTO GERİ SAYIM');
    } finally {
      // O3 — KRİTİK: kuyruğun kilitlenmemesi için MUTLAKA çağır.
      // Hata fırlatılsa bile sıradaki sipariş için yer açılır.
      completeActiveNewOrder();
    }
  }, [activeNewOrderRef, completeActiveNewOrder, performAcceptAndPrint]);

  // Bildirim içindeki "Onayla ve Yazdır" — bekleyen sipariş modala hiç düşmeden
  // pending kuyruğundan işlenir. Kasiyer telefon siparişi girerken (QuickSale
  // açıkken) sağ üstte biriken platform siparişlerini bekletmeden sırayla
  // onaylayıp yazdırabilir; işlenen sipariş için NewOrderModal asla açılmaz.
  const handleAcceptAndPrintPending = useCallback(async (q: QueuedNewOrder) => {
    if (processedAcceptKeysRef.current.has(q.key)) {
      console.warn('🤖 [AutoAccept] ⛔ DUPLICATE ENGELLENDİ (BİLDİRİM BUTONU) — bu sipariş zaten işlendi/işleniyor:', q.key);
      return;
    }
    processedAcceptKeysRef.current.add(q.key);

    // Bildirimi ANINDA kuyruktan çıkar: çift tıklama fiziksel olarak
    // imkansızlaşır, sıradaki bildirim yukarı kayar ve reactive promotion bu
    // siparişi bir daha göremez. Onay + yazdırma arka planda devam eder.
    removePendingNewOrder(q.key);

    await performAcceptAndPrint(q, true, 'BİLDİRİM BUTONU');
  }, [performAcceptAndPrint, removePendingNewOrder]);

  // Oto kabul: geri sayım bittiğinde tetiklenir (yalnızca autoAcceptEnabled iken bağlanır)
  const handleAutoAcceptAndPrint = useCallback(
    () => acceptAndPrintActiveOrder(false),
    [acceptAndPrintActiveOrder],
  );

  // Manuel "Onayla ve Yazdır" butonu: oto ayarlardan bağımsız, herkes kullanabilir.
  // Modal butonu tıklandığında geri sayımı da iptal ettiği için oto sistem devreye girmez.
  const handleManualAcceptAndPrint = useCallback(
    () => acceptAndPrintActiveOrder(true),
    [acceptAndPrintActiveOrder],
  );

  // ✅ YENİ: Kasiyer giriş handler
  const handleCashierLogin = useCallback((cashierInfo: CashierInfo) => {
    console.log('[PackageOrderScreen] Kasiyer giris yapti:', cashierInfo.cashierName);
    setCurrentCashier(cashierInfo);
    setIsCashierLoginModalOpen(false);
  }, []);

  // ✅ YENİ: Kasiyer çıkış handler
  const handleCashierLogout = useCallback(() => {
    if (currentCashier) {
      console.log('[PackageOrderScreen] Kasiyer cikis yapti:', currentCashier.cashierName);
    }
    setCurrentCashier(null);
  }, [currentCashier]);

  // Payment Handlers (extracted to usePaymentHandlers hook) - useOrderStatusHandlers'dan ONCE cagrilmali
  // ✅ C1 fix: pavoP2pSelectOpen / restP2pReceiptSelectOpen state'leri artık
  // PackageOrderScreen tarafında (yukarıda) tanımlı; setter'ları hook'a parametre
  // olarak veriyoruz ki anyOtherModalOpen useMemo'sundan görünebilsinler.
  const {
    buildCashPaymentRequest,
    handleOpenPaymentModal, handleCashPayment, handlePosPayment, handleOtherPayment, handleMixedPayment,
    handleOnlinePaymentRecord, handleOnlinePaymentFromModal,
    handlePavoP2pPayment, openPavoP2pSelect,
    openRestP2pReceiptSelect, handleRestP2pOnlineReceipt,
  } = usePaymentHandlers({
    selectedOrderId,
    selectedOrder,
    companyId: companyId ?? null,
    companyNameCache,
    screenData,
    currentCashier,
    currentUserEmail: currentUser?.email || null,
    getirOrders,
    migrosOrders,
    yemeksepetiOrders,
    trendyolOrders,
    setSnackbar,
    setIsPaymentModalOpen,
    setIsProcessingPayment,
    setTrendyolOrders,
    loadPackageOrders,
    fetchGetirOrders,
    fetchMigrosOrders,
    fetchYemeksepetiOrders,
    refreshTrendyolOrders,
    allWarehouses: warehouses,
    seller, // ✅ PR2: backend resolver'dan gelen satıcı snapshot (UI/print için)
    setPavoP2pSelectOpen,          // ✅ C1 fix: external state setter
    setRestP2pReceiptSelectOpen,   // ✅ C1 fix: external state setter
  });

  // Order Status Handler (extracted to useOrderStatusHandlers hook)
  const { handleUpdateOrderStatus, forceConfirmGetirScheduledOrder } = useOrderStatusHandlers({
    companyId: companyId ?? null,
    screenData,
    currentUser,
    filteredOrders,
    selectedOrderSource,
    getirOrders,
    migrosOrders,
    trendyolOrders,
    yemeksepetiOrders,
    setSnackbar,
    loadPackageOrders,
    fetchMasaSiparisleri,
    fetchGetirOrders,
    fetchMigrosOrders,
    fetchYemeksepetiOrders,
    updateMasaSiparisiStatus,
    updateGetirOrderStatus,
    updateMigrosOrderStatus,
    updateYemeksepetiOrderStatus,
    updateTrendyolOrderStatus,
    setGetirCountdownMap,
    setSelectCourierModalOpen,
    setTrendyolCancelOrder,
    setTrendyolCancelModalOpen,
    setTrendyolOrders,
    refreshTrendyolOrders,
    handleOnlinePaymentRecord,
    onGetirScheduledBlock: (scheduledDate, orderId) => {
      setGetirScheduledBlockDate(scheduledDate);
      setGetirScheduledBlockOrderId(orderId);
      setGetirScheduledBlockOpen(true);
    },
    onGetirOrderCancelled: handleGetirOrderCancelled,
  });

  // Courier Handlers (extracted to useCourierHandlers hook)
  const { handleCourierSelect } = useCourierHandlers({
    selectedOrderId,
    selectedOrder,
    companyId: companyId ?? null,
    screenData,
    trendyolOrders,
    setTrendyolOrders,
    updateTrendyolOrderStatus,
    getirOrders,
    setGetirOrders,
    setMigrosOrders,
    refreshGetirToken,
    fetchGetirOrders,
    fetchYemeksepetiOrders,
    handleUpdateOrderStatus,
    updateGetirOrderStatus,
    setSnackbar,
    setSelectCourierModalOpen,
    reloadOrders: loadPackageOrders,
  });

  // ===================== Kurye paneli =====================
  // Birlikte atanabilecek siparişler: hazır, kuryesi olmayan, teslimatı bizde olan kaynaklar
  const courierCandidates = useMemo(() => {
    const deliverable = ['phone', 'getir', 'migros', 'yemeksepeti', 'trendyol'];
    return filteredOrders.filter((o) =>
      o.status === 'ready' && !o.courierName && deliverable.includes(o.source)
      && !(o.id === selectedOrder?.id && o.source === selectedOrder?.source));
  }, [filteredOrders, selectedOrder]);

  /**
   * Kurye uygulamasından ödeme alınabilsin diye "Ödeme yap"ın nakit isteği sunucuda saklanır.
   * Kurye teslimde Nakit / POS / Pavo / Karma seçince sunucu bu yükle faturayı keser.
   */
  const saveCourierPaymentDraft = useCallback(async (order: any) => {
    if (!order || order.isPaid || order.isOnlinePayment || String(order.paymentMethod || '').toUpperCase() === 'ONLINE') return;
    const settings = screenData?.paymentSettings;
    if (!settings) return;
    const posOptions = (settings.posOptions || []) as Array<{ label?: string; bankAccountId: string; subAccountId: string | null; bankAccountName: string | null }>;
    const isPavo = (p: { label?: string; bankAccountName: string | null }) =>
      (p.label || '').toUpperCase().includes('PAVO') || (p.bankAccountName || '').toUpperCase().includes('PAVO');
    const pos = posOptions.find((p) => !isPavo(p)) || posOptions[0];
    const pavo = posOptions.find(isPavo);
    const num = (v?: string | null) => (v ? parseInt(String(v), 10) || null : null);
    const payload = await buildCashPaymentRequest(order);
    await courierModuleService.savePaymentDraft({
      branchId: screenData?.branchId || null,
      orderSource: toApiSource(order.source),
      orderRef: Number(order.id),
      payload,
      cashboxId: num(settings.cashPaymentCashboxId),
      cashboxName: settings.cashPaymentCashboxName || null,
      posBankAccountId: num(pos?.bankAccountId),
      posBankSubAccountId: num(pos?.subAccountId),
      posBankAccountName: pos ? pos.bankAccountName || pos.label || null : null,
      pavoBankAccountId: num(pavo?.bankAccountId),
      pavoBankSubAccountId: num(pavo?.subAccountId),
      pavoBankAccountName: pavo ? pavo.bankAccountName || pavo.label || null : null,
    });
  }, [screenData, buildCashPaymentRequest]);

  const handlePanelAssign = useCallback(async (items: PanelAssignItem[]) => {
    // Sırayla: her atama bugünkü akıştan geçer (platform bildirimleri, kurye uygulaması)
    for (const it of items) {
      await handleCourierSelect(it.courier.id, it.courier.firebaseUid, it.courier.name || '', {
        order: it.order,
        partNo: it.partNo,
        partCount: it.partCount,
        partNote: it.partNote,
        collectsPayment: it.collectsPayment,
        zoneId: it.zoneId,
        feeOverride: it.feeOverride,
      });
    }
    // Fatura yükü arka planda saklanır; hata atamayı etkilemez (kasiyer Ödeme yap ile alır)
    const seen = new Set<string>();
    for (const it of items) {
      const key = `${toApiSource(it.order?.source)}:${it.order?.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      void saveCourierPaymentDraft(it.order).catch((e) => console.warn('[Kurye ödemesi] Fatura yükü saklanamadı:', e));
    }
  }, [handleCourierSelect, saveCourierPaymentDraft]);

  // F8: kurye paneli (seçili sipariş hazırsa atama, değilse takip)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'F8') return;
      e.preventDefault();
      if (selectedOrder && selectedOrder.status === 'ready' && !selectedOrder.courierName) {
        setSelectCourierModalOpen(true);
      } else {
        setCourierViewOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedOrder]);

  // Yemeksepeti siparişleri otomatik DELIVERED olduğunda online ödeme kaydı tetikle
  const prevYsOrdersRef = useRef<Map<number, string>>(new Map());
  useEffect(() => {
    if (!yemeksepetiOrders || yemeksepetiOrders.length === 0) return;

    const prevStatuses = prevYsOrdersRef.current;
    const newStatuses = new Map<number, string>();

    for (const ysOrder of yemeksepetiOrders) {
      const status = (ysOrder.status || '').toUpperCase();
      newStatuses.set(ysOrder.id, status);

      const isDelivered = status === 'DELIVERED' || status === 'COMPLETED';

      if (prevStatuses.size === 0) {
        if (isDelivered) {
          const converted = convertYemeksepetiOrderToPackageOrder(ysOrder);
          if (converted.isOnlinePayment && !converted.isPaid) {
            console.log('[ONLINE_ODEME] Yemeksepeti ilk yukleme - odenmemis teslim edilmis siparis tespit edildi (manuel ödeme bekleniyor):', { id: ysOrder.id });
          }
        }
      } else {
        const prevStatus = prevStatuses.get(ysOrder.id);
        const wasNotDelivered = prevStatus && prevStatus !== 'DELIVERED' && prevStatus !== 'COMPLETED';

        if (isDelivered && wasNotDelivered) {
          const converted = convertYemeksepetiOrderToPackageOrder(ysOrder);
          if (converted.isOnlinePayment && !converted.isPaid) {
            console.log('[ONLINE_ODEME] Yemeksepeti teslim algılandi - manuel ödeme bekleniyor (oto kayıt devre dışı):', { id: ysOrder.id });
          }
        }
      }
    }

    prevYsOrdersRef.current = newStatuses;
  }, [yemeksepetiOrders]);

  // Loading durumu
  if (loading) {
    return (
      <ScreenContainer>
        <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
          <CircularProgress />
        </Box>
      </ScreenContainer>
    );
  }

  // Hata durumu
  if (error || !screenData) {
    return (
      <ScreenContainer>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
          <Typography color="error">{error || 'Ekran bulunamadı'}</Typography>
          {isAdmin && (
            <Typography
              variant="body2"
              sx={{ color: themeColors.primary, cursor: 'pointer' }}
              onClick={() => navigate('/siparis-yonetimi')}
            >
              ← Geri Dön
            </Typography>
          )}
        </Box>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      {/* ================================================================== */}
      {/* HEADER */}
      {/* ================================================================== */}
      <HeaderBar>
        <Box display="flex" alignItems="center" gap={1}>
          {isAdmin && (
            <Tooltip title="Geri">
              <IconButton size="small" onClick={() => navigate('/siparis-yonetimi')}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          <LocalShippingIcon sx={{ color: themeColors.primary, fontSize: 24 }} />
          <Box>
            <Box display="flex" alignItems="baseline" gap={0.75}>
              <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.1}>
                {screenData.name}
              </Typography>
              {/* ✅ Sürüm numarası (package.json'dan otomatik) */}
              <Typography
                variant="caption"
                sx={{ fontSize: '0.65rem', color: 'text.disabled', fontWeight: 500, letterSpacing: 0.3 }}
              >
                v{APP_VERSION}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {branchName}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title={sseConnectionStatus === 'connected' ? 'Caller ID Bağlı' : 'Caller ID Bağlı Değil'}>
            <PhoneIcon sx={{ fontSize: 20, color: sseConnectionStatus === 'connected' ? '#4caf50' : '#f44336' }} />
          </Tooltip>
          <Tooltip title={signalRStatus === 'Connected' ? 'SignalR Bağlı' : signalRStatus === 'Reconnecting' ? 'SignalR Yeniden Bağlanıyor...' : 'SignalR Bağlı Değil'}>
            <CellTowerIcon sx={{ fontSize: 20, color: signalRStatus === 'Connected' ? '#2196f3' : signalRStatus === 'Reconnecting' ? '#ff9800' : '#f44336' }} />
          </Tooltip>
          {isTrendyolEnabled && (
            <Tooltip title={trendyolLocalServiceConnected ? 'Masaüstü Yazılımı Bağlı' : 'Masaüstü Yazılımı Bağlı Değil - Uber Eats siparişleri alınamıyor'}>
              <ComputerIcon
                sx={{
                  fontSize: 20,
                  color: trendyolLocalServiceConnected ? '#4caf50' : '#f44336',
                  cursor: trendyolLocalServiceConnected ? 'default' : 'pointer',
                }}
                onClick={!trendyolLocalServiceConnected ? () => setShowLocalServiceWarningModal(true) : undefined}
              />
            </Tooltip>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {/* ✅ Tümüne Dön: arayan/müşteri bağlamı aktifken görünür — telefon kapansa bile
              ekranda kalan arayan bilgisini temizleyip tüm siparişler görünümüne döner */}
          {(currentCustomer || customerPhoneFilter || customerNameFilter || isCallAnswered) && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleReturnToAll}
              sx={{
                fontWeight: 'bold',
                height: 32,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                mr: 0.5,
              }}
            >
              Tümüne Dön
            </Button>
          )}
          {/* Tarih Filtresi (Son 7 gun, bugun haric) — sadece ikon */}
          <Tooltip
            title={
              selectedHistoricDate
                ? `Tarih filtresi aktif: ${formatHistoricDateLabel(selectedHistoricDate)}`
                : 'Tarih filtresi (son 7 gün)'
            }
          >
            <IconButton
              size="small"
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              sx={{
                mr: 0.5,
                width: 40,
                height: 40,
                bgcolor: selectedHistoricDate ? 'warning.main' : 'transparent',
                color: selectedHistoricDate ? 'white' : 'action.active',
                border: selectedHistoricDate ? 'none' : '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: selectedHistoricDate ? 'warning.dark' : 'action.hover',
                },
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={dateMenuAnchor}
            open={Boolean(dateMenuAnchor)}
            onClose={() => setDateMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
          >
            {selectedHistoricDate && (
              <MenuItem
                onClick={() => {
                  setSelectedHistoricDate(null);
                  setDateMenuAnchor(null);
                }}
                sx={{
                  py: 1,
                  color: 'error.main',
                  fontWeight: 600,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <CloseIcon sx={{ fontSize: 18, mr: 1 }} />
                Filtreyi Temizle (Bugün)
              </MenuItem>
            )}
            {last7Days.map((d) => {
              const dateStr = toLocalDateString(d).split('-').reverse().join('.');
              const weekday = d.toLocaleDateString('tr-TR', { weekday: 'long' });
              const weekdayTr = weekday.charAt(0).toLocaleUpperCase('tr-TR') + weekday.slice(1);
              const isActive = selectedHistoricDate && toLocalDateString(d) === toLocalDateString(selectedHistoricDate);
              return (
                <MenuItem
                  key={dateStr}
                  selected={!!isActive}
                  onClick={() => {
                    setSelectedHistoricDate(d);
                    setDateMenuAnchor(null);
                  }}
                  sx={{
                    py: 1,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 'none' },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255, 152, 0, 0.18)',
                      '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.28)' },
                    },
                  }}
                >
                  <Box sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{dateStr}</Box>
                  <Box sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{weekdayTr}</Box>
                </MenuItem>
              );
            })}
          </Menu>
          {/* Kasiyer Sistemi Bölümü */}
          {screenData?.useCashierSystem && (
            currentCashier ? (
              // Kasiyer giriş yapmış - Adını ve çıkış butonunu göster
              <Chip
                icon={<PersonIcon sx={{ fontSize: '1rem !important', color: 'white !important' }} />}
                label={currentCashier.cashierName}
                variant="filled"
                onClick={handleCashierLogout}
                sx={{
                  bgcolor: 'success.main',
                  color: 'white',
                  fontWeight: 'bold',
                  height: 32,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'success.dark' },
                  '& .MuiChip-deleteIcon': { color: 'white' },
                  mr: 1,
                }}
                deleteIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
                onDelete={handleCashierLogout}
              />
            ) : (
              // Kasiyer giriş yapmamış - Giriş butonu göster
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={<PersonIcon />}
                onClick={() => setIsCashierLoginModalOpen(true)}
                sx={{
                  mr: 1,
                  fontWeight: 'bold',
                  height: 32,
                  textTransform: 'none',
                }}
              >
                Kasiyer Girişi
              </Button>
            )
          )}
          {/* Pavo Sipariş Takip Butonu */}
          {activePavoPayments.length > 0 && (
            <Tooltip title="Pavo Sipariş Takip">
              <IconButton
                onClick={() => setPavoTrackingOpen(true)}
                sx={{
                  bgcolor: '#7c4dff',
                  color: 'white',
                  width: 40,
                  height: 40,
                  mr: 0.5,
                  position: 'relative',
                  '&:hover': { bgcolor: '#6a3de0' },
                }}
              >
                <PointOfSaleIcon />
                <Box sx={{ position: 'absolute', top: -4, right: -4, bgcolor: '#ff5722', color: '#fff', borderRadius: '50%', minWidth: 18, height: 18, px: 0.5, fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                  {activePavoPayments.length}
                </Box>
              </IconButton>
            </Tooltip>
          )}
          {/* Yenile Butonu - Dokunmatik ekranlar için büyük */}
          <Tooltip title="Siparişleri Yenile">
            <IconButton
              onClick={loadPackageOrders}
              sx={{
                bgcolor: themeColors.primary,
                color: 'white',
                width: 40,
                height: 40,
                '&:hover': {
                  bgcolor: themeColors.secondary,
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
            <IconButton size="small" onClick={toggleFullscreen}>
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Platform Ürün Kategorileri">
            <IconButton size="small" onClick={() => setCategoryManagementModalOpen(true)} sx={{ position: 'relative' }}>
              <CategoryIcon />
              {platformCategoryMappings.length > 0 && (
                <Box sx={{ position: 'absolute', top: -2, right: -2, bgcolor: 'success.main', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                  {platformCategoryMappings.length}
                </Box>
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Ayarlar">
            <IconButton size="small" onClick={() => setSettingsModalOpen(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          {/* Rehber düğmesi: satış akışını bozmasın diye klavye odağı almaz (tabIndex -1); dokunmatik için 40px */}
          <GuideButton guideKey="sales.packageOrderScreen" size="small" tabIndex={-1} sx={{ width: 40, height: 40 }} />
          <Tooltip title="Kullanıcı Menüsü">
            <IconButton size="small" onClick={handleOpenUserMenu}>
              <AccountCircleIcon sx={{ color: themeColors.primary }} />
            </IconButton>
          </Tooltip>
        </Box>
      </HeaderBar>

      {/* ================================================================== */}
      {/* TARIH FILTRESI UYARI BANNER'I (sadece historic mod aktifken) */}
      {/* ================================================================== */}
      {selectedHistoricDate && (
        <Alert
          severity="warning"
          icon={<CalendarTodayIcon fontSize="inherit" />}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<CloseIcon />}
              onClick={() => setSelectedHistoricDate(null)}
              sx={{ fontWeight: 'bold' }}
            >
              Filtreyi Temizle
            </Button>
          }
          sx={{
            borderRadius: 0,
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'warning.light',
            fontWeight: 600,
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center', gap: 1 },
          }}
        >
          <strong>Tarih Filtresi Aktif —</strong>
          <Box component="span" sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            bgcolor: 'warning.dark',
            color: 'warning.contrastText',
            borderRadius: 1,
            fontWeight: 700,
            fontSize: '0.95rem',
          }}>
            {formatHistoricDateLabel(selectedHistoricDate)}
          </Box>
          <span>
            tarihindeki siparisler gosteriliyor. Tum kanallar bu gun icin server-side
            filtrelenmistir. Canli guncellemeler bu modda gorunmez (arka planda akmaya devam eder).
          </span>
          {isLoadingHistoric && (
            <CircularProgress size={16} sx={{ ml: 1 }} />
          )}
        </Alert>
      )}

      {/* ================================================================== */}
      {/* KULLANICI MENÜSÜ */}
      {/* ================================================================== */}
      <Menu
        anchorEl={anchorElUser}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {userDisplayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentUser?.email}
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {isAdmin && (
          <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/siparis-yonetimi'); }}>
            <ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">{t('orderManagement.screen.userMenu.backToManagement')}</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <LogoutIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          <Typography variant="body2" color="error.main">{t('orderManagement.screen.userMenu.logout')}</Typography>
        </MenuItem>
      </Menu>

      {/* ================================================================== */}
      {/* MAIN CONTENT - 3 SÜTUN */}
      {/* ================================================================== */}
      <MainContent>
        {/* -------------------------------------------------------------- */}
        {/* SOL SÜTUN - CALLER ID / MÜŞTERİ */}
        {/* -------------------------------------------------------------- */}
        <LeftPanel
          incomingCall={incomingCall}
          isCallAnswered={isCallAnswered}
          onAnswerCall={answerCall}
          onEndCall={handleEndCallComplete}
          currentCustomer={currentCustomer}
          selectedAddressId={selectedAddressId}
          onSelectAddress={setSelectedAddressId}
          onAddCustomer={(phone) => {
            setNewCustomerPhone(phone);
                    setAddCustomerModalOpen(true);
                  }}
          onAddAddress={() => setAddAddressModalOpen(true)}
          recentCalls={recentCalls}
          customerPhoneFilter={customerPhoneFilter}
          onSelectRecentCall={handleSelectRecentCall}
          onShowAllCalls={() => setAllCallsModalOpen(true)}
          onViewAddress={(addr) => {
            setSelectedAddressForDetail(addr);
            setAddressDetailModalOpen(true);
          }}
          themeColors={themeColors}
        />

        {/* -------------------------------------------------------------- */}
        {/* ORTA SÜTUN - SİPARİŞLER */}
        {/* -------------------------------------------------------------- */}
        <CenterColumn>
          {/* Tab'lar */}
          <Box
            sx={{
              p: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              justifyContent: 'center',
            }}
          >
            {orderTabs.map((tab) => {
              // Platform sekmesi üç durumlu: hepsi açık, kısmen açık (bir marka
              // kapalı), hepsi kapalı. Kısmi durumda sipariş akmaya devam ettiği
              // için sekme tıklanabilir kalır, yalnızca uyarı rengiyle işaretlenir.
              const openState: PlatformOpenState = PLATFORM_TAB_IDS.includes(tab.id)
                ? getPlatformOpenState(tab.id)
                : 'open';
              const isAllClosed = openState === 'closed';
              const isPartiallyClosed = openState === 'partial';
              const closedStoreNames = isPartiallyClosed ? getClosedStoreNames(tab.id) : [];
              const stateColor = isAllClosed ? '#d32f2f' : isPartiallyClosed ? '#ed6c02' : null;

              const tabButton = (
              <Button
                key={tab.id}
                variant={selectedTab === tab.id ? 'contained' : 'outlined'}
                size="small"
                  onClick={() => {
                    if (isAllClosed) {
                      if (tab.id === 'migros') { setMigrosRestaurantClosedModalOpen(true); return; }
                      if (tab.id === 'yemeksepeti') { setYsVendorClosedModalOpen(true); return; }
                      if (tab.id === 'trendyol') { setTrendyolVendorClosedModalOpen(true); return; }
                    }
                    setSelectedTab(tab.id);
                  }}
                startIcon={tab.icon}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: selectedTab === tab.id ? 'bold' : 'normal',
                    bgcolor: stateColor
                      ? stateColor
                      : selectedTab === tab.id
                        ? themeColors.primary
                        : 'transparent',
                    color: stateColor
                      ? 'white'
                      : selectedTab === tab.id
                        ? 'white'
                        : 'text.secondary',
                    borderColor: stateColor
                      ? stateColor
                      : selectedTab === tab.id
                        ? themeColors.primary
                        : 'divider',
                  '&:hover': {
                      bgcolor: isAllClosed
                        ? '#b71c1c'
                        : isPartiallyClosed
                          ? '#c65a00'
                          : selectedTab === tab.id
                            ? themeColors.primary
                            : 'action.hover',
                      borderColor: stateColor || themeColors.primary,
                  },
                }}
              >
                {tab.label}
              </Button>
              );

              if (!isPartiallyClosed || closedStoreNames.length === 0) return tabButton;

              return (
                <Tooltip
                  key={tab.id}
                  title={`${t('orderManagement.screen.storeStatus.partiallyOpen')} — ${t('orderManagement.screen.storeStatus.closedStores', { stores: closedStoreNames.join(', ') })}`}
                  arrow
                >
                  <span>{tabButton}</span>
                </Tooltip>
              );
            })}
          </Box>

          {/* Marka alt sekmeleri — yalnız platform sekmesinde, o platformda birden fazla mağaza varsa */}
          {brandTabs.length > 0 && (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              <StorefrontIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
              <Chip
                label={`${t('orderManagement.screen.storeFilter.all')} ${tabOrders.length}`}
                size="small"
                onClick={() => setStoreFilter('all')}
                variant={storeFilter === 'all' ? 'filled' : 'outlined'}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: storeFilter === 'all' ? themeColors.primary : 'transparent',
                  color: storeFilter === 'all' ? 'white' : 'text.secondary',
                }}
              />
              {brandTabs.map(({ label, count }) => (
                <Tooltip key={label} title={label}>
                  <Chip
                    label={(
                      // Uzun marka adı kısalır, sayı her zaman görünür
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
                        <Box component="span" sx={{ fontWeight: 700, flex: 'none' }}>{count}</Box>
                      </Box>
                    )}
                    size="small"
                    onClick={() => setStoreFilter(label)}
                    variant={storeFilter === label ? 'filled' : 'outlined'}
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      maxWidth: 240,
                      bgcolor: storeFilter === label ? themeColors.primary : 'transparent',
                      color: storeFilter === label ? 'white' : 'text.secondary',
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          )}

          {/* Müşteri Filtresi Bilgi Çubuğu */}
          {customerPhoneFilter && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mx: 0.5,
                px: 1.5,
                py: 0.75,
                bgcolor: `${themeColors.primary}15`,
                borderRadius: 1,
                border: `1px solid ${themeColors.primary}40`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <PersonIcon sx={{ fontSize: 18, color: themeColors.primary }} />
                <Typography variant="body2" fontWeight="medium">
                  <strong>{customerNameFilter}</strong> müşterisinin siparişleri
                </Typography>
                <Chip 
                  label={`Bugün: ${filteredOrders.filter(o => o.orderDate === TODAY).length}`} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    bgcolor: 'success.main',
                    color: 'white',
                  }} 
                />
                <Chip 
                  label={`Geçmiş: ${filteredOrders.filter(o => o.orderDate !== TODAY).length}`} 
                  size="small" 
                  sx={{ 
                    height: 20, 
                    fontSize: '0.7rem',
                    bgcolor: 'grey.500',
                    color: 'white',
                  }} 
                />
                {/* Müşteri Detay Butonu */}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PersonIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    // Seçili telefon numarasına ait aramayı bul
                    const selectedCall = recentCalls.find(c => c.phone === customerPhoneFilter);
                    if (selectedCall) {
                      setSelectedCallForDetail(selectedCall);
                      setCustomerDetailModalOpen(true);
                    }
                  }}
                  sx={{
                    fontSize: '0.7rem',
                    py: 0.25,
                    px: 1,
                    bgcolor: themeColors.primary,
                    '&:hover': { bgcolor: themeColors.primary, filter: 'brightness(0.9)' },
                  }}
                >
                  Müşteri Bilgileri
                </Button>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  // ✅ Sipariş filtresini temizle
                  setCustomerPhoneFilter(null);
                  setCustomerNameFilter(null);
                  // ✅ Müşteri ve adres bölümünü de temizle
                  setCurrentCustomer(null);
                  setSelectedAddressId(null);
                  // ✅ isCallAnswered'ı da sıfırla (Yeni Sipariş butonu tekrar aktif olsun)
                  setIsCallAnswered(false);
                }}
                sx={{
                  fontSize: '0.75rem',
                  py: 0.25,
                  px: 1.5,
                  borderColor: themeColors.primary,
                  color: themeColors.primary,
                  '&:hover': {
                    bgcolor: `${themeColors.primary}10`,
                    borderColor: themeColors.primary,
                  },
                }}
              >
                ✕ Tümüne Dön
              </Button>
            </Box>
          )}

          {/* Siparişler Tablosu */}
          <OrdersTable
            orders={filteredOrders}
            selectedOrderId={selectedOrderId}
            selectedOrderSource={selectedOrderSource}
            onSelectOrder={handleSelectOrder}
            customerPhoneFilter={customerPhoneFilter}
            statusFilter={statusFilter}
            onStatusFilterClick={(e) => setStatusFilterAnchor(e.currentTarget)}
            onMenuOpen={handleMenuOpen}
            incomingCall={incomingCall}
            themeColors={themeColors}
            getUnmappedCount={getUnmappedCount}
            onUnmappedBadgeClick={handleUnmappedBadgeClick}
            isPavoPaid={isOrderPavoPaid}
            getCourierCollection={getCourierCollection}
            showStoreColumn={showStoreColumn}
            getStoreLabel={getOrderStoreLabel}
          />
        </CenterColumn>

        {/* -------------------------------------------------------------- */}
        {/* SAĞ SÜTUN - İŞLEMLER / BUTONLAR */}
        {/* -------------------------------------------------------------- */}
        <RightPanel
          currentCustomer={currentCustomer}
          incomingCall={incomingCall}
          isCallAnswered={isCallAnswered}
          selectedAddressId={selectedAddressId}
          selectedOrderId={selectedOrderId}
          selectedOrder={selectedOrder}
          activeOrderInfo={activeOrderInfo as any}
          getStatusButtonInfo={getStatusButtonInfo}
          useCashierSystem={screenData?.useCashierSystem || false}
          currentCashier={currentCashier}
          onOpenQuickSale={() => {
            setAddOnOrderId(null); // Yeni sipariş modu
                  setQuickSaleModalOpen(true);
                }}
          onOpenAddOnOrder={(orderId) => {
            console.log('📦 [İlave Sipariş] Modal açılıyor, OrderId:', orderId);
            setAddOnOrderId(orderId); // İlave sipariş modu
                  setQuickSaleModalOpen(true);
                }}
          onOpenManualPhone={() => {
            setManualPhoneNumber('');
            setIsManualPhoneModalOpen(true);
          }}
          onOpenCashierLogin={() => setIsCashierLoginModalOpen(true)}
          onOpenDetail={handleOpenDetail}
          onOpenPaymentModal={handleOpenPaymentModal}
          onKitchenPrint={handleKitchenPrint}
          onUpdateOrderStatus={handleUpdateOrderStatus}
          onOpenCourierModal={() => setSelectCourierModalOpen(true)}
          onOpenCourierPanel={() => setCourierViewOpen(true)}
          onRejectMigrosOrder={(orderId) => {
            setMigrosRejectOrderId(orderId);
            // Modal, reddet/iptal ayrımını NEW_PENDING'e göre yapıyor; ham statü
            // yerine kanonik değer gönderilir (COLLECTING = onaylanmış → iptal).
            setMigrosRejectOrderStatus(normalizeMigrosStatus(selectedOrder?.migrosStatus));
            setMigrosRejectModalOpen(true);
          }}
          onRejectYsOrder={(orderId) => {
            setYsRejectOrderId(orderId);
            setYsRejectModalOpen(true);
          }}
          onRejectTrendyolOrder={(orderId) => {
            const trdOrder = trendyolOrders.find(o => {
              const converted = convertTrendyolOrderToPackageOrder(o);
              return converted.id === orderId;
            });
            if (trdOrder) {
              setTrendyolCancelOrder(trdOrder);
              setTrendyolCancelModalOpen(true);
            }
          }}
          selectedTab={selectedTab}
          platformStatuses={{
            // "En az bir mağaza açık" ölçütü: kısmi durumda da kapatma düğmesi
            // görünmeli, yoksa açık kalan markayı kapatmanın yolu kalmıyor.
            getirOpen: false,
            migrosOpen: isAnyStoreOpen('migros', migrosRestaurantOpen),
            ysOpen: isAnyStoreOpen('yemeksepeti', yemeksepetiVendorOpen),
            trendyolOpen: isAnyStoreOpen('trendyol', trendyolVendorStatus === 'OPEN'),
          }}
          onCloseRestaurant={handleOpenCloseRestaurantModal}
          onPaymentVerification={() => setVerificationModalOpen(true)}
          themeColors={themeColors}
        />
      </MainContent>

      {/* ================================================================== */}
      {/* FOOTER - GÜNLÜK TOPLAMLAR */}
      {/* ================================================================== */}
      <FooterBar>
        <Box display="flex" alignItems="center" gap={1}>
          <img 
            src={BRAND.logos.dark} 
            alt={`${BRAND.name} Logo`} 
            style={{ height: 28, width: 'auto' }} 
          />
          <Typography variant="body2" fontWeight="medium">
            📅 {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Typography variant="caption">📞 Tel: {footerStats.phone}</Typography>
          <Typography variant="caption">🍽️ Y.Sepeti: {footerStats.yemeksepeti}</Typography>
          <Typography variant="caption">🛵 Trendyol: {footerStats.trendyol}</Typography>
          <Typography variant="caption">🏪 Migros: {footerStats.migros}</Typography>
          <Typography variant="caption">📱 Kiosk: {footerStats.kiosk}</Typography>
          <Typography variant="caption">📲 QR: {footerStats.qr}</Typography>
          <Typography variant="caption">🛍️ E-Tic: {footerStats.ecommerce}</Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{ 
              bgcolor: themeColors.primary, 
              color: 'white', 
              px: 2, 
              py: 0.5, 
              borderRadius: 1 
            }}
          >
            TOPLAM: {footerStats.total} Adet
          </Typography>
        </Box>
      </FooterBar>

      {/* ================================================================== */}
      {/* CONTEXT MENU */}
      {/* ================================================================== */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => { handleMenuClose(); handleOpenDetail(); }}>
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Detay Görüntüle</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            handleMenuClose(); 
            if (menuOrderId) handleKitchenPrint(menuOrderId, menuOrderSource || undefined); 
          }}
        >
          <ListItemIcon><RestaurantMenuIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Mutfağa Yazdır</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { 
            handleMenuClose(); 
            if (menuOrderId) handleReceiptPrint(menuOrderId); 
          }}
        >
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Fiş Yazdır</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>İptal Et</ListItemText>
        </MenuItem>
      </Menu>

      {/* ================================================================== */}
      {/* STATUS FILTER MENU */}
      {/* ================================================================== */}
      <Menu
        anchorEl={statusFilterAnchor}
        open={Boolean(statusFilterAnchor)}
        onClose={() => setStatusFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem 
          onClick={() => { setStatusFilter('all'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'all'}
          sx={{ minWidth: 150 }}
        >
          <ListItemText>🔄 Tümü</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => { setStatusFilter('new'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'new'}
        >
          <ListItemText>🟡 Yeni</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('accepted'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'accepted'}
        >
          <ListItemText>✅ Onaylandı</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('preparing'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'preparing'}
        >
          <ListItemText>👨‍🍳 Hazırlanıyor</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('ready'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'ready'}
        >
          <ListItemText>🟢 Teslime Hazır</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('onway'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'onway'}
        >
          <ListItemText>🔵 Yolda</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('delivered'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'delivered'}
        >
          <ListItemText>✅ Teslim Edildi</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('undelivered'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'undelivered'}
        >
          <ListItemText>⚠️ Teslim Edilemedi</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => { setStatusFilter('cancelled'); setStatusFilterAnchor(null); }}
          selected={statusFilter === 'cancelled'}
        >
          <ListItemText>❌ İptal</ListItemText>
        </MenuItem>
      </Menu>

      {/* Kurye paneli: "Kurye Ata" ile atama, "Kuryeler" / F8 ile takip */}
      <CourierPanel
        open={selectCourierModalOpen || courierViewOpen}
        mode={selectCourierModalOpen ? 'assign' : 'view'}
        branchId={screenData?.branchId}
        primaryOrder={selectCourierModalOpen ? selectedOrder : null}
        candidateOrders={courierCandidates}
        onClose={() => {
          setSelectCourierModalOpen(false);
          setCourierViewOpen(false);
        }}
        onAssign={handlePanelAssign}
      />

      <PackageOrderModals
        themeColors={themeColors as any}
        screenData={screenData}
        companyId={companyId}
        orders={orders}
        selectedOrder={selectedOrder}
        selectedOrderId={selectedOrderId}
        currentCustomer={currentCustomer}
        setCurrentCustomer={setCurrentCustomer}
        incomingCall={incomingCall}
        isCallAnswered={isCallAnswered}
        setIsCallAnswered={setIsCallAnswered}
        updateActiveCallCustomerName={updateActiveCallCustomerName}
        customerPhoneFilter={customerPhoneFilter}
        setCustomerPhoneFilter={setCustomerPhoneFilter}
        customerNameFilter={customerNameFilter}
        setCustomerNameFilter={setCustomerNameFilter}
        selectedAddressId={selectedAddressId}
        setSelectedAddressId={setSelectedAddressId}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
        detailModalOpen={detailModalOpen}
        handleCloseDetail={handleCloseDetail}
        handleKitchenPrint={handleKitchenPrint}
        handleReceiptPrint={handleReceiptPrint}
        addCustomerModalOpen={addCustomerModalOpen}
        setAddCustomerModalOpen={setAddCustomerModalOpen}
        newCustomerPhone={newCustomerPhone}
        addAddressModalOpen={addAddressModalOpen}
        setAddAddressModalOpen={setAddAddressModalOpen}
        handleAddressAdded={handleAddressAdded}
        customerDetailModalOpen={customerDetailModalOpen}
        setCustomerDetailModalOpen={setCustomerDetailModalOpen}
        selectedCallForDetail={selectedCallForDetail}
        setSelectedCallForDetail={setSelectedCallForDetail}
        allCallsModalOpen={allCallsModalOpen}
        setAllCallsModalOpen={setAllCallsModalOpen}
        handleSelectRecentCall={handleSelectRecentCall}
        settingsModalOpen={settingsModalOpen}
        setSettingsModalOpen={setSettingsModalOpen}
        phoneRingSoundEnabled={phoneRingSoundEnabled}
        setPhoneRingSoundEnabled={setPhoneRingSoundEnabled}
        selectedRingSound={selectedRingSound}
        setSelectedRingSound={setSelectedRingSound}
        ringSoundOptions={ringSoundOptions}
        playRingSound={playRingSound}
        stopRingSound={stopRingSound}
        testNewOrderSound={testNewOrderSound}
        incomingCallModalEnabled={incomingCallModalEnabled}
        setIncomingCallModalEnabled={setIncomingCallModalEnabled}
        settingsScreenId={screenData?.id || null}
        settingsLocalServiceUrl={screenData?.localServiceUrl || null}
        onPrintingSettingsSaved={() => {
          setScreenDataRefreshKey(prev => prev + 1);
        }}
        incomingCallPopupOpen={incomingCallPopupOpen}
        answerCall={answerCall}
        endCallUnanswered={endCallUnanswered}
        anyModalOpen={anyModalOpen}
        activeCall={activeCall}
        pendingCalls={pendingCalls}
        onRequestCallDecision={handleRequestCallDecision}
        onPackageOrderFlowCompleted={handleOrderFlowCompleted}
        quickSaleModalOpen={quickSaleModalOpen}
        setQuickSaleModalOpen={setQuickSaleModalOpen}
        quickSaleOpenSettings={quickSaleOpenSettings}
        setQuickSaleOpenSettings={setQuickSaleOpenSettings}
        quickSaleOpenProductSort={quickSaleOpenProductSort}
        setQuickSaleOpenProductSort={setQuickSaleOpenProductSort}
        addOnOrderId={addOnOrderId}
        setAddOnOrderId={setAddOnOrderId}
        currentCashier={currentCashier}
        loadPackageOrders={loadPackageOrders}
        isPaymentModalOpen={isPaymentModalOpen}
        setIsPaymentModalOpen={setIsPaymentModalOpen}
        isProcessingPayment={isProcessingPayment}
        handleCashPayment={handleCashPayment}
        handlePosPayment={handlePosPayment}
        handleMixedPayment={handleMixedPayment}
        handleOtherPayment={handleOtherPayment}
        handleOnlinePaymentFromModal={handleOnlinePaymentFromModal}
        handlePavoP2pPayment={openPavoP2pSelect}
        hasActivePavoPayment={hasActivePavoPaymentForSelected}
        onOpenPavoTracking={() => { setIsPaymentModalOpen(false); setPavoTrackingOpen(true); }}
        cancelPhoneOrderAllowed={cancelPhoneOrderPermissionForSelected.allowed}
        cancelPhoneOrderDisabledReasonI18nKey={
          !cancelPhoneOrderPermissionForSelected.allowed && cancelPhoneOrderPermissionForSelected.reason
            ? REASON_I18N_KEY[cancelPhoneOrderPermissionForSelected.reason]
            : undefined
        }
        onRequestCancelPhoneOrder={handleRequestCancelPhoneOrder}
        cancelPhoneOrderModalOpen={cancelPhoneOrderModalOpen}
        onCancelPhoneOrderClose={handleCloseCancelPhoneOrderModal}
        cancelPhoneOrderTargetOrderNo={cancelPhoneOrderTargetOrder?.shortOrderNo || cancelPhoneOrderTargetOrder?.id || ''}
        onCancelPhoneOrderConfirmed={handleCancelPhoneOrderConfirmed}
        editCartAllowed={editCartPermissionForSelected.allowed}
        editCartDisabledReasonI18nKey={
          !editCartPermissionForSelected.allowed && editCartPermissionForSelected.reason
            ? REASON_I18N_KEY[editCartPermissionForSelected.reason]
            : undefined
        }
        onRequestEditCart={handleRequestEditCart}
        editCartModalOpen={editCartModalOpen}
        onEditCartClose={handleCloseEditCartModal}
        editCartTargetOrderId={editCartTargetOrder?.id ?? null}
        editCartTargetOrderShortNo={editCartTargetOrder?.shortOrderNo ?? null}
        editCartTargetCustomerName={editCartTargetOrder?.customerName ?? null}
        onEditCartMutated={handleEditCartMutated}
        onEditCartAddNewItem={handleEditCartAddNewItem}
        orderDetailRefreshKey={orderDetailRefreshKey}
        onRequestOnlineReceipt={(orderId, source) => {
          // Detay modallari `(orderId, source)` ile cagiriyor; biz aktif (orders)
          // ve gecmis (pastOrders) listelerinden ilgili order'i bulup REST-P2P
          // cihaz secim modalini aciyoruz. Bulunamazsa kullaniciyi bilgilendir.
          //
          // Matching strategy:
          //   1) source ESLESEN + id (string-coerce) ESLESEN  → en güvenli
          //   2) source eslesmiyor ama id eslesiyor             → migration/tip
          //      tutarsizligi durumunda fallback (ornek: 'getir' vs 'GETIR')
          //   3) sadece source eslesen tek kayit varsa o      → son care
          //
          // Her adim console.log ile detayli rapor verir; kullanici kopyalayarak
          // tani koyabilir.
          const idStr = String(orderId);
          const srcLower = String(source || '').toLowerCase();
          console.group('[OnlineReceipt] onRequestOnlineReceipt cagrildi');
          console.log('Gelen parametreler:', {
            orderId, orderIdType: typeof orderId, idStr,
            source, srcLower,
          });
          console.log('orders.length =', orders.length, 'pastOrders.length =', pastOrders.length);
          console.log('selectedOrder snapshot:', selectedOrder ? {
            id: selectedOrder.id, idType: typeof selectedOrder.id,
            source: selectedOrder.source, shortOrderNo: selectedOrder.shortOrderNo,
            totalAmount: selectedOrder.totalAmount,
          } : null);
          // Aktif siparislerin ozet listesi (ilk 10) — JSON.stringify ile collapse engellenir
          try {
            const ordersSummary = orders.slice(0, 10).map((o: PackageOrder) => ({
              id: o.id, idType: typeof o.id, source: o.source, shortOrderNo: o.shortOrderNo,
            }));
            console.log('orders ozet JSON:', JSON.stringify(ordersSummary));
          } catch { /* noop */ }
          if (pastOrders.length > 0) {
            try {
              const pastSummary = pastOrders.slice(0, 5).map((o: PackageOrder) => ({
                id: o.id, idType: typeof o.id, source: o.source, shortOrderNo: o.shortOrderNo,
              }));
              console.log('pastOrders ozet JSON:', JSON.stringify(pastSummary));
            } catch { /* noop */ }
          }

          // 0) En guvenli: modal acikken selectedOrder zaten dogru kayit
          //    selectedOrder.id ile gelen orderId ayni ise dogrudan onu kullan
          let found: PackageOrder | undefined;
          if (selectedOrder && String(selectedOrder.id) === idStr) {
            found = selectedOrder;
            console.log('[SELECTED MATCH] selectedOrder kullanildi:', {
              id: found.id, source: found.source, shortOrderNo: found.shortOrderNo,
            });
          }

          // 1) Strict: source + id (string-coerce)
          if (!found) {
            found =
              orders.find((o: PackageOrder) =>
                String(o.source || '').toLowerCase() === srcLower && String(o.id) === idStr,
              ) ||
              pastOrders.find((o: PackageOrder) =>
                String(o.source || '').toLowerCase() === srcLower && String(o.id) === idStr,
              );
            if (found) {
              console.log('[STRICT MATCH] order bulundu:', {
                id: found.id, source: found.source, shortOrderNo: found.shortOrderNo,
                totalAmount: found.totalAmount,
              });
            }
          }

          // 2) Fallback: sadece id (source mismatch toleransi)
          if (!found) {
            found =
              orders.find((o: PackageOrder) => String(o.id) === idStr) ||
              pastOrders.find((o: PackageOrder) => String(o.id) === idStr);
            if (found) {
              console.warn('[ID-ONLY MATCH] source eslesmedi ama id ile bulundu:', {
                expectedSource: srcLower, actualSource: found.source, id: found.id,
              });
            }
          }

          // 3) Son care: source + shortOrderNo eslesmesi (id tipi tutarsizliklarinda).
          //    Ayni ShortCode iki markada tekrar edebildigi icin eslesmeye marka
          //    kosulu eklenir; marka bilgisi yoksa eslesme yapilmaz — yanlis
          //    siparise fis basmak, fis basmamaktan kotudur.
          if (!found && selectedOrder?.shortOrderNo) {
            const shortNo = selectedOrder.shortOrderNo;
            const expectedIntegrationId = selectedOrder.platformIntegrationId;
            const sameStore = (o: PackageOrder) =>
              !expectedIntegrationId || o.platformIntegrationId === expectedIntegrationId;
            const matches = [...orders, ...pastOrders].filter((o: PackageOrder) =>
              String(o.source || '').toLowerCase() === srcLower &&
              o.shortOrderNo === shortNo &&
              sameStore(o),
            );
            if (matches.length === 1) {
              found = matches[0];
              console.warn('[SHORTNO MATCH] shortOrderNo ile bulundu:', {
                shortOrderNo: shortNo, id: found.id, source: found.source,
                platformIntegrationId: found.platformIntegrationId,
              });
            } else if (matches.length > 1) {
              console.error('[SHORTNO MATCH] Ayni ShortCode birden fazla markada bulundu, eslesme yapilmadi:', {
                shortOrderNo: shortNo,
                adaylar: matches.map(m => ({ id: m.id, storeName: m.storeName, integrationId: m.platformIntegrationId })),
              });
            }
          }

          if (found) {
            console.log('[OnlineReceipt] openRestP2pReceiptSelect cagriliyor');
            console.groupEnd();
            openRestP2pReceiptSelect(found);
          } else {
            console.error('[OnlineReceipt] HICBIR EŞLESME YOK', {
              orderId, source, idStr, srcLower,
              ordersCount: orders.length, pastOrdersCount: pastOrders.length,
              selectedOrderId: selectedOrder?.id,
              selectedOrderSource: selectedOrder?.source,
            });
            console.groupEnd();
            setSnackbar({
              open: true,
              message: `Sipariş listede bulunamadı (id=${idStr}, source=${srcLower}). Lütfen sayfayı yenileyin.`,
              severity: 'warning',
            });
          }
        }}
        isManualPhoneModalOpen={isManualPhoneModalOpen}
        setIsManualPhoneModalOpen={setIsManualPhoneModalOpen}
        manualPhoneNumber={manualPhoneNumber}
        setManualPhoneNumber={setManualPhoneNumber}
        isSearchingPhone={isSearchingPhone}
        handleManualPhoneSearch={handleManualPhoneSearch}
        selectCourierModalOpen={selectCourierModalOpen}
        setSelectCourierModalOpen={setSelectCourierModalOpen}
        handleCourierSelect={handleCourierSelect}
        isCashierLoginModalOpen={isCashierLoginModalOpen}
        setIsCashierLoginModalOpen={setIsCashierLoginModalOpen}
        handleCashierLogin={handleCashierLogin}
        activeNewOrder={activeNewOrder}
        pendingNewOrders={pendingNewOrders}
        completeActiveNewOrder={completeActiveNewOrder}
        handleCloseNewOrderModal={handleCloseNewOrderModal}
        autoAcceptSeconds={screenData?.autoAcceptEnabled ? screenData.autoAcceptDelaySeconds : undefined}
        onAutoAccept={screenData?.autoAcceptEnabled ? handleAutoAcceptAndPrint : undefined}
        onAcceptAndPrint={handleManualAcceptAndPrint}
        onAcceptAndPrintPending={handleAcceptAndPrintPending}
        getirDetailModalOpen={getirDetailModalOpen}
        setGetirDetailModalOpen={setGetirDetailModalOpen}
        getirDetailOrderId={getirDetailOrderId}
        setGetirDetailOrderId={setGetirDetailOrderId}
        getirCancelModalOpen={getirCancelModalOpen}
        handleCloseGetirCancelModal={handleCloseGetirCancelModal}
        cancelledOrderInfo={cancelledOrderInfo}
        qrOrderDetailModalOpen={qrOrderDetailModalOpen}
        setQrOrderDetailModalOpen={setQrOrderDetailModalOpen}
        selectedQrOrder={selectedQrOrder}
        updateMasaSiparisiStatus={updateMasaSiparisiStatus}
        fetchMasaSiparisleri={fetchMasaSiparisleri}
        getirRestaurantClosedModalOpen={getirRestaurantClosedModalOpen}
        setGetirRestaurantClosedModalOpen={setGetirRestaurantClosedModalOpen}
        getirRestaurantOpening={getirRestaurantOpening}
        setGetirRestaurantOpening={setGetirRestaurantOpening}
        openGetirRestaurant={openGetirRestaurant}
        migrosCancelModalOpen={migrosCancelModalOpen}
        handleCloseMigrosCancelModal={handleCloseMigrosCancelModal}
        migrosCancelledOrderInfo={migrosCancelledOrderInfo}
        migrosRestaurantClosedModalOpen={migrosRestaurantClosedModalOpen}
        setMigrosRestaurantClosedModalOpen={setMigrosRestaurantClosedModalOpen}
        migrosRestaurantOpening={migrosRestaurantOpening}
        setMigrosRestaurantOpening={setMigrosRestaurantOpening}
        openMigrosRestaurant={openMigrosRestaurant}
        ysVendorClosedModalOpen={ysVendorClosedModalOpen}
        setYsVendorClosedModalOpen={setYsVendorClosedModalOpen}
        ysVendorOpening={ysVendorOpening}
        setYsVendorOpening={setYsVendorOpening}
        openYemeksepetiVendor={openYemeksepetiVendor}
        ysRejectModalOpen={ysRejectModalOpen}
        setYsRejectModalOpen={setYsRejectModalOpen}
        ysRejectOrderId={ysRejectOrderId}
        setYsRejectOrderId={setYsRejectOrderId}
        ysRejectReasons={ysRejectReasons}
        selectedYsRejectReason={selectedYsRejectReason}
        setSelectedYsRejectReason={setSelectedYsRejectReason}
        updateYemeksepetiOrderStatus={updateYemeksepetiOrderStatus}
        fetchYemeksepetiOrders={fetchYemeksepetiOrders}
        ysCancelModalOpen={ysCancelModalOpen}
        setYsCancelModalOpen={setYsCancelModalOpen}
        ysCancelledOrderInfo={ysCancelledOrderInfo}
        setYsCancelledOrderInfo={setYsCancelledOrderInfo}
        migrosRejectModalOpen={migrosRejectModalOpen}
        setMigrosRejectModalOpen={setMigrosRejectModalOpen}
        migrosRejectOrderId={migrosRejectOrderId}
        setMigrosRejectOrderId={setMigrosRejectOrderId}
        migrosRejectOrderStatus={migrosRejectOrderStatus}
        setMigrosRejectOrderStatus={setMigrosRejectOrderStatus}
        migrosCancelReasons={migrosCancelReasons}
        selectedMigrosCancelReasonId={selectedMigrosCancelReasonId}
        setSelectedMigrosCancelReasonId={setSelectedMigrosCancelReasonId}
        fetchMigrosOrders={fetchMigrosOrders}
        migrosDetailModalOpen={migrosDetailModalOpen}
        setMigrosDetailModalOpen={setMigrosDetailModalOpen}
        migrosDetailOrderId={migrosDetailOrderId}
        setMigrosDetailOrderId={setMigrosDetailOrderId}
        ysDetailModalOpen={ysDetailModalOpen}
        setYsDetailModalOpen={setYsDetailModalOpen}
        ysDetailOrderId={ysDetailOrderId}
        setYsDetailOrderId={setYsDetailOrderId}
        trendyolDetailModalOpen={trendyolDetailModalOpen}
        setTrendyolDetailModalOpen={setTrendyolDetailModalOpen}
        trendyolDetailOrder={trendyolDetailOrder}
        setTrendyolDetailOrder={setTrendyolDetailOrder}
        trendyolProductDescriptions={trendyolProductDescriptions}
        trendyolCancelModalOpen={trendyolCancelModalOpen}
        setTrendyolCancelModalOpen={setTrendyolCancelModalOpen}
        trendyolCancelOrder={trendyolCancelOrder}
        setTrendyolCancelOrder={setTrendyolCancelOrder}
        cancelTrendyolOrder={cancelTrendyolOrder}
        trendyolCancelNotifModalOpen={trendyolCancelNotifModalOpen}
        handleCloseTrendyolCancelNotifModal={handleCloseTrendyolCancelNotifModal}
        trendyolCancelledNotifOrder={trendyolCancelledNotifOrder}
        trendyolVendorClosedModalOpen={trendyolVendorClosedModalOpen}
        setTrendyolVendorClosedModalOpen={setTrendyolVendorClosedModalOpen}
        trendyolVendorOpening={trendyolVendorOpening}
        setTrendyolVendorOpening={setTrendyolVendorOpening}
        openTrendyolVendor={openTrendyolVendor}
        closeRestaurantModalOpen={closeRestaurantModalOpen}
        setCloseRestaurantModalOpen={setCloseRestaurantModalOpen}
        platformStores={platformStores}
        closeRestaurantPlatform={closeRestaurantPlatform}
        closingRestaurant={closingRestaurant}
        setClosingRestaurant={setClosingRestaurant}
        handleCloseRestaurantConfirm={handleCloseRestaurantConfirm}
      />

      {/* Adres Detay / Güncelleme Modal — ad güncelleme de buradan yapılır */}
      <AddressDetailModal
        open={addressDetailModalOpen}
        onClose={() => {
          setAddressDetailModalOpen(false);
          setSelectedAddressForDetail(null);
        }}
        address={selectedAddressForDetail}
        customerName={currentCustomer?.name}
        customerPhone={currentCustomer?.phone}
        accountId={currentCustomer?.id || undefined}
        onAddressUpdated={(updatedAddr) => {
          if (currentCustomer) {
            setCurrentCustomer(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                addresses: prev.addresses.map(a =>
                  a.id === updatedAddr.id ? updatedAddr : a
                ),
              };
            });
          }
        }}
        onCustomerNameUpdated={(newName) => {
          // Müşteri adı backend'te güncellendi — sol panel + her yer için state senkronu
          setCurrentCustomer(prev => prev ? { ...prev, name: newName } : prev);
          // Aktif/pending telefon görüşmesinin görünen adını da güncelle (anlık yansıma)
          const phoneForUpdate = currentCustomer?.phone || activeCall?.call?.phone;
          if (phoneForUpdate && typeof updateActiveCallCustomerName === 'function') {
            updateActiveCallCustomerName(phoneForUpdate, newName);
          }
        }}
      />

      {/* Platform Ürün-Kategori Eşleştirme Modalı (Sipariş Bazlı) */}
      <PlatformCategoryMappingModal
        open={categoryMappingModalOpen}
        onClose={() => { setCategoryMappingModalOpen(false); setUnmappedProductsForModal([]); }}
        unmappedProducts={unmappedProductsForModal}
        existingMappings={platformCategoryMappings}
        categories={availableCategories}
        packageScreenId={screenData?.packageScreenId || screenData?.id || 0}
        branchId={screenData?.branchId || null}
        onMappingsSaved={(updated) => setPlatformCategoryMappings(updated)}
      />

      {/* Kategori Eşleştirme Yönetim Modalı (Sabit Buton) */}
      <CategoryMappingManagementModal
        open={categoryManagementModalOpen}
        onClose={() => setCategoryManagementModalOpen(false)}
        mappings={platformCategoryMappings}
        categories={availableCategories}
        packageScreenId={screenData?.packageScreenId || screenData?.id || 0}
        branchId={screenData?.branchId || null}
        onMappingsUpdated={(updated) => setPlatformCategoryMappings(updated)}
      />

      {/* Getir İleri Tarihli Sipariş Uyarı Modal */}
      <Dialog
        open={getirScheduledBlockOpen}
        onClose={() => setGetirScheduledBlockOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ bgcolor: '#ff6f00', color: 'white', display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <Box sx={{ fontSize: 36 }}>⏰</Box>
          <Typography variant="h6" fontWeight="bold">İleri Tarihli Getir Siparişi</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem' }}>
            {t('orderManagement.screen.preOrderDialog.body')}
          </Typography>
          {getirScheduledBlockDate && (() => {
            const utcStr = getirScheduledBlockDate;
            const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
            const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
            const trDate = new Date(trMs);
            const h = String(trDate.getUTCHours()).padStart(2, '0');
            const m = String(trDate.getUTCMinutes()).padStart(2, '0');
            const d = String(trDate.getUTCDate()).padStart(2, '0');
            const mo = String(trDate.getUTCMonth() + 1).padStart(2, '0');
            const deliveryTimeStr = `${d}.${mo} ${h}:${m}`;

            return (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Box sx={{
                  bgcolor: '#fff3e0',
                  border: '2px solid #ff6f00',
                  borderRadius: 2,
                  p: 2,
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('orderManagement.screen.preOrderDialog.deliveryTime')}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#e65100">
                    {deliveryTimeStr}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setGetirScheduledBlockOpen(false)}
            sx={{ color: '#ff6f00', borderColor: '#ff6f00', '&:hover': { borderColor: '#e65100', bgcolor: '#fff3e0' } }}
          >
            {t('orderManagement.screen.localServiceDialog.close')}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setGetirScheduledBlockOpen(false);
              if (getirScheduledBlockOrderId) {
                await forceConfirmGetirScheduledOrder(getirScheduledBlockOrderId);
              }
            }}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
          >
            {t('orderManagement.screen.preOrderDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ödeme Kontrol Modal */}
      <PaymentVerificationModal
        open={verificationModalOpen}
        onClose={() => setVerificationModalOpen(false)}
        companyId={companyId || ''}
        branchId={screenData?.branchId}
        orders={orders}
        getirOrders={getirOrders}
        migrosOrders={migrosOrders}
        yemeksepetiOrders={yemeksepetiOrders}
        trendyolOrders={trendyolOrders}
      />

      {/* Trendyol Local Service Bağlantı Uyarı Modalı */}
      <Dialog
        open={showLocalServiceWarningModal}
        onClose={handleDismissLocalServiceWarning}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, textAlign: 'center', py: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 56, color: '#f44336' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('orderManagement.screen.localServiceDialog.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {t('orderManagement.screen.localServiceDialog.body', { platform: 'Uber Eats' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight="bold">
            {t('orderManagement.screen.localServiceDialog.instruction')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetryLocalServiceConnection}
            startIcon={<RefreshIcon />}
          >
            {t('orderManagement.screen.localServiceDialog.retry')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleDismissLocalServiceWarning}
          >
            {t('orderManagement.screen.localServiceDialog.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trendyol Polling Durdu Uyarı Modalı: local-service ayakta ama sipariş
          sorgulaması dönmüyor - masaüstü yazılımı yeniden başlatılmalı */}
      <Dialog
        open={showPollingStaleModal}
        onClose={handleDismissPollingStale}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, textAlign: 'center', py: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 56, color: '#f44336' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('orderManagement.screen.pollingStaleDialog.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {t('orderManagement.screen.pollingStaleDialog.body', { platform: 'Uber Eats' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight="bold">
            {t('orderManagement.screen.pollingStaleDialog.instruction')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDismissPollingStale}
          >
            {t('orderManagement.screen.pollingStaleDialog.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Çoklu Mağaza Desteklenmiyor Uyarısı: yerel servis eski sürüm, yalnızca
          ilk mağaza yoklanıyor - diğer markaların siparişleri düşmüyor */}
      <Dialog
        open={showMultiStoreWarning}
        onClose={handleDismissMultiStoreWarning}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, textAlign: 'center', py: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 56, color: '#ff9800' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('orderManagement.screen.multiStoreUnsupportedDialog.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {t('orderManagement.screen.multiStoreUnsupportedDialog.body')}
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight="bold">
            {t('orderManagement.screen.multiStoreUnsupportedDialog.instruction')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDismissMultiStoreWarning}
          >
            {t('orderManagement.screen.multiStoreUnsupportedDialog.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Local Service Guncelleme Modali */}
      <LocalServiceUpdateModal
        open={showLocalServiceUpdateModal}
        version={localServiceUpdateVersion}
        onRestart={applyLocalServiceUpdate}
        onClose={() => {
          setShowLocalServiceUpdateModal(false);
          dismissLocalServiceUpdate();
        }}
      />

      {/* PavoPay P2P Cihaz Seçim Dialog (ÖDEME ALMA)
          Paket sipariş ekranı yalnızca "Sipariş Modu" (pavo_order) cihazlarını
          kullanabilir; DeviceManagement'tan diğer modlarda (pavo_quicksale /
          pavo_restaurant / pavo_rest_p2p) eklenmiş cihazlar bu listeye girmez. */}
      <PavoP2PDeviceSelectDialog
        open={pavoP2pSelectOpen}
        onClose={() => setPavoP2pSelectOpen(false)}
        onSelect={(deviceCode) => { setPavoP2pSelectOpen(false); handlePavoP2pPayment(deviceCode); }}
        companyId={companyId ?? undefined}
        branchId={screenData?.branchId || undefined}
        allowedModes={['pavo_order']}
      />

      {/* REST-P2P Cihaz Seçim Dialog (ONLINE FIŞ YAZDIRMA)
          Online ödenmiş siparişlerin (Yemeksepeti / Trendyol / Migros / Getir /
          Web sitesi) sadece mali fişini Pavo cihazından kestirmek içindir.
          Yalnızca "pavo_rest_p2p" modundaki cihazları listeler — pavo_order
          ve diğer modlar bu listeye girmez. İki sistem birbirine karışmaz. */}
      <PavoP2PDeviceSelectDialog
        open={restP2pReceiptSelectOpen}
        onClose={() => setRestP2pReceiptSelectOpen(false)}
        onSelect={(deviceCode) => { setRestP2pReceiptSelectOpen(false); handleRestP2pOnlineReceipt(deviceCode); }}
        companyId={companyId ?? undefined}
        branchId={screenData?.branchId || undefined}
        allowedModes={['pavo_rest_p2p']}
      />

      {/* PavoPay Aktif Ödeme Takip Modal */}
      <PavoOrderTrackingModal
        open={pavoTrackingOpen}
        onClose={() => setPavoTrackingOpen(false)}
        payments={activePavoPayments}
        onPaymentCancelled={loadActivePavoPayments}
      />

      {/* Oto onay/yazdırma başarısız uyarısı — yeni sipariş modalının üstünde
          gösterilir (render sırası gereği) ve ayrı bir alarm sesiyle gelir.
          Bilinçli olarak `anyOtherModalOpen` hesabına DAHİL EDİLMEDİ: uyarı
          onaylanmadı diye yeni sipariş kuyruğu durmasın. */}
      <AutoAcceptFailureModal
        open={!!activeAutoAcceptFailure}
        failure={activeAutoAcceptFailure}
        remainingCount={Math.max(0, autoAcceptFailures.length - 1)}
        onAcknowledge={handleAcknowledgeAutoAcceptFailure}
      />

      {/* Birleşik Kuyruk — Zorunlu Telefon Karar Modalı (her şeyin üstünde) */}
      <CallDecisionModal
        open={!!callDecisionCall}
        call={callDecisionCall}
        mode={callDecisionMode}
        pendingCount={pendingCalls.length}
        themeColor={themeColors.primary}
        onHold={handleCallDecisionHold}
        onSwitch={handleCallDecisionSwitch}
      />
    </ScreenContainer>
  );
};

export default PackageOrderScreen;
